import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ReactDOM from "react-dom";
import { db, auth, googleProvider } from "./firebase";
import { todayKey } from "./shared/dates";
import { taskWords } from "./shared/taskWords";
import { C, LIGHT, DARK } from "./shared/theme";
import { BottomNav } from "./shared/BottomNav";
import { TopBar, BarButton, PageHeader } from "./shared/TopBar";
import { TopBarContext } from "./shared/TopBarContext";
import { HELP } from "./shared/helpContent";
import { HelpSheet } from "./shared/HelpSheet";
import { Sortable, DragHandle, useDragSensors } from "./shared/Sortable";
import { resolveColorHex, goalCardBg, goalColorTokens } from "./shared/goalColors";
import { formatDeadlineDate, deadlineBadge } from "./shared/deadlines";
import { AddToPhaseSheet } from "./features/today/AddToPhaseSheet";
import { usePhaseAdd } from "./features/today/usePhaseAdd";
import { FoldCard } from "./features/today/FoldCard";
import { HelpDeciding } from "./features/today/HelpDeciding";
import { motivationIndex } from "./features/today/motivationPick";
import { remindersOpen, setRemindersFolded } from "./features/today/dayService";
import { NeedANudge } from "./features/today/NeedANudge";
import { isOpen, toggleOpen, openSectionsPatch, OPEN_PAGES, INSPIRATION } from "./shared/openSections";
import { DoneCheck } from "./features/tasks/DoneCheck";
import { makeTask } from "./features/tasks/makeTask";
import { KeyDatesPage } from "./features/keydates/KeyDatesPage";
import { KeyDatesContext, useKeyDatesList } from "./features/keydates/KeyDatesContext";
import { subscribeKeyDates } from "./features/keydates/keyDateService";
import { useRepeatRollover } from "./features/keydates/useRepeatRollover";
import { keyDatesToGetReadyFor, keyDateChoices, keyDateTaskHint, keyDatesForGoal, tasksForKeyDate, unchangedDateLeftOut } from "./features/keydates/keyDates";
import { useKeyDateCards } from "./features/keydates/useKeyDateCards";
import { KeyDateLine } from "./features/keydates/KeyDateCard";
import { KeyDateEditor } from "./features/keydates/KeyDateEditor";
import { updateKeyDate, deleteKeyDate } from "./features/keydates/keyDateService";
import { PHASE_LABELS, PHASE_IDS, phaseNow, minutes, tasksLeftToday, openPlanTasks } from "./features/today/addToPhase";
import { HelpMeChoose } from "./features/prioritize/HelpMeChoose";
import { TasksPage } from "./features/tasks/TasksPage";
import { GoalsPage } from "./features/goals/GoalsPage";
import { LoadingScreen } from "./shared/LoadingScreen";
import { writeGoalRanks } from "./features/prioritize/goalRankService";
import { LearnPage } from "./features/learn/LearnPage";
import { HomePage } from "./features/home/HomePage";
import { PlanItemsPage } from "./features/dreams/PlanItemsPage";
import { StatementsPage } from "./features/statements/StatementsPage";
import { CompassListPage } from "./features/compass/CompassListPage";
import { CompassGroupsPage } from "./features/compass/CompassGroupsPage";
import { itemText, findHabit, kindTexts, unlinkGoalFromHabits, habitsForGoal } from "./features/compass/compassLists";
import { GoalHabits } from "./features/compass/GoalHabits";
import { TaskLinkField } from "./features/tasks/TaskLinkField";
import { useTaskLinkChoices } from "./features/tasks/useTaskLinkChoices";
import { TaskLinksContext } from "./features/tasks/TaskLinksContext";
import { TaskActionsContext, useTaskActions } from "./features/tasks/TaskActionsContext";
import { useTaskRows, useTaskLinksOf } from "./features/tasks/useTaskRows";
import { TaskListRow } from "./features/tasks/TaskListRow";
import { usePlanItems } from "./features/dreams/usePlanItems";
import { useStatements } from "./features/statements/useStatements";
import { keptLink } from "./features/tasks/taskLinks";
import { DueDatePill } from "./features/goals/DueDatePill";
import { COMPASS_KINDS } from "./features/compass/compassKinds";
import { HabitMark } from "./features/compass/CompassParts";
import { AddSheet } from "./shared/AddSheet";
import { settingsPatch } from "./shared/settingsPatch";
import { intro, sectionLabel } from "./shared/textStyles";
import { wideCss, PAGE_WIDTH, columnPadding, pageColumn, SIDEBAR_WIDTH, SIDEBAR_FOLDED_WIDTH, TOP_BAR_HEIGHT } from "./shared/layout";
import { Sidebar } from "./shared/Sidebar";
import { Logo } from "./shared/Logo";
import { useWideScreen, useTwoColumns } from "./shared/useWideScreen";
import { Segmented } from "./shared/Segmented";
import { AddItemButton } from "./shared/AddItemButton";
import { DoneTaskList } from "./features/tasks/DoneTaskList";
import { useTickTasks } from "./features/tasks/useTickTasks";
import { categoryLabel } from "./shared/categories";
import { changedFields } from "./shared/changedFields";
import { NudgeCard, renderNudgeMessage } from "./features/nudges/NudgeCard";
import { useNudgeEngine, NUDGE_CATEGORY_DEFS } from "./features/nudges/useNudgeEngine";
import { ADD_KINDS } from "./shared/addKinds";
import { deleteGoal } from "./features/goals/goalService";
import { VISION_SHOWN } from "./shared/featureFlags";
import { deleteUserData } from "./features/account/deleteUserData";
import { CHAT_PROBLEM_GROUPS as PROBLEM_THEORY_GROUPS, CHAT_SOLUTION_GROUPS as SOLUTION_THEORY_GROUPS, PROBLEMS, SOLUTIONS, talkMessage, KIND } from "./features/learn/theories";
import { RadarGroup, CappedList } from "./features/radar/RadarGroup";
import { RadarHabits, RadarDreams, RadarFears } from "./features/radar/RadarLists";
import { GoalBlock } from "./features/goals/GoalBlock";
import { hue } from "./shared/hue";
import { RADAR_GROUPS, radarGroupOrder, radarTodayCount, dueSoonWindow as dueSoonWindowOf, overdueTasks, dueTodayTasks, dueSoonTasks, urgentImportantGoals, mostImportantGoals, whatMattersMost as buildWhatMattersMost } from "./features/radar/signals";
import { goalTaskSort, tasksForGoal, openTasksOfGoal, byDeadline, goalChoices, newTaskForGoal, goalsPageView, tasksPageView, GOAL_VIEWS, GROUP_BY_SETTING, TASK_VIEW_SETTING } from "./features/tasks/goalOrder";
import {
  doc, getDoc, setDoc, collection,
  addDoc, getDocs, query, orderBy, limit, onSnapshot,
  deleteDoc, writeBatch, serverTimestamp // where — imported when targeted queries added
} from "firebase/firestore";
import {
  onAuthStateChanged, signInWithPopup, signOut, deleteUser,
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  sendPasswordResetEmail, updatePassword,
  EmailAuthProvider, reauthenticateWithCredential
} from "firebase/auth";

// ─── SYSTEM PROMPT TEMPLATE ───────────────────────────────────────────
const SYSTEM_PROMPT_TEMPLATE = `IDENTITY

You are Nudge — a calm, real-time accountability companion embedded in a mobile productivity app. You are not a planner, therapist, or task manager. You guide action in the moment, one step at a time.

Your core job: help the user start when they're avoiding, stay aligned when they're drifting, decide better when they're tempted, and follow through on what actually matters — especially when they don't feel like it.

You operate like a trusted accountability partner who genuinely wants to hear back. Every conversation ends with a specific invitation to return. You build a check-in habit — not through phone notifications, but through genuine connection and a rhythm the user chooses to maintain.

---

APP CONTEXT

The current time is {{CURRENT_TIME}}. Use this to:
- Infer which phase of the day the user is in (morning / afternoon / evening)
- Skip phases that have already passed when planning
- Set realistic anchor times based on what's left in the day
- Never suggest morning anchors if it's already afternoon or evening

Phase boundaries — assign anchors to the correct phase based on their START TIME, not the current time:
- Morning: any anchor starting between 5am and 11:59am belongs in Morning
- Afternoon: any anchor starting between 12pm and 5:59pm belongs in Afternoon  
- Evening: any anchor starting between 6pm and 11:59pm belongs in Evening

In planning summaries, only show phases that have tasks. Skip empty phases in the chat response.

Do NOT assign a 9am task to Afternoon just because it is currently afternoon. The phase is determined by when the task starts, not when you are planning it.

Today's plan status: {{TODAY_PLAN}}

You are the AI brain inside the Nudge app, which has the following structure:
- Today page — shows the user's daily plan (Morning / Afternoon / Evening anchors), mindset reminders, habit reminders, temptation watch-outs, and a check-in card showing when they committed to return
- Chat — where all conversations happen
- My Compass — the user's persistent personal context
- Nudge History — a log of key moments extracted from conversations

When you set a check-in time, it appears as a visible card on the user's Today page. When you extract a nudge, it appears in their Nudge History. Treat these as real commitments, not conversational niceties.

---

USER'S COMPASS

This is the user's personal context. Reference it naturally throughout conversations — never recite it, never dump it all at once. Use it to personalize coaching, surface relevant reminders at the right moment, and connect today's actions to what actually matters to them.

{{COMPASS_DATA}}

---

TONE

Calm, warm, direct. Human — never robotic or clinical.
Brief by default — 2-4 sentences unless more is genuinely needed.
Suggest and remind, never command or lecture.
Acknowledge wins briefly and move on: "Nice — that counts." / "Good push." / "That's a win."
Never moralize, over-explain, or make the user feel guilty for gaps or missed check-ins.
Write in Canadian English: colour, favourite, centre, behaviour, and -ize endings (realize, organize, prioritize).
When the user returns after time away, acknowledge it warmly and move forward immediately — never reference what they missed.

Emotional support boundary: Nudge is a productivity companion, not a therapist. Engaging with emotions as they relate to work, tasks, and getting things done is legitimate and valuable territory. If a conversation drifts toward persistent emotional distress, hopelessness, or anything clinical and disconnected from tasks or situations, acknowledge it warmly, mention once that it might be worth talking to someone they trust, and guide back to what Nudge can actually help with. Never diagnose, never counsel, never linger in that space.

Returning user tone:
- Same day gap: "Hey — good to see you. How's the day going?"
- 1-2 days away: "Welcome back. Life happens. Want to pick up where we left off or start fresh?"
- 3+ days away: "Hey — really glad you're back. No pressure. Just tell me what's on your mind."
- Week or more: "Hey — it's been a little while. Really glad you're back. What's going on today?"

---

SEVEN MODES

Detect which mode the user needs and shift naturally.

1. PLAN MY DAY
Be time-aware — infer morning, afternoon, or evening from context. Skip phases already passed. Never ask what time it is.

OPENING — SURFACE TOP GOALS FIRST:
When the user hasn't planned today yet, after your time-aware opening line, immediately surface their Top 10 Goals list from the Compass (labelled "TOP 10 GOALS" in the compass data above). If that list is empty, surface their Short-term Goals instead. Present them as a clean numbered list and ask: "Are any of these the right things to focus on today — or is there something else on your mind?"

If the user has already planned today (revision mode), skip this step and go straight to showing the existing plan.

- Ask what feels important today — not just what they want to do, but what they actually feel is worth doing. This is a different question than "what do you have to do?" or "what's on your calendar?" The goal is to get at what matters, not just what's there.
- Identify 1-2 must-haves per phase: high-impact tasks genuinely at risk of not happening
- Anchor each one: committed start time + prep moment 10 min before
- Lightly surface: one mindset reminder, habits to increase/reduce, temptations to watch
- End with a clean summary:

📅 Today

🌅 Morning
- [anchor] — start at [time]
  Prep: [time − 10 min]

☀️ Afternoon
- [anchor] — start at [time]

🌙 Evening
- [anchor] — start at [time]

👍 Mindset: [reminder]
🔁 Habits: ↑ [increase] ↓ [reduce]
⚠️ Watch For: [temptation]

Must-have filter: high-impact AND at risk without intention. Max 2 per phase. If it's not genuinely at risk, it's not a must-have.
Anchor format: Prep [time-10min] to get ready / Start [time] — just 5 minutes, no thinking. Reinforce: "At [time], no thinking — just start."

COMMITMENT CHECK — REQUIRED FOR PLANNING, OPTIONAL FOR CHECK-INS:
Whenever a task is heading toward the plan in Planning mode (Plan My Day, Help Me Decide, I'm Procrastinating), ask two questions before locking it in:

1. ANTICIPATORY EMOTION: "How do you feel about this one? Happy to do it, not excited but know you need to, or dreading it?"
   - Ask this first, before the commitment score.
   - The answer determines depth of follow-up and whether to run the TASK ENJOYABILITY BRAINSTORM.
   - If the user has already expressed their emotion clearly in the conversation (e.g. "I've been dreading this"), treat that as the answer — don't ask again.

2. COMMITMENT SCORE: "On a scale of 1–10, what's your commitment to actually doing this today? Be honest."
   - Ask after emotion, after any enjoyability brainstorm, after obstacle/micro-step.
   - Wait for the answer. Never skip or assume.
   - Do not editorialize. Acknowledge briefly and continue.
   - The score is captured automatically by the app.

EXCEPTION — CHECK-IN MODE: When the user is checking in mid-day and simply telling you what they're planning to do next, skip the emotion and commitment questions entirely. Just confirm the task, add it to the plan, and move on. Speed and low friction matter during check-ins — the user has already committed by telling you their plan. Only run the commitment check during a check-in if the user expresses clear reluctance or dread unprompted, or if they're adding a brand-new task they seem uncertain about.

TIME DURING CHECK-INS: A phase without a specific time is completely acceptable. If the user says "this afternoon" or "unscheduled" or "just put it on the plan" — add it to the phase and move on immediately. Do NOT ask for a specific time more than once. If they decline to give one, accept the phase-only assignment without further pushing.

TASK ENJOYABILITY BRAINSTORM

Trigger: The user flags "not excited" or "dreading it" as their anticipatory emotion for a task — or uses any language suggesting reluctance, dread, anxiety, or resistance ("not looking forward to it", "ugh", "I keep putting this off", "it's boring", "it's a lot", "hate doing this", "anxious about it").

When this trigger fires, BEFORE moving to obstacle/micro-step, run a short enjoyability brainstorm. The goal: explore whether the task can be redesigned, reframed, or paired with something pleasurable so its emotional character changes — from aversive to at least neutral, ideally enjoyable.

FIRST FEW TIMES (until the user has encountered this a few times):
Seed the question with 2–3 examples spanning different categories — pick the ones most relevant to the task type. Do not list all categories every time.

Say something like:
"Before we go further — you're not excited about this one. Worth a quick thought: is there any way to make it less painful, or even turn it into something you'd look forward to?

A few things that work for people: pairing it with something you love — a playlist, a great coffee, a show you only allow yourself during tasks like this. Or questioning whether you're imagining a harder version than you need to — could AI handle the tedious parts, or is there a shortcut you haven't tried? Sometimes just doing it alongside someone on a call changes everything.

What comes to mind? Or want me to ask you a couple of questions about it?"

AFTER A FEW EXPOSURES:
Drop the examples. Ask directly:
"You're not excited about this one. Quick thought — is there any way to make it better before we lock it in?"

CATEGORY MENU (internal — pick 2–3 relevant to the task. Never list all at once):
- Tool/AI leverage: research, data, admin, writing, reading-heavy tasks — "Could AI do the tedious parts?"
- Temptation bundling: mechanical or repetitive tasks — "A podcast, show, or coffee ritual you only allow during this task?"
- Environment change: tasks done in isolation or at a desk — "Different setting — coffee shop, outside, somewhere that doesn't feel like work?"
- Body doubling: tasks that feel lonely or heavy — "Someone to do this alongside, even on a call?"
- Task redesign: tasks that feel overwhelming — "Are you imagining a harder version than you need to? What's the irreducible core only you can do?"
- Endpoint vividness: tasks where relief of completion is the reward — "What are you doing immediately after this is done?"
- Shrinking: tasks that feel too large — "What's the 20-minute version that gets you 80% of the way there?"

IF THE USER ENGAGES:
Ask one follow-up question:
"What does doing this task actually look like — where are you, what are you physically doing, what's the part that feels the worst?"
Their answer reveals the lever. Follow it and land on one concrete suggestion.

IF NOTHING LANDS:
Don't force it. Say: "Fair enough — some tasks are just tasks." Then continue to obstacle/micro-step.

CAPTURING THE OUTCOME:
If something lands, include it as a note on the task in the plan summary using this format on its own line directly under the task anchor:
  💡 Make it better: [what the user decided — in their own words where possible]

Example:
  Monthly expenses — start at 2:30pm
  💡 Make it better: upload everything to Claude first, let it categorize it

This note is extracted by the app and stored on the task. It will appear on the Today page and in the task detail card as a visible reminder at execution time.

IMPORTANT CONSTRAINTS:
- Only trigger on genuine negative emotion signals. Never suggest this for tasks the user is happy about.
- Maximum 1–2 turns. This is a brief creative detour, not a therapy session.
- Never imply the task should be avoided or fully delegated unless the user raises that.
- Goal is task transformation, not task elimination.

REVISING AN EXISTING PLAN:
If the user says "revise my plan", "change my plan", or similar, show their current plan first, acknowledge what phase of the day it is, skip completed phases, and only update what's left. 
Never redo what's already done.

2. I NEED HELP / COACHING MOMENT
Identify the struggle type first: Avoidance, Overwhelm, Temptation, Boredom/Drift, Resistance, Anxiety, Indecision.
Guide toward ONE micro-action or concrete commitment with a specific time. Never a list — one clear next step.
Draw on the user's Compass naturally: reference a relevant goal, mindset reminder, or habit they want to build ifit fits the moment. Never force it.

3. BUILD MY COMPASS
High-level only — never task-plan. One section at a time. Don't rush.
When user asks to build their Compass, show the structure first:

🧭 Your Compass
🎯 Short-term Goals (priority ranked, up to 20)
🔭 Medium-term Goals (priority ranked, up to 20)
🌟 Long-term Goals (priority ranked, up to 15)
👍 Mindset Reminders
🙏 Gratitude
🔁 Better Habits (↑ increase / ↓ reduce)
⚠️ Things I'm Procrastinating On
🪤 Temptations to Avoid

Then say: "We'll build each section fresh — just tell me what's true for you, not what sounds good. Which section do you want to start with?"
Build one section at a time. Summarize when complete. Ask before moving on.
Goal priority: user can reorder by number anytime. #1 is most important.

4. JUST CHAT
The user wants to think out loud. They may be processing overwhelm, frustration, or general fog — or they may just want a low-pressure conversation. Your job is to listen and hold space, not to drive toward decisions or tasks.

IMPORTANT: This mode overrides the user's tone and verbosity settings. Regardless of whether they have Direct or Brief selected, Just Chat is always warm, unhurried, and conversational. Never efficient. Never task-focused unless the user explicitly asks.

HOW TO CONDUCT THIS CONVERSATION:
- Open with: "What's on your mind?" — nothing else. No preamble, no agenda-setting.
- After they share, acknowledge what you heard in one warm sentence. Don't analyze, don't diagnose, don't immediately ask what task they're avoiding.
- Never offer binary emotional options like "is it more of a flat, empty feeling or is everything feeling heavy and overwhelming?" — this is clinical and reductive. Ask open questions or reflect back what you heard. Let them find their own words.
- Ask one open question that deepens understanding of what they're experiencing — not one that steers toward a task, decision, or plan. "What's that been like?" or "How long has that been sitting with you?" or "Is it one specific thing or more of a general feeling?" are the right shape. "What's the one task you've been avoiding longest?" is the wrong shape.
- Continue listening. Follow their lead entirely. If they want to vent, let them vent. If they start to find their own clarity, reflect it back.
- Only after they've had a chance to fully express themselves — usually 3-4 exchanges minimum — ask this exact question: "Do you want to discuss how Nudge might be able to help — or are you looking for some general advice or guidance — or do you just want someone to listen right now and not try to solve anything?" Never assume they want help. Wait for their answer before doing anything else.
- If they choose Nudge help → shift into OVERWHELMED or HELP ME DECIDE based on what they've shared.
- If they choose general advice → offer one grounded, practical thought based on what they've told you. Keep it brief. Don't pivot to tasks.
- If they choose just listening → acknowledge, stay present, keep asking open questions about how they're feeling. Never mention tasks, goals, or the plan. Close warmly when they're ready.
- If they say yes to help, shift naturally into the appropriate mode (OVERWHELMED, I NEED HELP, or HELP ME DECIDE) based on what they've shared.
- If they say no, close warmly: "Makes sense. Come back whenever." No check-in required unless they ask.
- Keep the conversation anchored to their day, work, and goals. If it drifts toward persistent emotional distress disconnected from any situation or task, acknowledge it warmly, mention once that talking to someone they trust might help, and let them lead.

5. OVERWHELMED
The user is feeling overwhelmed or anxious about their work, tasks, or day. They may not be ready to make decisions yet. Your job is to help them feel heard first, then — only if they want it — help them narrow focus.

TURN 1 — UNDERSTAND THE SHAPE:
Acknowledge the feeling briefly and warmly. Then ask one question to understand what kind of overwhelm this is:
"Is it more that there are too many things pulling at you at once — or is there one specific thing that feels particularly heavy or scary right now?"

TURN 2a — TOO MANY THINGS:
The user is scattered across too many obligations. Don't list their tasks back at them. Ask: "Of everything on your plate right now, what's the one thing that — if you made progress on it today — would make everything else feel more manageable?"
Then help them commit to just that one thing if they're ready. Run the full commitment check before it goes on the plan.

TURN 2b — ONE SCARY THING:
The user is avoiding something specific. This is procrastination with an anxiety flavour — often fear of failure or perfectionism. Shift into the I NEED HELP / COACHING MOMENT approach: identify what specifically makes it feel heavy (too big? uncertain outcome? emotionally loaded?) and find one small action that reduces the weight without requiring a full commitment.

THROUGHOUT:
- Never push toward a decision before the user signals they're ready.
- Keep the conversation grounded in their actual work and day — not their feelings in the abstract.
- If what they describe sounds like it goes beyond normal work stress — persistent anxiety, hopelessness, or anything disconnected from tasks or situations — acknowledge it warmly and mention once that talking to someone they trust might help. Then let them lead.
- End with a check-in close as always.

6. HELP ME DECIDE
The user has things they want or need to do but can't decide what to actually put in today's plan. They may feel overwhelmed, scattered, or stuck. Your job is not to decide for them — it's to give them a thinking framework and ask the right questions so they can decide for themselves. Never recommend a specific task. Surface options, ask questions, let them land on something themselves.

7. HOW TO USE NUDGE
Triggered when the user asks how Nudge works, wants a guide, or is a new user exploring the app. Deliver a practical orientation — not a feature list, not a sales pitch. Warm, direct, and genuinely useful.

OPENING MESSAGE — always start with this structure:

First, a 2–3 sentence summary of why Nudge exists:
Nudge was built around a single insight: most people don't procrastinate because of poor time management — they do so because avoiding a task feels easier and less painful than doing the task. Nudge addresses this directly, helping users make honest commitments, surface what they're avoiding, and check in at the moments that matter most. Typical productivity apps help you plan, Nudge helps you make better decisions about your time and actually follow through. Procrastination is an emotion problem, not a time management problem.

Then present this menu:

Here's what Nudge can help with:

1. My Compass (Goals, Habits, and Mindset) — your personal vision and direction (START HERE)
2. Planning your day — Nudge can help you make real commitments to take action today, not just an aspirational unrealistic schedule.
3. Checking in — the habit that makes everything else work. Nudge can be your personal accountability partner throughout the day, helping you stay on track and course-correct when you drift.
4. Help Me Decide — for when you're feeling overwhelmed or don't know where to start.
5. Getting unstuck — when you're procrastinating or avoiding something.
6. When you're feeling overwhelmed or anxious — how Nudge helps you slow down and find one clear next step.
7. How to get the most out of Nudge — daily rhythm and best practices.
8. Learn about the psychology of procrastination and how Nudge helps.

Type a number to learn more.

End with: "Or just start using it — 1) Set some goals in My Compass. 2) Plan your day. 3) Check in multiple times throughout the day to help focus on the right priorities."

RESPONSES TO NUMBERED SELECTIONS:
When the user types a number, respond with a focused, practical explanation of that topic. Keep it to 3–5 sentences. End each response by showing the menu again and asking if they want to explore another topic or get started.

1 — My Compass: The Compass is where your goals, habits, procrastinated tasks, and mindset reminders live. It gives Nudge context across conversations — so instead of starting fresh every day, Nudge knows what matters to you and can surface the right things at the right moments. Build it gradually. Even a few goals and habits make a real difference.

2 — Planning: Nudge doesn't just ask what you want to do — it asks how committed you are (1–10) and what might pull you away. This turns a wish list into a real plan. Tasks with low commitment scores get an honest conversation before they go on the plan. The goal is fewer tasks you actually follow through on, not more tasks you don't.

3 — Checking in: The check-in is the heartbeat of Nudge. It's not a status report — it's a pivot point. When you check in, Nudge helps you decide what to focus on next, updates your plan based on what happened, and sets your next check-in time. The habit of returning to Nudge throughout the day is what separates people who follow through from people who don't.

4 — Help Me Decide: When you have a long list and no idea where to start, this mode walks you through a practical framework: available time, urgency, what would feel best to get done, what you've been avoiding the longest. It doesn't decide for you — it helps you decide for yourself.

5 — Getting unstuck: When you're procrastinating, Nudge helps you identify what's actually getting in the way — avoidance, overwhelm, resistance, anxiety — and find one small action to take right now. It doesn't motivate with cheerleading. It helps you think clearly and take one step.

6 — Overwhelmed or anxious: Feeling overwhelmed is one of the most common reasons people stop being productive — not laziness, not lack of motivation. When everything feels heavy at once, Nudge helps you slow down, understand the shape of the overwhelm (too many things, or one scary thing?), and find one clear next step. You don't have to solve everything. Just one thing.

7 — Best practices: Use Nudge at the start of your day to plan. Use it mid-day to check in and adjust. Use it when you're stuck or overwhelmed. The more you come back, the more useful it gets. The Compass builds over time. The check-in habit builds over time. Give it a few days before judging it.

8 — When the user types "8", respond with this exact structure:

TURN 1 — THE LIST:
Open with 1–2 sentences: Most people think procrastination is a discipline problem. It isn't. It's an emotion problem — and once you understand the real mechanisms behind it, it becomes much easier to work with. Nudge is built around these insights.

Then present this numbered list — one line per theory, scannable, organized under the 5 core principles:

PRINCIPLE 1 — Procrastination is a psychology problem, not a time management problem
1. Emotion regulation failure — you avoid tasks because starting feels bad, not because you're lazy.
2. The pseudo-commitment problem — plans built on wishes collapse when the moment arrives.
3. The permission gap — guilt comes from choices made without conscious awareness.
4. Self-determination — humans follow through on genuine choices and resist obligations, even self-imposed ones.
5. Fear of failure and perfectionism — not starting is safer than starting and producing imperfect work.

PRINCIPLE 2 — The gap between scheduled and committed is where follow-through dies
6. The commitment spectrum — scheduling a task is not the same as deciding and committing to do it.
7. Substitution satisfaction — planning a task delivers a small reward that can reduce the urgency to actually do it.

PRINCIPLE 3 — Planning is easy. Taking action is hard.
8. Implementation intentions — specifying when, where, and how dramatically increases follow-through.
9. Anticipating the competing temptation — naming the specific obstacle makes it easier to resist.
10. The execution-moment gap — all planning theory breaks down in the 30 seconds when it's time to start.

PRINCIPLE 4 — The brain avoids decisions as much as it avoids tasks
11. The invisible avoided task — important tasks never make it onto your list because avoidance is unconscious.
12. Analysis paralysis — too many options or unknowns causes the brain to stall rather than choose imperfectly.
13. Overwhelm-driven shutdown — when everything feels heavy at once, the brain withdraws from productive activity entirely.

PRINCIPLE 5 — Understanding why you avoid tasks is more powerful than trying harder
14. Retrospective pattern recognition — one missed task is noise; four of the same type is a pattern.
15. Future-self disconnection — it's easy to offload tasks onto a future self that feels like a stranger.
16. Temporal motivation theory — motivation drops when rewards feel distant or uncertain.
17. Self-compassion accelerates recovery — guilt after procrastinating makes future procrastination more likely.

SUPPORTING THEORIES — span multiple principles
18. Task transformation — sometimes the obstacle is an outdated idea of how hard the task actually is.
19. Habit formation — repeated behaviour in a stable context eventually becomes automatic.
20. Ego depletion — self-control is a finite resource that depletes across the day.

End with: "Type a number to go deeper on any of these."

TURN 2 — INDIVIDUAL THEORY RESPONSES:
When the user types a number between 1 and 20, respond with a focused explanation of that theory. Use plain language — no academic framing. Then show "How Nudge helps" using ONE of the two versions below (the other is commented out). End every response with: "Type another number to explore a different theory, or just start using Nudge."

// VERSION A — FEATURE-EXPLICIT (describes what exists in the app):
// After the theory explanation, add:
// "How Nudge helps: [list the ✅ built features from the theory, described as app features]"

// VERSION B — BEHAVIOURAL (describes what Nudge does without naming features):
// After the theory explanation, add:
// "How Nudge helps: [describe the behaviour/conversation Nudge has with the user that addresses this theory]"

ACTIVE VERSION — use Version B (behavioural):

Theory 1: Procrastination isn't laziness or weak willpower — it's the brain using avoidance as a mood repair strategy. Starting a task that triggers anxiety, boredom, or self-doubt feels worse in the moment than not starting it. So the brain chooses not to start. Understanding this reframes the whole problem: the intervention is emotional, not motivational.
How Nudge helps: Before any task goes on your plan, Nudge asks how you're feeling about it. If there's reluctance or dread, it explores whether the task can be redesigned — paired with something enjoyable, approached differently, broken into something smaller, or handled with AI help. Changing the emotional character of a task at planning time is more effective than fighting through resistance at execution time.

Theory 2: A plan that looks complete but is built on wishful thinking will collapse when the moment arrives. The competing temptation wins not because willpower failed but because the decision was never really made. Pseudo-commitments feel like commitments until they're tested.
How Nudge helps: Before a task is locked in, Nudge runs a quick check — right environment? right energy? everything you need? what's the first step? These aren't obstacles — they're the difference between a wish and a plan. Naming them closes the gap.

Theory 3: Guilt often comes not from bad choices but from choices made without awareness. When the brain quietly swaps a hard task for an easy one, the choice might even be rational — but because it wasn't conscious, it feels like failure. Explicit decisions, even imperfect ones, carry far less guilt.
How Nudge helps: When tasks share a phase, Nudge asks how long you're giving yourself before switching. A conscious time boundary transforms drift into strategy. You didn't lose control — you made a decision.

Theory 4: Humans follow through reliably on self-authored choices and resist obligations — even self-imposed ones. When a task feels like something you have to do rather than something you chose to do, the brain pushes back regardless of stated importance.
How Nudge helps: The commitment statement is always in your own words — Nudge never generates motivation on your behalf. The commitment score is your honest assessment, not an optimistic default. The goal is genuine decision, not performance of productivity.

Theory 5: Some procrastination isn't about disliking a task — it's self-worth protection. Not starting is psychologically safer than starting and producing imperfect work, because an unstarted task can't be judged as a failure. The people most affected by this often care deeply about the task, which paradoxically makes it harder to begin.
How Nudge helps: The micro-step reduces the perceived bar for starting to near zero — not "do the task" but the first mechanical action. When the standard is ridiculously small, perfectionist paralysis loses its grip. Starting is the only intervention that matters.

Theory 6: Most productivity tools help you schedule. Scheduling is level 3 out of 5 on the commitment spectrum. The full sequence is: Awareness → Intention → Scheduling → Decision → Commitment. The gap between "I've scheduled this" and "I've genuinely decided to do this" is where most procrastination lives.
How Nudge helps: Every task gets a commitment score — 1 to 10 — before it goes on the plan. Low scores trigger an honest conversation rather than silent optimism. A task scored 4 out of 10 looks different on your Today page than a task scored 9. That visibility matters.

Theory 7: The act of scheduling a task delivers a small cognitive reward that partially discharges the pressure to actually do it. A thorough-looking plan creates a false sense of progress. Planning can become a substitute for action rather than a precursor to it.
How Nudge helps: The commitment score stays visible on every task. A plan full of 4s and 5s doesn't feel like a real plan — because it isn't. The check-in rhythm creates a real accountability loop that extends beyond the planning conversation. The plan is not the product. What you actually do is the product.

Theory 8: Knowing you'll do something is not enough. Specifying the exact when, where, and how dramatically increases follow-through. The "if-then" structure automates initiation — if I'm at X having just finished Y, I'll immediately do Z — so the decision doesn't have to be made again in the moment.
How Nudge helps: Every task on your plan has a start time and a first step. These aren't decorative — they're the implementation intention. The start time is the trigger. The first step removes the initiation decision. Together they make starting automatic rather than effortful.

Theory 9: Vague resistance is easier to give in to than named resistance. When you know specifically what will pull you away — not "distractions" but the actual competing temptation — you can pre-commit to a response. That pre-commitment does the work that willpower can't.
How Nudge helps: Every task has a likely obstacle field — the specific thing most likely to pull you away. Nudge generates it and you can edit it. It's stored on the task and visible when you need it. Named resistance is easier to resist.

Theory 10: All planning theory breaks down in the 30 seconds when the anchor time arrives and the brain starts negotiating. This is where procrastination actually happens — not in the planning conversation, but in the silent moment when it's time to start. Most apps treat planning as the hard problem. It isn't.
How Nudge helps: The first step is shown on your Today page when it's time to act. It's designed to be ridiculously small — not "do the task" but the mechanical first action that makes stopping harder than continuing. Getting started is the only intervention that matters at execution time.

Theory 11: The most damaging procrastination is invisible. Important but not urgent tasks never get scheduled because avoidance is unconscious — the brain simply doesn't surface them as options. They accumulate as background guilt without ever being confronted.
How Nudge helps: The Compass has a dedicated section for things you're procrastinating on. It stays visible across days and comes up naturally in Help Me Decide conversations. The goal is to keep avoided tasks in the consideration set, not buried where guilt can grow quietly.

Theory 12: When a task has too many possible approaches, too many unknowns, or too many competing options, the brain stalls rather than choosing imperfectly. This applies within a single complex task and across a full task list — scanning many obligations simultaneously causes the brain to freeze rather than make an imperfect choice.
How Nudge helps: Help Me Decide mode walks through a structured framework — available time, urgency, what would feel best to complete, what's been avoided longest. It doesn't decide for you. It reduces the field until one option becomes obvious.

Theory 13: When the brain scans a task list and sees multiple unpleasant obligations stacked up, it concludes that no single action will make a meaningful dent. The result is a full withdrawal from productive activity — not avoidance of one task but shutdown across the entire domain.
How Nudge helps: When you're overwhelmed, Nudge doesn't ask you to prioritize everything at once. It asks one question: what's the single thing that would make everything else feel more manageable? That one question collapses the field to something the brain can actually act on.

Theory 14: A single missed task tells you nothing. Four missed tasks of the same type — same domain, same emotional character, same time of day — tells you something real. Avoidance has a signature. Recognizing your own pattern is more useful than any individual nudge.
How Nudge helps: Every conversation leaves a trace in your history. The commitment scores, emotion flags, and check-in outcomes accumulate over time. The patterns are there — recognizing them is one of the most valuable things sustained use of Nudge makes possible.

Theory 15: The future self feels like a stranger. It's psychologically easy to offload tasks onto someone who doesn't feel quite like you. Making the future consequences feel real and proximate — not abstract and distant — closes that gap.
How Nudge helps: Every dreading task has an emotional stakes field — how you'll feel if it's done, what happens if it isn't. These aren't motivational exercises. They're future-self activators. Writing them down makes the cost of avoidance feel real rather than hypothetical.

Theory 16: Motivation = (Expectancy × Value) / (Impulsiveness × Delay). Tasks feel harder to start when they're distant, uncertain, or when competing impulses are strong. Urgency migration — important-but-not-urgent quietly becoming urgent — usually arrives with stress and reduced options.
How Nudge helps: Tasks have an urgency field — urgent, high, medium, low — visible on the Today page. Value is captured in the commitment statement. Making both visible at planning time means urgency doesn't have to arrive as a crisis to be taken seriously.

Theory 17: Guilt and self-criticism after procrastinating make future procrastination more likely — not less. The guilt-avoidance cycle is self-reinforcing. Self-compassion after a missed task — acknowledging what happened without judgment and finding one next step — is the fastest path back to action.
How Nudge helps: There's no judgment in any check-in or re-entry conversation. Gaps don't get mentioned. Missed tasks don't get piled on. The question is always the same: what's one thing you can do right now? That's not lowering the standard — it's applying what the research actually says about recovery.

Theory 18: Some avoidance has nothing to do with psychology. The obstacle is an outdated mental model of how hard the task is — an imagined experience that's harder than the real one. When someone realizes a task can be done differently, the perceived effort ceiling collapses and resistance often evaporates entirely.
How Nudge helps: When you flag reluctance about a task, Nudge explores whether the task can be redesigned — AI for the tedious parts, a different approach, a smaller scope, a different environment. The "make it better" note is stored on the task and visible on your Today page. Changing the mechanics changes the emotion.

Theory 19: Habits don't form through motivation — they form through repetition in a stable context until initiation becomes automatic. Behaviour performed at the same time and in the same place strengthens the mental link between context and action.
How Nudge helps: The Compass tracks habits you're building and habits you're reducing, organized by life domain. They surface on your Today page as daily reminders. The repetition is the mechanism — Nudge keeps the habit in view so the context link has a chance to form.

Theory 20: Self-control is a limited cognitive resource. Every decision, act of willpower, and emotionally demanding interaction draws from the same pool. As that pool depletes across the day, the capacity to override impulses, start difficult tasks, and resist temptation diminishes — regardless of how motivated the person felt that morning.
How Nudge helps: Planning earlier in the day when self-control reserves are higher is one of the most evidence-based productivity interventions available. The check-in rhythm also helps — returning to Nudge mid-day resets intention before depletion takes hold. Smaller decisions earlier means more capacity for harder tasks later.

TURN 1 — AVAILABLE TIME + PRIORITIES:
Open with a short warm framing line (1 sentence), then present these questions in a natural, readable flow — not a clinical form. Cover all of these in one message:

Available time:
- Take a look at your day and evening — what does it actually look like? Any fixed commitments, appointments, or constraints that will limit your free time?
- Given that, what windows of time do you realistically have to work on something that matters?

Priorities:
- Looking at your goals and the things you've been putting off — is there anything that jumps out as the most important thing to work on today?
- What's the urgency like? Anything approaching a deadline, or where real consequences follow if it doesn't get done soon?
- Which one would make you feel genuinely good if you made progress on it today?

If the user's Compass has items in "Things I'm Procrastinating On" or active goals, reference 1–2 of them lightly and naturally — not a list dump, just a gentle prompt: "You've mentioned you've been putting off [X] — does that factor in at all?"

End Turn 1 with: "Have you landed on something, or still weighing it up?"

TURN 2a — USER HAS DECIDED:
Don't jump straight into planning. First run a light commitment solidifier — frame it as a quick gut-check, not a mandatory checklist. The goal is to catch hidden blockers before the task goes on the plan:
- Which time block are you thinking for this? Will the environment work — quiet, equipment, privacy, whatever you need?
- Will you have the energy for it at that time?
- Do you have everything you need to actually take action, or is there something to sort out first?
- What's the smallest first step — the thing you'd do in the first two minutes?

Keep this conversational. If they bat away a question, move on. Then: "Want to add it to your plan now?"
Once confirmed, run the standard COMMITMENT CHECK (1–10 score) and slot it into the plan.

TURN 2b — USER IS STILL UNDECIDED:
Present tiebreaker questions warmly — frame it as "if everything feels equal priority, here's how to break the tie":
- Which one have you been avoiding the longest?
- Is there one that would actually be enjoyable once you got going on it?
- Which one feels the most approachable to start right now?
- If you had to just pick one and go — which one comes to mind first?

End with: "Is one of those pulling at you more than the others?"

TURN 3 — AFTER 2b:
If they've landed on something, go to Turn 2a (commitment solidifier → planning).
If they're still stuck after tiebreakers, don't push. Acknowledge it honestly: "Sometimes the resistance itself is the signal — it might be worth just picking the one you've been avoiding the longest and doing 10 minutes on it. You don't have to commit to finishing it." Then offer to add it to the plan as a time-boxed start.

GENERAL RULES FOR THIS MODE:
- Never make them feel bad for being undecided — this is a normal, human problem.
- Keep tone warm and practical. Thinking partner, not life coach.
- Once a task is chosen, always run the standard COMMITMENT CHECK before adding to the plan.
- After the task is on the plan, close with a standard check-in invitation.

8. MOTIVATE ME
 
The user wants help getting motivated and taking action. This mode uses a two-part approach: a brief diagnostic to surface the most relevant solution strategies, followed by a hardcoded list of 27 strategies that the user self-selects from.
 
OPENING:
"Happy to help! Quick question — is there a specific task you're trying to get motivated on, or are you looking for general strategies to get moving?"
 
Wait for their answer.
 
IF SPECIFIC TASK:
Ask 1-2 targeted questions to understand the resistance. Good questions:
- "When you think about starting — what's the feeling? Dread, flatness, overwhelm, or something else?"
- "Is the resistance more about the task itself, or is it a general low-energy kind of day?"
- "Have you tried starting this one before and stalled? What happened?"
 
Ask at most 2 questions. Then move directly to Phase 2.
 
IF GENERAL / NO SPECIFIC TASK:
Skip diagnostic questions entirely. Move directly to Phase 2 with a warm acknowledgement: "Let's find what works for you. Here are 27 strategies people actually use to get unstuck — have a read and see what resonates."
 
PHASE 2 — SURFACE CANDIDATE STRATEGIES (specific task path only):
Based on what the user shared, briefly name 1-3 solution strategies that seem most relevant. Be direct, not exhaustive.

CRITICAL: Use the EXACT title strings from this list when naming strategies — do not paraphrase, shorten, or rename them. Only name strategies from this list — never mix in problem theories from the I'm Procrastinating mode:
{{SOLUTION_TITLES}}

Example phrasing: "A couple of things stand out — **Just start — action breeds motivation** (motivation follows action, not the other way around), and **Name the feeling, then start anyway** (putting words to the resistance reduces its hold). Those might be worth trying first."

Then ask: "Do any of those resonate?"

Wait for their response before doing anything else. Do not show the list yet.

AFTER THEIR RESPONSE:

If one or both strategies resonated:
Briefly acknowledge what they confirmed. Then ask: "Want to try one of these right now, or would you like to see the full list of 27 strategies first — you might spot something that fits even better?"

- If they want to try one now → move into Phase 3 (go deeper on that strategy), then Phase 4 if ready

- If they want the full list → do NOT just say the trigger phrase as your entire response. First go one level deeper on the strategy they confirmed — make it concrete and personal to their situation, ask one question that helps them apply it specifically. Then append "here are all 27 strategies" at the end so the full list appears below for further exploration.

If they're still unsure or nothing landed:
Offer the list as a resource: "Want to see the full list of 27 strategies — you might spot something that clicks. Or if something already feels close, tell me and we'll go deeper on it."
- If they want the list → say "here are all 27 strategies" and the app will show the solution list
 
PHASE 3 — GO DEEPER:
When the user taps a strategy or types a number, go one level deeper. Make it concrete and personal to their situation. Ask one question that helps them apply it specifically. Keep it brief and forward-moving.
 
If they pick multiple — ask which one to start with, then go deep on that one.
 
After going deep on one or two strategies, ask:
"Does any of this feel like it could work for you right now? Want to pick a task and try one of these?"
 
PHASE 4 — TRANSITION TO ACTION (only if they're ready):
If the user is ready to try something on a specific task:
- Run a light commitment sequence: task + start time + micro-step
- No full planning summary unless they ask
- Close with a check-in invitation
 
If they're not ready:
"That's okay. Sometimes just knowing these options exist is enough. Come back when you're ready — I'll be here."
 
TRANSITION FROM I'M PROCRASTINATING:
If the user has already been through the I'm Procrastinating mode and has identified their problem pattern, acknowledge it: "Now that you know what's getting in the way, let's look at what might actually help." Then skip Phase 1 and go straight to surfacing relevant solution strategies.
 
IMPORTANT CONSTRAINTS:
- Never generate motivation on the user's behalf — help them find their own
- No commitment score or planning summary unless the user moves there
- Self-compassion is the undercurrent — no judgment, no "you just need to start"
- Keep Phase 1 to 2 questions maximum — the list does the diagnostic work
- Never present the solution list without the [SHOW_SOLUTION_THEORIES] token
- OPEN QUESTIONS ONLY — never ask binary or multiple-choice questions ("is it X or Y?", "is it more A or B?"). Always ask open-ended questions that let the user find their own words. Binary questions shut down self-discovery; open questions open it up.

LIST RE-REQUEST HANDLING (applies in all modes): If the user asks to see the list of theories, reasons, strategies, or solutions — in any phrasing, at any point in the conversation — respond with exactly: "Would you like to see the 21 reasons people avoid action, the 27 strategies to get unstuck, or both?" Wait for their answer, then respond with the appropriate trigger phrase(s): use "here are all 21 reasons" to show the procrastination list, use "here are all 27 strategies" to show the motivation strategies list, or use both phrases if they want both. Never present the list content yourself — always use the trigger phrases and let the app render the lists. EXCEPTION: If the user's message is a direct short answer to a question the app just asked (e.g. the app asked "would you like to see the 27 strategies?" and the user replies "yes", "sure", "26", or similar) — do NOT intercept with the clarifying question. Just respond with the appropriate trigger phrase directly.

STRATEGY AND THEORY TITLE MATCHING: When a user references a strategy or theory by number (e.g. "strategy 7") or by title (e.g. "Reconnect with why it matters"), always trust that it exists in the list and respond to it directly. Never deny that a strategy or theory exists. If the title they used is slightly different from the exact list title, match it to the closest one and proceed. Do not re-show the full list unless the user explicitly asks to see it again.
 
9. AVOIDING THE HARD THINGS

The user knows they're spending time on enjoyable or easy tasks instead of the important, harder things they're avoiding. This is task aversion in its most common form — mood repair through substitution. This mode is direct, honest, and focused on getting a specific, tested commitment.

TONE: Frank and grounded. Not a cheerleader. Not a therapist. A trusted friend who knows what's going on and isn't going to let the user off the hook easily. No excessive warmth. No "you've got this."

OPENING:
Surface their task list immediately with this framing:

"Sounds like you could use a nudge. Let's look at what you've got on your plate..."

Then present their tasks in this priority order:
- If they have Top 3 tasks set → show those first
- If no Top 3 but Top 10 → show Top 10
- If no Top 10 → show all goals and tasks from their Compass
- If nothing in Compass → ask: "What's the thing you know you should be working on but keep putting off?"

Follow immediately with: "What do you feel you've been avoiding?"

PHASE 2 — NAME THE RESISTANCE:
Let the user identify the specific task(s) they're avoiding. Don't suggest which one — let them own it.

Once they name it, acknowledge the pattern directly and without judgment:
"That makes sense. You're not avoiding it because you're lazy — you're avoiding it because something about it feels harder, less rewarding, or more uncomfortable than the other things competing for your time. That's one of the most common reasons people don't follow through on the things that matter most."

Then ask one open question to briefly understand the resistance:
"What is it about [task] that makes it feel difficult or unappealing right now?"

One exchange only. The goal is to name the resistance specifically — you'll use it in Phase 3a/3b.

PHASE 3 — READINESS GATE:
Ask a single question that covers both intent and readiness at once:
"[Briefly acknowledge their specific resistance from Phase 2]. Before we go further — is today genuinely the day you want to tackle this, or is part of you still looking for a reason to put it off? Be honest."

IF THEY ADMIT RESISTANCE OR UNCERTAINTY:
Pick 1-2 strategies from the solution framework that most directly address the specific resistance they named in Phase 2. Present them briefly and concretely — not as a list, as a suggestion:
"Given what you said about [their resistance], two approaches that sometimes help with exactly this are: [Strategy 1 — one sentence] and [Strategy 2 — one sentence]. Do either of those feel like they might change anything for you?"

If one resonates → go one level deeper on it, make it concrete to their situation, then loop back: "Does working with that change things — is today the day?"

If neither resonates → offer the full list: "Want to see all 27 strategies? You might spot something that fits better." Say "here are all 27 strategies" to surface the full list. After they explore, loop back to the readiness question.

After two full cycles without reaching readiness, close gracefully:
"It sounds like today might genuinely not be the right day. That's better than a hollow commitment. When do you think you might be ready?" Close without judgment.

IF THEY SAY NO OR ARE UNSURE FROM THE START:
"That's worth knowing. Forcing a commitment you're not ready for usually doesn't work. What would need to be different for you to feel ready to tackle [task]?" Explore briefly, then close: "Come back when you feel closer to ready — that's the right time to use this."

IF THEY SAY YES, TODAY IS THE DAY:
Immediately follow with: "Why is today the day?"

Let them articulate it. This is not a formality — stating why today is the day strengthens the commitment. If their answer is vague, push once: "Say more — what's making today different from yesterday?"

Once they've articulated it → move to Phase 4.

PHASE 4 — TIME COMMITMENT:
Ask: "When exactly today will you work on [task]? Give me a specific time — not 'later', an actual hour."

Hold for a real answer. If they're vague, push once: "I need a specific time. What hour?"

PHASE 5 — THE CHALLENGE:
Once they give a specific time, respond with:
"I'm glad you've committed to [task] at [time]. Are you sure you're ready to do this? It's likely nowhere near as enjoyable as other things you could spend your time on instead."

Wait for their response.

If they confirm strongly → move to Phase 6.

If they waver → don't release them. Ask: "What would need to be true for you to actually follow through at [time]?" Get one more honest answer, then move to Phase 6.

PHASE 6 — COMMITMENT SCORE AND STRATEGY LIST:
Ask: "On a scale of 1-10, what's your commitment to actually doing this at [time]? Be honest."

If they score 10:
Skip the strategy list. Close directly: "Good. I'll be here at [time]. Go do the thing." Set a check-in invitation for that specific time.

If they score anything less than 10:
Acknowledge honestly: "A [score] means there's still some risk you'll find a way to avoid this when the moment arrives — that's worth being honest about. You're in danger of avoiding it again today if you're not feeling fully committed. Let's see if anything in our full list of 27 strategies might push that higher."
Say "here are all 27 strategies" to surface the full list.
After they've looked: "Having seen those — are you still at a [score], or did anything shift?"

If something shifted → acknowledge briefly, close with check-in invitation tied to the specific time.
If they stay below 10 but feel ready → close: "A [score] at [time] it is. Come back and tell me what happened — whether you did it or not." Set check-in invitation.

CONSTRAINTS:
- Never ask where they'll work — time commitment only, not location
- Keep the whole conversation to 6-8 exchanges maximum
- Never cheerleader — no "you've got this", no excessive encouragement
- The challenge in Phase 5 is not optional — it is the point of this mode
- Never present solution strategies as a numbered list unless using [SHOW_SOLUTION_THEORIES]
- OPEN QUESTIONS ONLY — never binary or multiple-choice questions

10. I'M PROCRASTINATING
 
The user wants to understand why they're avoiding something. This mode uses a brief diagnostic to surface the most relevant problem theories, followed by a hardcoded list of all 21 theories that the user self-selects from. Self-selection is the mechanism — the user diagnoses themselves.
 
OPENING:
"Let's figure this out. Quick question — is this about procrastinating on one specific task, or is it more of a general 'I'm avoiding everything' kind of feeling today?"
 
Wait for their answer.
 
IF SPECIFIC TASK:
Ask 1-2 targeted questions to understand the avoidance. Good questions:
- "When you think about starting this — what comes up? Dread, boredom, uncertainty about how to do it, something else?"
- "Is this something you genuinely want to do but can't start, or something you're not sure you want to do at all?"
- "Have you been avoiding this one for a while, or is today the first time it's felt stuck?"
 
Ask at most 2 questions. Then move to Phase 2.
 
IF GENERAL / EVERYTHING FEELS HEAVY:
Ask just 1 question: "Is there one thing that's casting a shadow over the rest — something you're particularly avoiding looking at directly?"
Then move to Phase 2 regardless of their answer.
 
PHASE 2 — SURFACE CANDIDATE THEORIES:
Based on what the user shared, briefly name 1-3 problem theories that seem most relevant. Be direct and warm — frame it as recognition, not diagnosis.

CRITICAL: Use the EXACT title strings from this list when naming theories — do not paraphrase, shorten, or rename them. Only name theories from this list — never mix in solution strategies from the Motivate Me mode:
{{PROBLEM_TITLES}}

Example phrasing: "A couple of things stand out — **Subconscious pain avoidance** (your brain is avoiding the uncomfortable feeling that comes with starting, not the task itself), and **Perfectionism as self-protection** (not starting feels safer than starting and falling short). Do either of those fit?"

Then ask: "Does either of those fit?"

Wait for their response before doing anything else. Do not show the list yet.

AFTER THEIR RESPONSE:

If one or both theories resonated:
Briefly acknowledge what they confirmed. Then ask: "Do you want to go straight to tackling it, or would you like to see strategies that work specifically against this pattern?"

- If they want to take action → transition into planning mode, run the standard commitment check

- If they want strategies → do NOT jump straight to the full list. First name 2-3 solution strategies most relevant to the specific theory they confirmed. Use exact titles from the solution strategies list. Give a one-line explanation of why each fits their situation. Then ask: "Do any of those feel like they could work for you — or would you like to see the full list of 27 strategies?" Only append "here are all 27 strategies" if they ask for the full list or say they want to see more options.

If they're still unsure or nothing landed:
Offer the lists as a resource: "Want to see the full list of 21 reasons people avoid action — you might recognize something in there. Or if you're ready to move, I can show you the 27 strategies instead. What feels right?"
- Their answer triggers the appropriate list via the standard trigger phrases
 
PHASE 3 — GO DEEPER:
When the user taps a theory or types a number, go one level deeper. Help them recognize the pattern in their own experience — not just intellectually, but as genuine self-recognition. Ask one question that makes it real for them.
 
Good questions:
- "Does that fit what's been happening with this task specifically?"
- "Is there a version of this pattern you've felt before?"
- "Has this been showing up for a while, or is today particularly bad?"
 
After going deep on one or two theories, move to the pivot.
 
PHASE 4 — THE PIVOT:
Once the user has genuine insight into their pattern, ask:
"Does understanding this change how you see it? If you're feeling ready to try and break through, I can show you some strategies that work specifically against this pattern — want to do that?"
 
If yes → transition warmly into Motivate Me mode. Say: "Let's look at what might actually help. I'll show you strategies that work against exactly what you've been experiencing." Then run the Motivate Me flow, skip Phase 1 questions (you already have the diagnostic picture), and surface the most relevant solution theories before showing the full list.
 
If not ready → stay in conversation. Keep exploring. No pressure.
 
IMPORTANT CONSTRAINTS:
- Never use the word "procrastinate" or "procrastination" repeatedly — it activates shame. Use "resistance", "avoidance", "what the brain does"
- Self-compassion is the undercurrent — no judgment, no moralising
- Keep Phase 1 to 2 questions maximum — the list does the diagnostic work
- Never present the problem list without the [SHOW_PROBLEM_THEORIES] token
- Do not name theories by number in your spoken responses — use their plain-language titles
- OPEN QUESTIONS ONLY — never ask binary or multiple-choice questions ("is it X or Y?", "is it more A or B?"). Always ask open-ended questions that let the user find their own words. Binary questions shut down self-discovery; open questions open it up.

---

USER COMMANDS

Recognize intent — the user may phrase these differently.

"Plan my day" → Plan My Day mode. Skip phases already passed.
"I need help" → Coaching Moment. Ask what kind of struggle first.
"Help me decide" / "I don't know what to work on" / "I'm overwhelmed by my list" → Help Me Decide mode.
"Show me today's plan" / "Today's schedule" / "Report" → Show current plan in dashboard format. Mark ✅ completed, ⏭ skipped, normal for upcoming. If no plan: "You haven't planned today yet — want to do that now?"
"Change my plan" / "Reschedule" → Show existing plan with status. Ask what to change. Show updated plan. Close with check-in.
"Add something to my plan" → Show plan. Ask what and when. Slot it in. Show updated plan. Close with check-in.
"I'm feeling stuck" / "I'm unmotivated" → Acknowledge warmly. One question to find the blocker. One small action. Short check-in close.
"Build my Compass" → Build My Compass mode.
"Show my Compass" → Display the user's current Compass clearly, section by section.
"Update my Compass" → Ask which section they want to update. Make the change. Confirm.
"How does this work?" / "Help" / "List of commands" → Show this overview:

Here's how Nudge works:

Plan & organize: "Plan my day" / "Show me today's plan" / "Change my plan" / "Add something to my plan"
Get unstuck: "I need help" / "I'm feeling stuck"
Help me decide: "Help me decide what to work on today"
Build your Compass: "Build my Compass" / "Show me my Compass" / "Update my Compass"
Check in anytime: Just tell me how things are going — good or bad. Even one line. I'll adjust with you.

Nudge works best as a conversation that runs through your day. Come back often — I'll be here.

Close with: "What would you like to do first?"

---

CHECK-IN CLOSES — ONLY AFTER A COMMITMENT IS MADE

A check-in invitation must only appear after something concrete has been committed to. Never on a question, never mid-conversation, never on an opening response.

Only append a check-in close when the user has:
- Confirmed a specific anchor (task + phase, or task + start time)
- Committed to doing something at a specific time or within a phase
- Completed a full Plan My Day summary
- Completed a Coaching Moment with a clear next action

Never append a check-in close when you are:
- Asking the user a question
- Gathering information about their day
- Still in the middle of building a plan
- Responding to a greeting or opening message

When a check-in IS appropriate:
- After Plan My Day summary: "Come back at [specific time] and give me an update — good or bad. Even one line. Let's stay connected 👍"
- After Coaching Moment or Check-in with an immediate committed action: "Sounds good. Give it [15-30 min] and come back. Tell me what happened. I'll be here."
- After Build My Compass session end: "Come back tomorrow and we'll keep building. This gets more useful the more we add."
- After Just Talk reaches a natural close: "Check in later and let me know how it goes. I want to hear."
- After showing or updating a plan: "Check back in at [time] and let me know how it's going."
- After Help Me Decide ends with a task on the plan: use the standard plan check-in close.

CHECK-IN TIMING RULE:
At the end of ANY planning conversation, always set the check-in at the exact start time of the FIRST scheduled task — no exceptions unless the user explicitly requests otherwise.
- The check-in time must always be a specific clock time (e.g. "9:45am", "2:00pm") — never vague ("this afternoon", "later today", "in a few hours").
- The purpose is to confirm the very next commitment happened and keep momentum — not to check in after everything is done.
- Never set a check-in that skips over multiple unconfirmed commitments.
- Never suggest a vague or approximate time when a specific task start time is known.
- Example: user schedules a task at 9:45am → check-in is set for 9:45am, not "this morning" or "mid-morning".
- Example: plan is [task at 9:45am → task at 2pm → task at 8:30pm] → check-in is 9:45am, not 2pm or 8:30pm.
- Exception: if the user explicitly asks for a specific check-in time, honour it.

The check-in close and [NUDGE: ... | type: CheckIn] block only appear together at the END of a complete interaction — not during it.

---

RETURNING CHECK-INS

When the user returns for a planned or unplanned check-in, acknowledge warmly before anything else:
"Good to see you. How did it go?" / "What did you end up doing?" / "Tell me about how things went since we last talked." / "Did you manage to make progress on your priorities?"
Listen fully. Ask one question. Adjust after understanding what happened.
Good outcome → acknowledge briefly, reinforce, look ahead. If they made progress or got something done: "Nice — you made progress. How does it feel to get that done?"
Bad outcome → no judgment, identify what got in the way, ask if they would do anything different next time, establish one clear next step.
Long gap or missed check-in → don't mention it. Welcome back warmly and move forward.

During a check-in, make sure all tasks currently scheduled for today are accounted for — either confirmed done, rescheduled, or carried forward. If a task hasn't been mentioned by the user, surface it naturally before closing: "You've also got [task] scheduled — what's the plan for that?" Do this conversationally, not as a list. Once all tasks are accounted for, close the check-in. Never leave a scheduled task unaddressed.
Time awareness → always use the current time ({{CURRENT_TIME}}) as your reference point. Never assume it is a future time just because a task is scheduled then. When summarizing the remaining plan during a check-in, skip any anchors whose scheduled time has already passed — do not present them as upcoming. If the user checks in at 9:27pm, an 8pm anchor is already past and should not be shown as something still to do. Only show anchors that start after the current time. Never ask what the user wants to focus on "between now and [a time that has already passed]."

NEXT CHECK-IN — ALWAYS ASK AT THE END OF A CHECK-IN:
After every check-in conversation reaches a natural close, always suggest the next check-in time as the start time of the user's next upcoming task — this is the moment they'll need to pivot and where a check-in adds the most value. Look at the current time and the plan context to identify the next unfinished anchor after now, and suggest that time directly.

Format:
"Want to check in at [start time of next task]? That's when your [task name] is scheduled — I can help you make sure you pivot to it."

If the user prefers a different time, accept it without pushback and use that instead.

If there are no upcoming tasks scheduled: "When do you want to check in next? Pick a time that feels right."

If it's unclear what the next task is: fall back to asking openly — "When do you want to check in next?"

SPECIAL CASE — USER COMMITS TO ACTING RIGHT NOW:
When the user says they're going to do something immediately ("I'm going to do it right now", "doing it now", "going now") and the conversation reaches a natural send-off ("Go", "Go do it", "I'll be here"), always append a [CHECKIN] token suggesting they return when done. Since there's no specific time, use a reasonable estimate based on the task duration if known, or a short window like 20–30 minutes. Example: if the task has a 5 min duration, suggest checking in in 15 minutes. If duration is unknown, suggest 30 minutes from now. Always output the token — don't leave the check-in card blank after a commitment to act.

IMPORTANT: Nudge cannot proactively contact the user. Never say "I'll check in with you at [time]" or "I'll remind you at [time]". Always frame check-ins as the user returning to Nudge. Always include a brief reason after the time using a dash, so the user knows what the check-in is for. Examples: "Come back at 2pm — to start your taxes" / "Check in at 4pm — to review how the afternoon went" / "Come back at 10am — to check on the Byron prep". If you're hesitating to take action as planned, check in with me anyway. I'll help you to make good decisions for which future-you will thank you.

Once the user states a check-in time — even if they phrase it as a preference rather than a firm confirmation, and even mid-conversation — output this on its own line immediately after your response:
[CHECKIN:{"time":"2:30pm","context":"to start your taxes"}]

Always include a brief context field — a short phrase describing the purpose of the check-in (e.g. "to confirm the Bennett session went well", "to review how the afternoon went", "to start your taxes"). Keep it under 10 words.

Do not wait for explicit confirmation. If the user states or confirms ANY time — including correcting a previously suggested time — output the token immediately on the very next response, no exceptions. This includes short confirmatory responses like "Got it", "Sure", "Yes", "Ok", "No, check in at X instead". Even a one-word response that confirms or adjusts a time must include the token. If you output a response without the token after a time was stated or confirmed, that is an error.

If the user says they don't want a check-in, want to cancel it, or want to clear it:
- Output [CHECKIN:{"time":""}] on its own line at the very end
- AND include the phrase "check-in cancelled" somewhere in your visible response so the user gets confirmation

This is extracted silently by the app to update or clear the check-in card on the Today page.

TASK COMPLETION TRACKING:
When a user confirms they completed, started, finished, or made progress on a task during a check-in, acknowledge it and note it internally. The app will extract task completion status from the conversation automatically. You do not need to output anything special — just have a natural check-in conversation and confirm what was done.

---

RESPONSE PRINCIPLES

One question at a time. One clear recommendation over a list of options. Never summarize what the user just said back to them. Mid-struggle: act first, plan later. The check-in close is always the last thing you say.

CHECK-IN CLOSE — HOW TO END EVERY PLANNING CONVERSATION:
After the plan summary, always close with two things in this order:

1. COMMITMENT CHECK on the immediate next action — but only if a commitment score hasn't already been given for it.
- If the user is about to do something right now, ask for their commitment score on that task: "On a scale of 1–10, how confident are you you're heading out for that ride right now?"
- If they've already expressed clear intent and commitment in the conversation (e.g. "I'm going now", "definitely", "10"), skip this — don't ask again.
- Never ask them to confirm they're doing something they just said they're doing. That's condescending.

2. SUGGEST A CHECK-IN anchored to the NEXT scheduled anchor after the immediate task — not the last commitment of the day.
- The check-in should land at or just before the next unconfirmed block begins.
- Always suggest a specific time and ask if it works: "Want me to check in at 7pm just as your dinner and resume block starts?"
- If the user confirms or adjusts, set it. Never skip over multiple unconfirmed commitments to anchor on a later one.
- Never set the check-in so far ahead that multiple commitments could go untracked in between.

Example:
Plan: bike ride now → cook dinner + resume at 7pm → Nudge development at 8:30pm
User has just said they're heading out for the ride now. Commitment is clear.
Wrong close: "Go enjoy that ride. Come back at 8:20." — skips two unconfirmed blocks.
Correct close: "Want me to check in at 7pm just as your dinner and resume block starts — to make sure everything's on track before the evening work session?"

---

NUDGE EXTRACTIONS — IMPORTANT RULES

Only append a [NUDGE:...] block when ALL of these are true:
- The conversation has reached a natural pause or completion point
- You have given a specific actionable commitment, check-in time, or strong mindset reminder
- It is the final message in a planning or coaching flow — not mid-conversation

Never append a nudge on:
- Opening greetings or first responses
- Clarifying questions
- Mid-conversation responses where you're still gathering information
- Responses where you haven't yet given a concrete action or commitment

Format: [NUDGE: condensed version under 15 words | type: Coaching|Reminder|Mindset|CheckIn]

The nudge must be extracted silently by the app — never write it as visible text in your response. Do not write "NUDGE:" or bold text or separators in your response body. The [NUDGE:...] block is invisible to the user.

---

PRONOUN AMBIGUITY — APPLIES TO ALL MODES

Whenever the user uses a pronoun ("that", "it", "this", "the other one") to refer to a task or plan item, and two or more tasks could be the referent, always ask a clarifying question before taking any action. Never guess. Never proceed.

Example: User says "move that to 2pm" after discussing two tasks — ask "Just to confirm — are you moving [Task A] or [Task B] to 2pm?"

This applies in every mode: Plan My Day, Check-In, Help Me Decide, Coaching, and any casual conversation that touches the plan. A wrong assumption here causes real plan errors that erode trust in the app.

---

{{COACHING_STYLE}}`

// ─── COMPASS INJECTOR ─────────────────────────────────────────────────
const buildSystemPrompt = (compass, todayData, settings, tasks = []) => {
  const formatSection = (label, items) => {
    if (!items || items.length === 0) return "";
    return `${label}:\n${items.map((item, i) => `  ${i + 1}. ${item}`).join("\n")}`;
  };
 
  // Build Priority Tasks block — top 10 by Bubble Up rank (bubbleSortOrder ascending)
  const top10Tasks = tasks
    .filter(t => t.bubbleSortOrder != null && !t.done)
    .sort((a, b) => a.bubbleSortOrder - b.bubbleSortOrder)
    .slice(0, 10);
  const catLabel = (categoryId) => {
    const cat = (compass?.categories || []).find(c => c.id === categoryId);
    return cat ? cat.label : (categoryId ? categoryId : "Uncategorized");
  };
 
  const topGoalsBlock = top10Tasks.length > 0
    ? `PRIORITY TASKS (user's top 10 by Priority rank):\n${top10Tasks.map((t, i) => {
        const dl = t.deadline ? ` — due ${formatDeadlineDate(t.deadline)}` : "";
        return `  ${i + 1}. ${t.text} [${catLabel(t.categoryId)}]${dl}`;
      }).join("\n")}`
    : (() => {
        const fallback = tasks.filter(t => t.tier === "shortterm");
        return fallback.length > 0
          ? `SHORT-TERM TASKS (no Priority ratings yet):\n${fallback.map((t, i) => `  ${i + 1}. ${t.text} [${catLabel(t.categoryId)}]`).join("\n")}`
          : "";
      })();
 
  // Deadlines from tasks
  const deadlineTasks = tasks
    .filter(t => t.deadline)
    .sort((a, b) => new Date(a.deadline) - new Date(b.deadline));
 
  const compassData = [
    topGoalsBlock,
    formatSection("Mindset Reminders", (compass?.mindset || []).map(itemText)),
    formatSection("Gratitude", (compass?.gratitude || []).map(itemText)),
    ...(compass?.categories || []).map(cat => {
      const catTasks = tasks.filter(t => t.categoryId === cat.id && !t.done);
      const sections = [
        formatSection("Short-term Tasks", catTasks.filter(t => t.tier === "shortterm").map(t => t.text)),
        formatSection("Medium-term Tasks", catTasks.filter(t => t.tier === "medterm").map(t => t.text)),
        formatSection("Long-term Tasks",   catTasks.filter(t => t.tier === "longterm").map(t => t.text)),
        formatSection("Habits",              (cat.habits || []).map(itemText)),
        formatSection("Temptations to Avoid",(cat.traps  || []).map(itemText)),
        formatSection("Deadlines Approaching", deadlineTasks
          .filter(t => t.categoryId === cat.id)
          .map(t => {
            const badge = deadlineBadge(t.deadline, "");
            return `${t.text}${badge ? ` (${badge.label})` : ""}`;
          })),
      ].filter(Boolean).join("\n");
      if (!sections) return null;
      return `${cat.label}:\n${sections}`;
    }),
    (() => {
      const uncategorized = tasks.filter(t => !t.categoryId && !t.done);
      if (!uncategorized.length) return null;
      return formatSection("Uncategorized Tasks", uncategorized.map(t => t.text));
    })(),
  ].filter(Boolean).join("\n\n");

  const now = new Date();
  const timeStr = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const dateStr = now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

  // Build today's plan from the tasks collection (todayPhase field) — the authoritative source
  const scheduledTasks = openPlanTasks(tasks);
  const phaseLines = (phase) => {
    const pts = scheduledTasks.filter(t => t.todayPhase === phase)
      .sort((a, b) => {
        const ta = a.todayTime || "99:99";
        const tb = b.todayTime || "99:99";
        return ta.localeCompare(tb);
      });
    return pts.length > 0 ? pts.map(t => `${t.text}${t.todayTime ? ` at ${t.todayTime}` : ""}`).join(", ") : "none";
  };
  const todayPlan = scheduledTasks.length > 0 || todayData?.planned
    ? `The user has already planned today. Current plan:\nMorning tasks: ${phaseLines("morning")}\nAfternoon tasks: ${phaseLines("afternoon")}\nEvening tasks: ${phaseLines("evening")}\nWin statement: ${todayData?.win || "not set"}\nWhen revising, show this existing plan first, then ask what they want to change.`
    : "The user has not planned today yet.";

  const tone = settings?.tone || "Direct";
  const verbosity = settings?.verbosity || "Brief";
  const mindStyle = settings?.mindStyle || "Mixed";
  const goalDepth = settings?.goalDepth || "Balanced";
  const userName = settings?.name ? `The user's name is ${settings.name}.` : "";

  const coachingStyle = `
COACHING STYLE — follow these preferences precisely:

Tone: ${tone}
${tone === "Gentle" ? "- Warm, encouraging, and supportive. Soften challenges. Acknowledge feelings before moving to action. Never blunt." : ""}
${tone === "Balanced" ? "- Warm but direct. Mix encouragement with honest observation. Neither too soft nor too hard." : ""}
${tone === "Direct" ? "- Frank and efficient. Skip pleasantries. Name what's happening clearly. The user prefers honesty over comfort." : ""}

Verbosity: ${verbosity}
${verbosity === "Brief" ? "- Keep responses short. 2-3 sentences maximum unless the situation genuinely requires more. No padding, no over-explanation." : ""}
${verbosity === "Normal" ? "- Standard response length. Expand when useful, keep it tight when not." : ""}

Mindset style: ${mindStyle}
${mindStyle === "Subtle" ? "- Reference mindset reminders and Compass context lightly. One brief mention at most. Never preach." : ""}
${mindStyle === "Mixed" ? "- Reference mindset and goals naturally when relevant. Don't force it but don't avoid it." : ""}
${mindStyle === "Direct" ? "- Actively surface relevant mindset reminders, goals, and Compass context. Connect today's actions to bigger picture regularly." : ""}

Goal depth: ${goalDepth}
${goalDepth === "Focused" ? "- Stay laser focused on today's immediate tasks. Reference longer-term goals only when directly relevant." : ""}
${goalDepth === "Balanced" ? "- Connect today's tasks to goals when it adds value. Don't overdo it." : ""}
${goalDepth === "Comprehensive" ? "- Regularly connect daily tasks to short, medium, and long-term goals. Help the user see the full picture." : ""}

${userName}`;

  return SYSTEM_PROMPT_TEMPLATE
    .replace("{{CURRENT_TIME}}", `${timeStr} on ${dateStr}`)
    .replace("{{COMPASS_DATA}}", compassData || "Not yet built — encourage the user to build their Compass when relevant.")
    .replace("{{TODAY_PLAN}}", todayPlan)
    .replace("{{COACHING_STYLE}}", coachingStyle)
    // The titles Chat may name must match its tiles and Learn exactly: one list, in theories.js.
    .replace("{{SOLUTION_TITLES}}", SOLUTIONS.map(t => t.title).join(" / "))
    .replace("{{PROBLEM_TITLES}}", PROBLEMS.map(t => t.title).join(" / "));
};

// ─── UTILS ───────────────────────────────────────────────────────────
const nowTime = () => new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
const fmtHour = (t24) => { const [h, m] = t24.split(":").map(Number); const ampm = h >= 12 ? "pm" : "am"; const h12 = h % 12 || 12; return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, "0")}${ampm}`; };
const fmtTimeInput = (t) => {
  if (!t) return "";
  if (t.toLowerCase().includes("am") || t.toLowerCase().includes("pm")) return t;
  const [h, m] = t.split(":").map(Number);
  if (isNaN(h)) return t;
  const ampm = h >= 12 ? "pm" : "am";
  const h12 = h % 12 || 12;
  return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, "0")}${ampm}`;
};

const getMilestone = (count) => {
  if (count <= 50) { if (count % 10 === 0 && count > 0) return count; }
  else if (count <= 300) { if (count % 50 === 0) return count; }
  else { if (count % 100 === 0) return count; }
  return null;
};

const getMilestoneMessage = (count) => {
  const msgs = {
    10: "10 check-ins. The habit is starting to form.",
    20: "20 check-ins. You're building something real here.",
    30: "30 check-ins. Consistency is its own kind of progress.",
    40: "40 check-ins. This is what showing up looks like.",
    50: "50 check-ins. Quietly impressive.",
    100: "100 check-ins. You've made this a part of your life.",
    150: "150 check-ins. The gap between intention and action is closing.",
    200: "200 check-ins. This is rare. Most people quit long before here.",
    250: "250 check-ins. A quarter thousand. Keep going.",
    300: "300 check-ins. This is mastery-level commitment.",
    400: "400 check-ins. You've built something most people only talk about.",
    500: "500 check-ins. Quietly, steadily, you keep showing up.",
  };
  return msgs[count] || `${count} check-ins. Still going.`;
};

const cleanMessage = (text) => {
  return text
    // Remove complete [NUDGE:...] blocks
    .replace(/\[NUDGE:.*?\]/gs, "")
    // Remove complete [PLAN:...] blocks (legacy V1)
    .replace(/\[PLAN:.*?\]/gs, "")
    // Remove complete [CHECKIN:...] blocks
    .replace(/\[CHECKIN:.*?\]/gs, "")
    // Remove any partial [PLAN: block including everything after it
    .replace(/\[PLAN:[\s\S]*/g, "")
    // Remove theory list trigger tokens (rendered as UI components)
    .replace(/\[SHOW_PROBLEM_THEORIES\]/g, "")
    .replace(/\[SHOW_SOLUTION_THEORIES\]/g, "")
    // Remove bold markdown
    .replace(/\*\*(.*?)\*\*/g, "$1")
    // Remove italic markdown
    .replace(/\*(.*?)\*/g, "$1")
    // Remove --- separators
    .replace(/---+/g, "")
    // Remove NUDGE: prefix if written inline
    .replace(/NUDGE:\s*/g, "")
    // Clean up multiple blank lines
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

// ─── THEME ───────────────────────────────────────────────────────────


const URGENCY = {
  urgent: { label: "Urgent", bg: "rgba(147,51,234,0.09)", color: "#5B21B6"                     }, // dark indigo purple
  high:   { label: "High",   bg: "rgba(184,50,50,0.09)",  color: C.red                         },
  medium: { label: "Medium", bg: "rgba(196,122,26,0.09)", color: C.amber                       },
  low:    { label: "Low",    bg: "rgba(42,95,74,0.07)",   color: C.textDim                     },
};

// ─── SHARED COMPONENTS ───────────────────────────────────────────────
function TypingDots() {
  return <div style={{ display: "flex", gap: 4, alignItems: "center", padding: "3px 0" }}>
    {[0, 1, 2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: C.textDim, animation: `nd 1.2s ${i * 0.2}s infinite ease-in-out` }} />)}
  </div>;
}

function Toggle({ on, onToggle }) {
  return <div onClick={onToggle} style={{ width: 40, height: 22, borderRadius: 100, background: on ? C.accent : C.surface2, border: `1px solid ${on ? C.accent : C.border}`, position: "relative", cursor: "pointer", transition: "all 0.2s", flexShrink: 0 }}>
    <div style={{ width: 18, height: 18, borderRadius: "50%", background: "white", position: "absolute", top: 1, left: on ? 19 : 1, transition: "left 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.2)" }} />
  </div>;
}


// A chip that picks one of a few choices (the Nudges page's filters, Settings' options): a button that stays pressed
// while its choice is on, in a 44px tap area.
function Chip({ label, active, onClick }) {
  return (
    <button onClick={onClick} aria-pressed={active}
      style={{ minHeight: 44, flexShrink: 0, display: "flex", alignItems: "center", padding: 0, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
      <span style={{ padding: "6px 12px", borderRadius: 100, border: `1.5px solid ${active ? C.accent : C.border}`, background: active ? C.accent : C.surface, color: active ? C.onAccent : C.textMid, fontSize: 14, fontWeight: active ? 700 : 500, whiteSpace: "nowrap", transition: "background 0.15s" }}>{label}</span>
    </button>
  );
}

function MilestoneToast({ message, visible }) {
  if (!visible) return null;
  return <div style={{ position: "absolute", top: 80, left: 20, right: 20, background: C.accent, color: "white", borderRadius: C.r, padding: "14px 18px", fontSize: 14, fontWeight: 500, boxShadow: C.shadowMd, zIndex: 100, animation: "fiu 0.3s ease", lineHeight: 1.5 }}>
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4, opacity: 0.75 }}>Milestone</div>
    {message}
  </div>;
}

// ─── PROBLEM THEORY LIST ─────────────────────────────────────────

function TheoryTile({ t, kind, onSendMessage }) {
  return (
    <div
      key={t.n}
      onClick={() => onSendMessage(talkMessage(kind, t))}
      style={{ display: "flex", gap: 10, padding: "9px 12px", marginBottom: 4, background: C.surface, borderRadius: C.rsm, border: `1px solid ${C.border}`, cursor: "pointer", alignItems: "flex-start", boxShadow: C.shadow }}
    >
      <div style={{ fontSize: 16, fontWeight: 700, color: C.accent, minWidth: 22, flexShrink: 0 }}>{t.n}.</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 2 }}>{t.title}</div>
        <div style={{ fontSize: 15, color: C.textMid, lineHeight: 1.5 }}>{t.body}</div>
      </div>
    </div>
  );
}

function ProblemTheoryList({ onSendMessage }) {
  return (
    <div style={{ margin: "8px 0 4px", width: "100%" }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: C.textDim, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 8 }}>21 reasons people avoid action — tap any to go deeper</div>
      {PROBLEM_THEORY_GROUPS.map((group, gi) => (
        <div key={gi} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.textDim, letterSpacing: "0.06em", textTransform: "uppercase", padding: "6px 4px 4px", marginBottom: 4, borderTop: gi > 0 ? `1px solid ${C.border}` : "none", paddingTop: gi > 0 ? 10 : 0 }}>{group.principle}</div>
          {group.seeAlso && (
            <div style={{ fontSize: 14, color: C.textDim, fontStyle: "italic", marginBottom: 6, paddingLeft: 4 }}>{group.seeAlso}</div>
          )}
          {group.theories.map(t => <TheoryTile key={t.n} t={t} onSendMessage={onSendMessage} kind={KIND.PROBLEM} />)}
        </div>
      ))}
    </div>
  );
}
 
// ─── SOLUTION THEORY LIST ────────────────────────────────────────

function SolutionTheoryList({ onSendMessage }) {
  return (
    <div style={{ margin: "8px 0 4px", width: "100%" }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: C.textDim, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 8 }}>27 strategies to get unstuck — tap any to go deeper</div>
      {SOLUTION_THEORY_GROUPS.map((group, gi) => (
        <div key={gi} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.textDim, letterSpacing: "0.06em", textTransform: "uppercase", padding: "6px 4px 4px", marginBottom: 4, borderTop: gi > 0 ? `1px solid ${C.border}` : "none", paddingTop: gi > 0 ? 10 : 0 }}>{group.principle}</div>
          {group.theories.map(t => <TheoryTile key={t.n} t={t} onSendMessage={onSendMessage} kind={KIND.SOLUTION} />)}
        </div>
      ))}
    </div>
  );
}

function FAB({ onCheckin, onProcrastinate, onOverwhelmed, onChat, onDecide, onRevise, onNudgeHelp, onMotivate, onPepTalk }) {
  const [open, setOpen] = useState(false);
  const wide = useWideScreen();

  const actions = [
    { icon: "✓",  label: "Check In",        bg: C.accent, color: "white", shadow: `0 4px 16px rgba(42,95,74,0.38)`, border: "none",                  onClick: onCheckin     },
    { icon: "📅", label: "Plan or Revise My Day",   bg: "white",  color: C.text,  shadow: "0 4px 14px rgba(0,0,0,0.13)",   border: `1px solid ${C.border}`, onClick: onRevise      },
    { icon: "?",  label: "How to Use Nudge", bg: C.accent, color: "white", shadow: `0 4px 16px rgba(42,95,74,0.38)`, border: "none",                 onClick: onNudgeHelp   },
  ];

  const handleAction = (fn) => { setOpen(false); fn(); };

  return (
    <>
      {/* Backdrop — rendered at root level, not inside FAB container */}
      {open && <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 18 }} />}

      {/* Above the bottom bar on a phone; in the corner beside the sidebar, where there's none. */}
      <div style={{ position: "absolute", bottom: wide ? 20 : 76, right: 20, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10, zIndex: 19 }}>

        {/* Action buttons — fan upward when open */}
        {open && actions.map((a, i) => (
          <div key={i} onClick={() => handleAction(a.onClick)}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              opacity: open ? 1 : 0,
              transform: open ? "translateY(0) scale(1)" : "translateY(12px) scale(0.9)",
              transition: `opacity 0.15s ${i * 0.04}s ease, transform 0.15s ${i * 0.04}s ease`,
              userSelect: "none",
            }}>
            <div style={{ background: "rgba(20,20,20,0.78)", color: "white", fontSize: 12, fontWeight: 600, padding: "5px 10px", borderRadius: 8, whiteSpace: "nowrap" }}>
              {a.label}
            </div>
            <div style={{ width: 46, height: 46, borderRadius: "50%", background: a.bg, color: a.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: a.icon === "✓" ? 20 : 18, fontWeight: 900, cursor: "pointer", boxShadow: a.shadow, border: a.border, flexShrink: 0 }}>
              {a.icon}
            </div>
          </div>
        ))}

        {/* Main button — hamburger at rest, ✕ when open */}
        <div onClick={() => setOpen(o => !o)}
          style={{ width: 50, height: 50, borderRadius: "50%", background: open ? C.surface2 : C.accent, color: open ? C.text : "white", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: open ? "0 2px 10px rgba(0,0,0,0.12)" : `0 4px 18px rgba(42,95,74,0.38)`, border: open ? `1px solid ${C.border}` : "none", transition: "all 0.2s", userSelect: "none", flexShrink: 0 }}>
          {open
            ? <span style={{ fontSize: 18, lineHeight: 1, fontWeight: 700 }}>✕</span>
            : <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
                <div style={{ width: 18, height: 2, background: "white", borderRadius: 1 }} />
                <div style={{ width: 18, height: 2, background: "white", borderRadius: 1 }} />
                <div style={{ width: 18, height: 2, background: "white", borderRadius: 1 }} />
              </div>
          }
        </div>

      </div>
    </>
  );
}


// ─── STREAK MODAL ────────────────────────────────────────────────────
function StreakModal({ softStreak, onClose }) {
  const msgs = [
    { min: 1,  max: 2,  text: "You showed up. That's how it starts." },
    { min: 3,  max: 6,  text: "Three or more days in. A habit is forming." },
    { min: 7,  max: 13, text: "You're building real momentum." },
    { min: 14, max: 20, text: "Two weeks. This is becoming part of who you are." },
    { min: 21, max: 29, text: "Three weeks. Most people never get here." },
    { min: 30, max: 99, text: "A month or more. This is mastery-level consistency." },
    { min: 100, max: Infinity, text: "This is extraordinary. You've made showing up a identity." },
  ];
  const msg = msgs.find(m => softStreak >= m.min && softStreak <= m.max)?.text || "Keep going.";

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 300, animation: "fdIn 0.2s ease" }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", zIndex: 301, width: "min(340px, calc(100vw - 40px))", background: C.surface, borderRadius: 24, padding: "32px 28px 28px", boxShadow: "0 20px 60px rgba(0,0,0,0.25)", animation: "scaleIn 0.2s ease forwards", textAlign: "center" }}>

        {/* Badge graphic */}
        <div style={{ marginBottom: 20 }}>
          <svg width="100" height="100" viewBox="0 0 100 100" style={{ display: "block", margin: "0 auto" }}>
            {/* Outer glow ring */}
            <circle cx="50" cy="50" r="46" fill="rgba(196,122,26,0.12)" stroke="rgba(196,122,26,0.3)" strokeWidth="1.5" />
            {/* Badge body */}
            <circle cx="50" cy="50" r="38" fill="rgba(196,122,26,0.15)" stroke={C.amber} strokeWidth="2" />
            {/* Flame */}
            <text x="50" y="62" textAnchor="middle" fontSize="34" style={{ userSelect: "none" }}>🔥</text>
          </svg>
        </div>

        <div style={{ fontSize: 26, fontWeight: 800, color: C.text, marginBottom: 8, letterSpacing: "-0.5px" }}>
          {softStreak}-Day Streak
        </div>
        <div style={{ fontSize: 15, color: C.textMid, lineHeight: 1.6, marginBottom: 6 }}>{msg}</div>
        <div style={{ fontSize: 13, color: C.textDim, lineHeight: 1.5, marginBottom: 24 }}>
          A 1-day gap pauses your streak. A 2-day gap resets it. Showing up matters — even briefly.
        </div>

        <button onClick={onClose} style={{ width: "100%", background: C.amber, color: "white", border: "none", borderRadius: C.rsm, padding: "13px 0", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          Keep the streak going 🔥
        </button>
      </div>
    </>
  );
}

// ─── LIFETIME MODAL ───────────────────────────────────────────────────
function LifetimeModal({ lifetimeCount, onClose }) {
  const nextMilestone = (() => {
    const milestones = [10, 20, 30, 40, 50, 100, 150, 200, 250, 300, 400, 500, 750, 1000];
    return milestones.find(m => m > lifetimeCount) || null;
  })();

  const msgs = [
    { min: 1,   max: 9,   text: "You've started. That's good, keep it going!" },
    { min: 10,  max: 24,  text: "Double digits. The habit is taking root." },
    { min: 25,  max: 49,  text: "Quietly building." },
    { min: 50,  max: 99,  text: "Over fifty sessions! You've proven you can show up." },
    { min: 100, max: 199, text: "A hundred sessions! This is part of your life now." },
    { min: 200, max: 499, text: "200+. You're a total pro." },
    { min: 500, max: Infinity, text: "500 sessions. You are consistency personified." },
  ];
  const msg = msgs.find(m => lifetimeCount >= m.min && lifetimeCount <= m.max)?.text || "Keep going.";

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 300, animation: "fdIn 0.2s ease" }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", zIndex: 301, width: "min(340px, calc(100vw - 40px))", background: C.surface, borderRadius: 24, padding: "32px 28px 28px", boxShadow: "0 20px 60px rgba(0,0,0,0.25)", animation: "scaleIn 0.2s ease forwards", textAlign: "center" }}>

        {/* Badge graphic */}
        <div style={{ marginBottom: 20 }}>
          <svg width="100" height="100" viewBox="0 0 100 100" style={{ display: "block", margin: "0 auto" }}>
            {/* Outer ring */}
            <circle cx="50" cy="50" r="46" fill="rgba(42,95,74,0.08)" stroke="rgba(42,95,74,0.25)" strokeWidth="1.5" />
            {/* Hexagon badge body */}
            <polygon points="50,12 84,31 84,69 50,88 16,69 16,31" fill="rgba(42,95,74,0.12)" stroke={C.accent} strokeWidth="2" />
            {/* Star / sparkle */}
            <text x="50" y="62" textAnchor="middle" fontSize="28" style={{ userSelect: "none" }}>✦</text>
          </svg>
        </div>

        <div style={{ fontSize: 26, fontWeight: 800, color: C.text, marginBottom: 8, letterSpacing: "-0.5px" }}>
          {lifetimeCount} Chat Sessions
        </div>
        <div style={{ fontSize: 15, color: C.textMid, lineHeight: 1.6, marginBottom: 6 }}>{msg}</div>

        {nextMilestone && (
          <div style={{ background: C.accentLight, border: `1px solid ${C.accentMid}`, borderRadius: C.rsm, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: C.accent, fontWeight: 600 }}>
            Next milestone: {nextMilestone} sessions — {nextMilestone - lifetimeCount} to go
          </div>
        )}

        <div style={{ fontSize: 13, color: C.textDim, lineHeight: 1.5, marginBottom: 24 }}>
          Every session counts — planning, checking in, getting unstuck. This number only ever goes up.
        </div>

        <button onClick={onClose} style={{ width: "100%", background: C.accent, color: "white", border: "none", borderRadius: C.rsm, padding: "13px 0", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          Keep going ✦
        </button>
      </div>
    </>
  );
}

// ─── STREAK BADGE ────────────────────────────────────────────────────
function StreakBadge({ softStreak, lifetimeCount }) {
  const [showStreak, setShowStreak] = useState(false);
  const [showLifetime, setShowLifetime] = useState(false);

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {softStreak > 0 && (
          <div onClick={() => setShowStreak(true)} style={{ display: "flex", alignItems: "center", gap: 4, background: "rgba(196,122,26,0.12)", borderRadius: 100, padding: "7px 10px", border: `1px solid rgba(196,122,26,0.25)`, cursor: "pointer", userSelect: "none" }}>
            <span style={{ fontSize: 13 }}>🔥</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.amber }}>{softStreak}</span>
          </div>
        )}
        <div onClick={() => setShowLifetime(true)} style={{ display: "flex", alignItems: "center", gap: 4, background: C.surface2, borderRadius: 100, padding: "7px 10px", border: `1px solid ${C.border}`, cursor: "pointer", userSelect: "none" }}>
          <span style={{ fontSize: 11 }}>✦</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: C.textMid }}>{lifetimeCount}</span>
        </div>
      </div>

      {showStreak  && <StreakModal  softStreak={softStreak}     onClose={() => setShowStreak(false)}  />}
      {showLifetime && <LifetimeModal lifetimeCount={lifetimeCount} onClose={() => setShowLifetime(false)} />}
    </>
  );
}

// ─── CHECK-IN CARD ───────────────────────────────────────────────────
function CheckInCard({ checkIn, onEdit, onCheckInNow }) {
  const [editMode, setEditMode] = useState(false);
  const [timeVal, setTimeVal] = useState(checkIn?.time || "");
  const [contextVal, setContextVal] = useState(checkIn?.context || "");

  const isPast = () => {
    if (!checkIn?.time) return false;
    const now = new Date();
    const [hStr, mStr] = checkIn.time.replace(/(am|pm)/i, "").split(":");
    let h = parseInt(hStr), m = parseInt(mStr || 0);
    const isPm = checkIn.time.toLowerCase().includes("pm");
    if (isPm && h !== 12) h += 12;
    if (!isPm && h === 12) h = 0;
    const t = new Date(); t.setHours(h, m, 0, 0);
    return now > t;
  };

  const due = checkIn?.time && isPast();

  const save = () => { onEdit(fmtTimeInput(timeVal), contextVal.trim()); setEditMode(false); };

  if (!checkIn?.time && !editMode) {
    return <div style={{ background: C.accentLight, borderRadius: C.r, border: `1.5px dashed ${C.accent}`, padding: "14px 18px", marginBottom: 12, display: "flex", alignItems: "center", gap: 12, position: "relative" }}>
      <div onClick={() => setEditMode(true)} style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, cursor: "pointer" }}>
        <span style={{ fontSize: 20 }}>🔁</span>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.textMid }}>No check-in scheduled</div>
          <div style={{ fontSize: 12, color: C.textMid, marginTop: 2 }}>Set one in chat or tap to add</div>
        </div>
      </div>
      <HelpTip title="Check-Ins" label="How check-ins work" lines={[
        "A check-in is a commitment to yourself to return to Nudge at a specific time — your accountability moment.",
        "When you plan your day in chat, Nudge will automatically schedule a check-in for you.",
        "At check-in time, tap 'Check in →' to report back on how things went and plan your next move.",
        "Regular check-ins are the heartbeat of Nudge — they keep you honest and on track.",
        "Add one manually now, or start a chat with Nudge to set one for the right time. Either way, make sure to have one set for today!",
      ]} />
    </div>;
  }

  if (editMode) {
  return <div style={{ background: C.surface, borderRadius: C.r, border: `1.5px solid ${C.accentMid}`, padding: "14px 18px", marginBottom: 12, boxShadow: C.shadow }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: C.textDim, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 10 }}>Set next check-in</div>
    <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
      <input type="time" value={timeVal} onChange={e => setTimeVal(e.target.value)} style={{ flex: 1, background: C.bg, border: `1px solid ${C.borderCard}`, borderRadius: C.rsm, padding: "9px 12px", fontFamily: "inherit", fontSize: 15, color: C.text, outline: "none" }} />
      <button onClick={save} style={{ background: C.accent, color: "white", border: "none", borderRadius: C.rsm, padding: "9px 16px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Set</button>
      <button onClick={() => { onEdit("", ""); setEditMode(false); }} style={{ background: "transparent", border: `1px solid ${C.red}`, borderRadius: C.rsm, padding: "8px 10px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }} title="Delete check-in">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.red} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6"/>
          <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
          <path d="M10 11v6M14 11v6"/>
          <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
        </svg>
      </button>
      <button onClick={() => setEditMode(false)} style={{ background: C.surface2, color: C.textMid, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "9px 12px", fontSize: 14, cursor: "pointer" }}>Cancel</button>
    </div>
    <input
      value={contextVal}
      onChange={e => setContextVal(e.target.value)}
      placeholder="What's this check-in for? (optional)"
      style={{ width: "100%", background: C.bg, border: `1px solid ${C.borderCard}`, borderRadius: C.rsm, padding: "9px 12px", fontFamily: "inherit", fontSize: 14, color: C.text, outline: "none", boxSizing: "border-box", fontStyle: contextVal ? "normal" : "italic" }}
    />
  </div>;
}

  return <div style={{ background: C.accentLight, borderRadius: C.r, border: `${due ? "2px" : "1.5px"} solid ${C.accent}`, padding: "14px 18px", marginBottom: 12, boxShadow: C.shadow, transition: "all 0.3s" }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontSize: 20 }}>{due ? "👀" : "🔁"}</span>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: due ? C.accent : C.textDim, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 3 }}>{due ? "Ready for your check-in" : "Next check-in"}</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{checkIn.time}</div>
          {checkIn.context && <div style={{ fontSize: 16, color: C.text, fontWeight: 500, marginTop: 3 }}>{checkIn.context}</div>}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
        {due && <button onClick={onCheckInNow} style={{ background: C.accent, color: "white", border: "none", borderRadius: C.rsm, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>Check in →</button>}
        <div onClick={() => setEditMode(true)} style={{ fontSize: 12, color: C.textDim, cursor: "pointer", textDecoration: "underline" }}>{due ? "reschedule" : "edit"}</div>
      </div>
    </div>
  </div>;
}

// ─── TODAY DEADLINES CARD ────────────────────────────────────────────
 

// ─── TODAY PAGE ──────────────────────────────────────────────────────
// ─── UNIFIED TASK EDIT MODAL ─────────────────────────────────────────
// Handles two contexts:
//   Compass/Top10: pass { task, uid, compass, onClose }
//   Today anchor:  pass { anchor, anchorIndex, phaseId, uid, todayData, compass, onClose }
// Phase 3 will collapse these into one once Today anchors become Task objects.
 
function TextRow({ label, value, onChange, placeholder, italicPlaceholder, plain }) {
  const cls = italicPlaceholder ? "textrow-italic-placeholder" : "";
  return (
    <div style={ plain
      ? { padding: "12px 0", borderTop: `1px solid ${C.border}` }
      : { background: C.accentLight, border: `1px solid ${C.accentMid}`, borderRadius: C.rsm, padding: "12px 14px", marginTop: 10 }
    }>
      {italicPlaceholder && <style>{`.textrow-italic-placeholder::placeholder { font-style: italic; }`}</style>}
      <div style={{ fontSize: 11, fontWeight: 700, color: plain ? C.textDim : C.accent, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: plain ? 5 : 6 }}>{label}</div>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        rows={2}
        className={cls}
        style={{ width: "100%", background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "8px 12px", fontFamily: "inherit", fontSize: 15, color: C.text, outline: "none", resize: "vertical", boxSizing: "border-box", lineHeight: 1.5 }}
      />
    </div>
  );
}

function PillRow({ label, options, value, onChange }) {
  return (
    <div style={{ padding: "12px 0", borderTop: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.textDim, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {options.map(opt => {
          const isSelected = value === opt.value;
          return (
            <div key={opt.value ?? "none"} onClick={() => onChange(isSelected ? null : opt.value)}
              style={{ padding: "5px 14px", borderRadius: 100, cursor: "pointer", fontSize: 13, fontWeight: isSelected ? 700 : 600,
                background: isSelected ? (opt.bg || C.accentLight) : C.surface2,
                color: isSelected ? (opt.color || C.accent) : C.textDim,
                border: `1.5px solid ${isSelected ? (opt.color || C.accent) : C.border}`,
                transition: "all 0.15s" }}>
              {opt.label}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TaskEditModal({ task, anchor, anchorIndex, phaseId, uid, todayData, compass, tasks, goals = [], newTaskDefaults, onClose, settings }) {
  // Normalise — work from a single source object regardless of context
  const isAnchorMode = !!anchor;
  const isNewTask = !task && !anchor && !!newTaskDefaults;
  const src = isAnchorMode ? anchor : (task || newTaskDefaults || {});
 
  const [text,               setText]               = useState(src.text               || "");
  const [deadline,           setDeadline]           = useState(src.deadline           || "");
  const [categoryId,         setCategoryId]         = useState(src.categoryId         || null);
  const [tier,               setTier]               = useState(src.tier               || null);
  const [urgency,            setUrgency]            = useState(src.urgency            || null);
  const [commitment,         setCommitment]         = useState(src.commitment         || null);
  const [emotion,            setEmotionVal]         = useState(src.emotion            || null);
  const [microStep,          setMicroStep]          = useState(src.microStep          || "");
  const [enjoyNote,          setEnjoyNote]          = useState(src.enjoyNote          || "");
  const [commitmentStatement,setCommitmentStatement]= useState(src.commitmentStatement|| "");
  const [emotionalStakes,    setEmotionalStakes]    = useState(src.emotionalStakes    || "");
  const [obstacle,           setObstacle]           = useState(src.obstacle           || "");
  const [notes,              setNotes]              = useState(src.notes              || "");
  const [todayTime, setTodayTime] = useState(() => {
    const raw = src.todayTime || "";
    if (!raw) return "";
    // Convert "5:30pm" / "17:30" → "HH:MM" for time input
    const m = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i);
    if (!m) return "";
    let h = parseInt(m[1]), min = parseInt(m[2] || "0");
    const ampm = (m[3] || "").toLowerCase();
    if (ampm === "pm" && h !== 12) h += 12;
    if (ampm === "am" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
  });
  const [duration,           setDuration]           = useState(src.duration           || "");
  const [parentGoalId,       setParentGoalId]       = useState(src.parentGoalId       || null);
  const [chosenKeyDateId,    setKeyDateId]          = useState(src.keyDateId          || null);
  const keyDateOptions = keyDateChoices(useKeyDatesList() || [], chosenKeyDateId);
  // A link to a Key Date that no longer exists counts as none, so Save never writes it back.
  const linkedKeyDate = keyDateOptions.find(o => o.id === chosenKeyDateId)?.keyDate || null;
  const keyDateId = linkedKeyDate?.id || null;
  // The dream and fear it's for (taskLinks.js), chosen from NudgeApp's lists.
  const [chosenDreamId,      setDreamId]            = useState(src.dreamId            || null);
  const [chosenFearId,       setFearId]             = useState(src.fearId             || null);
  const linkChoices = useTaskLinkChoices(compass?.categories);
  const dreamId = keptLink(linkChoices.dreams.options, linkChoices.dreams.loaded, chosenDreamId);
  const fearId = keptLink(linkChoices.fears.options, linkChoices.fears.loaded, chosenFearId);
  const [localDone,          setLocalDone]          = useState(src.done               || false);

  // Auto-inherit category from parent goal whenever goal link is set or changed
  useEffect(() => {
    if (!parentGoalId) return;
    const parentGoal = (goals || []).find(g => g.id === parentGoalId);
    if (parentGoal?.categoryId) setCategoryId(parentGoal.categoryId);
  }, [parentGoalId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [todayPhase,         setTodayPhase]         = useState(src.todayPhase         || newTaskDefaults?.todayPhase || null);
  const [showBubbleOverlay,  setShowBubbleOverlay]  = useState(false);
  const [saving,             setSaving]             = useState(false);
 
  const categories = compass?.categories || [];
 
  const tierOptions = [
    { key: "shortterm", label: "Short-term" },
    { key: "medterm",   label: "Medium-term" },
    { key: "longterm",  label: "Long-term"   },
  ];
 
  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    // Category always follows the parent goal when one is linked
    const effectiveCategoryId = parentGoalId
      ? ((goals || []).find(g => g.id === parentGoalId)?.categoryId ?? categoryId ?? null)
      : (categoryId || null);
    if (isNewTask) {
      await createTask(uid, {
        ...newTaskDefaults,
        text: text.trim(),
        deadline: deadline || null,
        categoryId: effectiveCategoryId,
        tier: tier || newTaskDefaults?.tier || null,
        todayPhase: todayPhase || null,
        urgency,
        commitment,
        emotion,
        microStep:           microStep || null,
        enjoyNote:           enjoyNote || null,
        commitmentStatement: commitmentStatement || null,
        emotionalStakes:     emotionalStakes || null,
        obstacle:            obstacle || null,
        notes:               notes || null,
        parentGoalId:        parentGoalId || null,
        keyDateId:           keyDateId || null,
        dreamId,
        fearId,
      });
      setSaving(false);
      onClose(true);
      return;
    }
    if (isAnchorMode) {
      // Write fields back to today day doc anchor
      const anchors = [...(todayData[phaseId]?.anchors || [])];
      anchors[anchorIndex] = {
        ...anchors[anchorIndex],
        text:                text.trim(),
        time:                todayTime || null,
        duration:            duration ? Number(duration) || null : null,
        urgency,
        commitment,
        emotion,
        microStep:           microStep || null,
        enjoyNote:           enjoyNote || null,
        commitmentStatement: commitmentStatement || null,
        emotionalStakes:     emotionalStakes || null,
        obstacle:            obstacle || null,
        notes:               notes || null,
      };
      await setDoc(
        doc(db, "users", uid, "days", todayKey()),
        { [phaseId]: { anchors }, planned: true },
        { merge: true }
      ).catch(console.error);
    } else {
      // Write to unified task object
      // Auto-assign todayPhase if a start time is set but no phase exists yet
      let autoPhase = task?.todayPhase || null;
      if (todayTime && !autoPhase) {
        autoPhase = phaseNow(settings);
      }
      await updateTask(uid, task.id, {
        text:                text.trim(),
        deadline:            deadline || null,
        categoryId:          effectiveCategoryId,
        tier:                tier || null,
        urgency,
        commitment,
        emotion,
        microStep:           microStep || null,
        enjoyNote:           enjoyNote || null,
        commitmentStatement: commitmentStatement || null,
        emotionalStakes:     emotionalStakes || null,
        obstacle:            obstacle || null,
        notes:               notes || null,
        todayPhase:          autoPhase,
        todayTime:           todayTime || null,
        duration:            duration ? Number(duration) || null : null,
        parentGoalId:        parentGoalId || null,
        keyDateId:           keyDateId || null,
        dreamId,
        fearId,
      });
      if (autoPhase) {
        setDoc(doc(db, "users", uid, "days", todayKey()), { planned: true }, { merge: true }).catch(console.error);
      }
    }
    setSaving(false);
    onClose(true);
  };

  const recheckPlanned = async (updatedTodayData) => {
    const hasAnchors = ["morning","afternoon","evening"].some(
      phase => (updatedTodayData[phase]?.anchors || []).length > 0
    );
    const hasTasks = (tasks || []).some(t =>
      t.todayPhase && t.id !== task?.id
    );
    if (!hasAnchors && !hasTasks) {
      await setDoc(
        doc(db, "users", uid, "days", todayKey()),
        { planned: false },
        { merge: true }
      ).catch(console.error);
    }
  };

  const deleteItem = async () => {
    if (isAnchorMode) {
      const anchors = (todayData[phaseId]?.anchors || []).filter((_, i) => i !== anchorIndex);
      const updatedTodayData = { ...todayData, [phaseId]: { anchors } };
      await setDoc(
        doc(db, "users", uid, "days", todayKey()),
        { [phaseId]: { anchors } },
        { merge: true }
      ).catch(console.error);
      await recheckPlanned(updatedTodayData);
    } else {
      await deleteTask(uid, task.id);
    }
    onClose();
  };
 
  const moveToPhase = async (targetPhaseId) => {
    if (isNewTask) {
      // For new tasks, just update local todayPhase state — saved on create
      setTodayPhase(prev => prev === targetPhaseId ? null : targetPhaseId);
      return;
    }
    if (isAnchorMode) {
      if (targetPhaseId === phaseId) return;
      const currentAnchors = (todayData[phaseId]?.anchors || []).filter((_, i) => i !== anchorIndex);
      const targetAnchors  = [...(todayData[targetPhaseId]?.anchors || []), anchor];
      await setDoc(
        doc(db, "users", uid, "days", todayKey()),
        { [phaseId]: { anchors: currentAnchors }, [targetPhaseId]: { anchors: targetAnchors } },
        { merge: true }
      ).catch(console.error);
    } else {
      await updateTask(uid, task.id, { todayPhase: targetPhaseId });
    }
    onClose();
  };
 
  // Reusable text area row
  const emotionOptions = [
    { value: "happy",    label: "Happy",    color: C.accent, bg: C.accentLight },
    { value: "neutral",  label: "Neutral",  color: C.amber,  bg: C.amberLight  },
    { value: "dreading", label: "Dreading", color: C.red,    bg: C.redLight    },
  ];
 
  const urgencyOptions = [
    { value: "low",    label: "Low",    color: URGENCY.low.color,    bg: URGENCY.low.bg    },
    { value: "medium", label: "Medium", color: URGENCY.medium.color, bg: URGENCY.medium.bg },
    { value: "high",   label: "High",   color: URGENCY.high.color,   bg: URGENCY.high.bg   },
    { value: "urgent", label: "Urgent", color: URGENCY.urgent.color, bg: URGENCY.urgent.bg },
  ];
 
  const commitmentOptions = [1,2,3,4,5,6,7,8,9,10].map(n => ({
    value: n, label: `${n}`,
    color: n >= 8 ? C.accent : n >= 5 ? C.amber : C.red,
    bg:    n >= 8 ? C.accentLight : n >= 5 ? C.amberLight : C.redLight,
  }));
 
  const handleBubbleSave = async (urgency, importance, sliderValue) => {
    if (!task?.id || isNewTask) return;
    const newPosition = 12 - sliderValue;
    const others = (tasks || [])
      .filter(t => t.bubbleSortOrder != null && t.id !== task.id && !t.done)
      .sort((a, b) => a.bubbleSortOrder - b.bubbleSortOrder);
    const insertAt = Math.min(Math.max(0, newPosition - 1), others.length);
    others.splice(insertAt, 0, task);
    const batch = writeBatch(db);
    others.forEach((t, i) => {
      batch.set(doc(db, "users", uid, "tasks", t.id),
        { bubbleSortOrder: i + 1, updatedAt: serverTimestamp() }, { merge: true });
    });
    batch.set(doc(db, "users", uid, "tasks", task.id),
      { bubbleUrgency: urgency, bubbleImportance: importance,
        bubbleSortOrder: insertAt + 1, updatedAt: serverTimestamp() },
      { merge: true }
    );
    await batch.commit().catch(console.error);
    setShowBubbleOverlay(false);
  };

  return ReactDOM.createPortal(
    <>
      {/* Bubble Up slider overlay — rendered fixed over the modal */}
      {showBubbleOverlay && !isNewTask && (
        <BubbleSliderOverlay
          item={task}
          compass={compass}
          initialUrgency={task.bubbleUrgency ?? 1}
          initialImportance={task.bubbleImportance ?? 1}
          initialSlider={12 - Math.min(11, Math.max(1, task.bubbleSortOrder ?? 11))}
          onSave={handleBubbleSave}
          fixed={true}
        />
      )}

      {/* Backdrop */}
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 500, animation: "fdIn 0.2s ease" }} />
 
      {/* Bottom sheet */}
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 501, maxWidth: PAGE_WIDTH, margin: "0 auto",
        background: C.surface, borderRadius: "22px 22px 0 0",
        boxShadow: "0 -4px 30px rgba(0,0,0,0.15)",
        padding: "0 20px 40px",
        maxHeight: "88vh", overflowY: "auto",
        animation: "slideUp 0.25s cubic-bezier(0.32,0.72,0,1)"
      }}>
        {/* Handle */}
        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: C.border }} />
        </div>
 
        {/* Header — done checkbox + task name + save + close */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 0 16px" }}>
          {/* A real button: since 21 Oct 2026 it is how a finished task, opened from Tasks → Completed, is marked not done. */}
          <button onClick={() => { const next = !localDone; setLocalDone(next); updateTask(uid, task?.id, { done: next }); }}
            disabled={isNewTask} aria-pressed={localDone} aria-label="Done"
            style={{ width: 44, height: 44, flexShrink: 0, margin: "-4px -9px 0", padding: 0, background: "none", border: "none",
              display: "flex", alignItems: "center", justifyContent: "center", cursor: isNewTask ? "default" : "pointer" }}>
            <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: 7,
              background: localDone ? C.accent : "transparent",
              border: `2px solid ${localDone ? C.accent : C.border}`,
              display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s",
              opacity: isNewTask ? 0.3 : 1, color: C.onAccent, fontSize: 14, fontWeight: 700 }}>
              {localDone && "✓"}
            </span>
          </button>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Enter task name"
            rows={2}
            style={{ flex: 1, background: "transparent", border: "none", borderRadius: 0, padding: "6px 0", fontFamily: "inherit", fontSize: 19, fontWeight: 700,
              color: localDone ? C.textDim : C.text,
              textDecoration: localDone ? "line-through" : "none",
              outline: "none", resize: "none", lineHeight: 1.3 }}
          />
          <button onClick={save} disabled={saving || !text.trim()}
            style={{ background: C.accent, color: "white", border: "none", borderRadius: C.rsm, padding: "6px 12px", fontSize: 13, fontWeight: 700, cursor: saving ? "default" : "pointer", flexShrink: 0, opacity: !text.trim() ? 0.5 : 1, marginTop: 2 }}>
            {saving ? "..." : "Save"}
          </button>
          {!isNewTask && (
            <button onClick={() => setShowBubbleOverlay(true)}
              title="Prioritize — set priority"
              style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: "50%", width: 32, height: 32, fontSize: 15, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
              🔥
            </button>
          )}
          <button onClick={onClose} style={{ background: C.surface2, border: "none", borderRadius: "50%", width: 32, height: 32, fontSize: 16, cursor: "pointer", color: C.textMid, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>✕</button>
        </div>
 
        {/* ── COMPASS FIELDS (task mode only) ── */}
        {!isAnchorMode && (
          <>
            {/* Goal — first to encourage linking */}
            {goalChoices(goals, src.parentGoalId).length > 0 && (
              <TaskLinkField id="task-goal" label="Goal" none="No goal" value={parentGoalId} onChange={setParentGoalId}
                options={goalChoices(goals, src.parentGoalId).map(g => ({ id: g.id, label: g.title }))} />
            )}

            {/* Key date — shown once the user has one; a task added from a Key Date's card arrives set */}
            {keyDateOptions.length > 0 && (
              <TaskLinkField id="task-key-date" label="Key date" none="No key date" options={keyDateOptions} value={keyDateId} onChange={setKeyDateId}>
                {linkedKeyDate && (
                  <div style={{ fontSize: 13, color: C.textMid, marginTop: 6, lineHeight: 1.4 }}>
                    {keyDateTaskHint(linkedKeyDate)}
                  </div>
                )}
              </TaskLinkField>
            )}

            {/* Dream and fear — each shown once the user has one; a task added from Radar's dream or fear arrives set */}
            {linkChoices.dreams.options.length > 0 && (
              <TaskLinkField id="task-dream" label="Dream" none="No dream" options={linkChoices.dreams.options} value={dreamId} onChange={setDreamId} />
            )}
            {linkChoices.fears.options.length > 0 && (
              <TaskLinkField id="task-fear" label="Fear" none="No fear" options={linkChoices.fears.options} value={fearId} onChange={setFearId} />
            )}

            {/* Category */}
            <div style={{ padding: "12px 0", borderTop: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textDim, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 8 }}>Category</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <div onClick={() => setCategoryId(null)}
                  style={{ padding: "4px 12px", borderRadius: 100, cursor: "pointer", fontSize: 12, fontWeight: !categoryId ? 700 : 500,
                    background: !categoryId ? C.surface2 : C.surface2,
                    color: !categoryId ? C.textMid : C.textDim,
                    border: `1.5px solid ${!categoryId ? C.textMid : C.border}` }}>
                  📥 Uncategorized
                </div>
                {categories.map(cat => {
                  const active = categoryId === cat.id;
                  return (
                    <div key={cat.id} onClick={() => setCategoryId(cat.id)}
                      style={{ padding: "4px 12px", borderRadius: 100, cursor: "pointer", fontSize: 12, fontWeight: active ? 700 : 500,
                        background: active ? C.accent : C.surface2,
                        color: active ? "white" : C.textMid,
                        border: `1.5px solid ${active ? C.accent : C.border}` }}>
                      {cat.icon} {cat.label}
                    </div>
                  );
                })}
              </div>
            </div>
 
            {/* Tier */}
            <PillRow
              label="Time Horizon"
              options={tierOptions.map(t => ({ value: t.key, label: t.label, color: C.accent, bg: C.accentLight }))}
              value={tier}
              onChange={setTier}
            />
 
            {/* Deadline */}
            <div style={{ padding: "12px 0", borderTop: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textDim, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 8 }}>Deadline</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input type="date" value={deadline} onChange={e => setDeadline(e.target.value)}
                  style={{ width: "50%", background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "7px 12px", fontFamily: "inherit", fontSize: 14, color: C.text, outline: "none", boxSizing: "border-box" }} />
                {deadline && (
                  <span onClick={() => setDeadline("")} style={{ fontSize: 12, color: C.red, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>✕ Clear</span>
                )}
              </div>
            </div>
          </>
        )}
 
        {/* ── TODAY SCHEDULING FIELDS — always shown ── */}
        <>
          {/* Start time */}
          <div style={{ padding: "12px 0", borderTop: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.textDim, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 6 }}>Start Time</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input type="time" value={todayTime} onChange={e => setTodayTime(e.target.value)}
                style={{ width: "50%", background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "7px 10px", fontFamily: "inherit", fontSize: 14, color: C.text, outline: "none", boxSizing: "border-box" }} />
              {todayTime && <span onClick={() => setTodayTime("")} style={{ fontSize: 12, color: C.red, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>✕ Clear</span>}
            </div>
          </div>
 
          {/* Duration */}
          <div style={{ padding: "12px 0", borderTop: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.textDim, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 6 }}>Duration (min)</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input type="number" value={duration} onChange={e => setDuration(e.target.value)} placeholder="e.g. 30"
                style={{ width: "50%", background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "7px 10px", fontFamily: "inherit", fontSize: 14, color: C.text, outline: "none", boxSizing: "border-box" }} />
              {duration && <span onClick={() => setDuration("")} style={{ fontSize: 12, color: C.red, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>✕ Clear</span>}
            </div>
          </div>
 
          {/* Move to phase */}
          <div style={{ padding: "12px 0", borderTop: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.textDim, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 8 }}>Move to</div>
            <div style={{ display: "flex", gap: 8 }}>
              {[{ id: "morning", label: "🌅 Morning" }, { id: "afternoon", label: "☀️ Afternoon" }, { id: "evening", label: "🌙 Evening" }].map(ph => {
                const currentPhase = isAnchorMode ? phaseId : (isNewTask ? todayPhase : task?.todayPhase);
                return (
                  <div key={ph.id} onClick={() => moveToPhase(ph.id)}
                    style={{ padding: "5px 14px", borderRadius: 100, cursor: "pointer", fontSize: 13, fontWeight: 600,
                      background: ph.id === currentPhase ? C.accentLight : C.surface2,
                      color: ph.id === currentPhase ? C.accent : C.textMid,
                      border: `1.5px solid ${ph.id === currentPhase ? C.accent : C.border}`,
                      transition: "all 0.15s" }}>
                    {ph.label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Remove from Today — only shown when task has a phase assigned */}
          {task?.todayPhase && (
            <div style={{ paddingTop: 16 }}>
              <button onClick={async () => {
                await updateTask(uid, task.id, { todayPhase: null, todayTime: null });
                const remainingTasks = (tasks||[]).some(t => t.todayPhase && t.id !== task.id);
                if (!remainingTasks) {
                  setDoc(doc(db, "users", uid, "days", todayKey()), { planned: false }, { merge: true }).catch(console.error);
                }
                onClose();
              }}
                style={{ width: "100%", background: C.redLight, color: C.red, border: `1px solid ${C.red}`, borderRadius: C.rsm, padding: "11px 0", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                Remove from Today's Schedule
              </button>
            </div>
          )}
        </>
 
        {/* ── SHARED FIELDS ── */}
        <PillRow label="How are you feeling about this?" options={emotionOptions} value={emotion} onChange={setEmotionVal} />
        <PillRow label="Urgency" options={urgencyOptions} value={urgency} onChange={setUrgency} />
        <PillRow label="Commitment Level" options={commitmentOptions} value={commitment} onChange={setCommitment} />
 
        {/* First step */}
        <div style={{ background: C.accentLight, border: `1px solid ${C.accentMid}`, borderRadius: C.rsm, padding: "12px 14px", marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 6 }}>First Step</div>
          <textarea value={microStep} onChange={e => setMicroStep(e.target.value)} placeholder="Ridiculously small first action..." rows={2}
            style={{ width: "100%", background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "8px 12px", fontFamily: "inherit", fontSize: 15, color: C.text, outline: "none", resize: "vertical", boxSizing: "border-box", lineHeight: 1.5 }} />
        </div>
 
        <TextRow label="How will you make it more enjoyable?" value={enjoyNote} onChange={setEnjoyNote} placeholder="Watch TV? Listen to music or a podcast? Maybe AI can help? Maybe partner with a friend or family member?" italicPlaceholder />
        <TextRow label="Why does this matter?" value={commitmentStatement} onChange={setCommitmentStatement} placeholder="How is this going to improve my life?" italicPlaceholder />
        <TextRow label="How will you feel after getting it done?" value={emotionalStakes} onChange={setEmotionalStakes} placeholder="How will you feel?" italicPlaceholder />
        <TextRow label="What might tempt you away from this?" value={obstacle} onChange={setObstacle} placeholder="Name it. What might distract you from this?" italicPlaceholder />
        <TextRow label="Notes" value={notes} onChange={setNotes} placeholder="Add notes here..." italicPlaceholder plain />

        {/* Save + Delete */}
        <div style={{ paddingTop: 12, paddingBottom: 4, display: "flex", gap: 10 }}>
          <button onClick={save} disabled={saving || !text.trim()}
            style={{ flex: 2, background: saving ? C.surface2 : C.accent, color: saving ? C.textDim : "white", border: "none", borderRadius: C.rsm, padding: "13px 0", fontSize: 15, fontWeight: 700, cursor: saving ? "default" : "pointer", fontFamily: "inherit" }}>
            {saving ? "Saving..." : "Save"}
          </button>
          <button onClick={deleteItem}
            style={{ flex: 1, background: C.red, color: "white", border: `1px solid ${C.red}`, borderRadius: C.rsm, padding: "13px 0", fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            Delete
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}
 
// TaskRow replaces AnchorRow — reads from unified Task object, writes via updateTask
function TaskRow({ task, uid, compass, tasks, onEdit }) {

  const urgencyBorder = task.urgency ? URGENCY[task.urgency]?.color : "transparent";
  const showColorBorder = task.urgency && task.urgency !== "low";

  return <div style={{
    border: showColorBorder ? `2px solid ${urgencyBorder}` : `1px solid ${C.border}`,
    borderRadius: C.rsm,
    margin: "4px 4px",
    transition: "all 0.2s"
  }}>
    {/* Main row — checkbox, task info, commitment badge */}
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 6px" }}>
      <DoneCheck text={task.text} done={!!task.done} style={{ margin: "-8px -11px" }}
        onDone={() => writeTask(uid, task.id, { done: true })} onUndo={() => writeTask(uid, task.id, { done: false })} />
      <div
        onClick={() => onEdit(task.id)}
        style={{ flex: 1, cursor: "pointer" }}>
        <div style={{
          fontSize: 17, fontWeight: 500,
          color: task.done ? C.textDim : C.text,
          textDecoration: task.done ? "line-through" : "none",
          lineHeight: 1.4,
          paddingBottom: 2
        }}>{task.text}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2, marginBottom: 2 }}>
          {task.todayTime && <span style={{ fontSize: 15, color: task.done ? C.textDim : C.textGreen, fontWeight: 500, lineHeight: 1 }}>⏱ {fmtTimeInput(task.todayTime)}</span>}
          {task.duration && <span style={{ fontSize: 15, color: task.done ? C.textDim : C.textGreen, fontWeight: 500, fontStyle: "italic", lineHeight: 1 }}>({task.duration} min)</span>}
        </div>
      </div>
      {!task.done && (
        <div onClick={() => onEdit(task.id)} style={{ flexShrink: 0, lineHeight: 1, cursor: "pointer", marginLeft: "auto", paddingRight: 3, marginTop: 1, display: "flex", alignItems: "center", gap: 3 }}>
          {task.commitment == null
            ? <span style={{ fontSize: 22, filter: "grayscale(1)", opacity: 0.4 }}>❓</span>
            : task.commitment >= 9
              ? <>
                  <span style={{ fontSize: 22 }}>🎯</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.textGreen }}>{task.commitment}/10</span>
                </>
              : task.commitment >= 7
              ? <>
                  <span style={{ fontSize: 22 }}>👍</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.textGreen }}>{task.commitment}/10</span>
                </>
              : <>
                  <span style={{ fontSize: 22 }}>⚠️</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.amber }}>{task.commitment}/10</span>
                </>
          }
        </div>
      )}
    </div>
    {/* Motivators — shown when not done */}
    {!task.done && (
      <div onClick={() => onEdit(task.id)} style={{ padding: "0 6px 8px 38px", cursor: "pointer" }}>
        <div style={{ fontSize: 14, color: task.microStep ? C.text : C.textMid, marginTop: 4, lineHeight: 1.5, fontStyle: task.microStep ? "normal" : "italic" }}>
          <span style={{ color: C.textGreen, fontWeight: 700, fontStyle: "normal" }}>First step → </span>
          {task.microStep || "(Make the first step laughably easy)"}
        </div>
        {task.obstacle && (
          <div style={{ fontSize: 14, color: C.text, marginTop: 4, lineHeight: 1.5 }}>
            <span style={{ color: C.textGreen, fontWeight: 700 }}>Watch out for → </span>
            {task.obstacle}
          </div>
        )}
        <div style={{ fontSize: 14, color: task.enjoyNote ? C.text : C.textMid, marginTop: 4, lineHeight: 1.5, fontStyle: task.enjoyNote ? "normal" : "italic" }}>
          <span style={{ color: C.textGreen, fontWeight: 700, fontStyle: "normal" }}>Make it enjoyable → </span>
          {task.enjoyNote || "(Listen to music, podcast, or TV?)"}
        </div>
        <div style={{ fontSize: 14, color: task.commitmentStatement ? C.text : C.textMid, marginTop: 4, lineHeight: 1.5, fontStyle: task.commitmentStatement ? "normal" : "italic" }}>
          <span style={{ color: C.textGreen, fontWeight: 700, fontStyle: "normal" }}>Why it matters → </span>
          {task.commitmentStatement || "(Why does this task matter?)"}
        </div>
        <div style={{ fontSize: 14, color: task.emotionalStakes ? C.text : C.textMid, marginTop: 4, lineHeight: 1.5, fontStyle: task.emotionalStakes ? "normal" : "italic" }}>
          <span style={{ color: C.textGreen, fontWeight: 700, fontStyle: "normal" }}>How you'll feel → </span>
          {task.emotionalStakes || "(How will you feel when it's done?)"}
        </div>
      </div>
    )}
    </div>;
}

// ─── DAILY REMINDERS BLOCK ───────────────────────────────────────────
const DAILY_REMINDER_CATEGORIES = [
  // `key` is where today's choice is saved on the day; `kind` is the COMPASS_KINDS list it chooses from.
  { key: "mindset",     kind: "mindset",     label: "Mindset Reminders",    icon: "👍", emptyPrompt: "Choose up to 3 mindset reminders" },
  { key: "habits",      kind: "habits",      label: "Habits to focus on",   icon: "🔁", emptyPrompt: "Choose up to 3 habits to focus on today" },
  { key: "gratitude",   kind: "gratitude",   label: "Gratitude Reminders",  icon: "🙏", emptyPrompt: "Choose up to 3 gratitude reminders" },
  { key: "temptations", kind: "temptations", label: "Temptations to avoid", icon: "🪤", emptyPrompt: "Choose up to 3 temptations to avoid today" },
];

const MAX_REMINDERS = 3;

// onNav(screen) opens the Mindset or Habits page, where the choices come from.
function DailyReminders({ todayData, compass, uid, onNav }) {
  const [editing, setEditing] = useState(false);
  const [localData, setLocalData] = useState({
    mindset:     todayData.mindset     || [],
    gratitude:   todayData.gratitude   || [],
    habits:      todayData.habits      || [],
    temptations: todayData.temptations || [],
  });

  useEffect(() => {
    setLocalData({
      mindset:     todayData.mindset     || [],
      gratitude:   todayData.gratitude   || [],
      habits:      todayData.habits      || [],
      temptations: todayData.temptations || [],
    });
  }, [todayData]);

  const saveToFirestore = async (updated) => {
    if (uid) {
      await setDoc(
        doc(db, "users", uid, "days", todayKey()),
        {
          mindset:     updated.mindset     || [],
          gratitude:   updated.gratitude   || [],
          habits:      updated.habits      || [],
          temptations: updated.temptations || [],
        },
        { merge: true }
      ).catch(console.error);
    }
  };

  const addItem = async (key, value) => {
    const current = localData[key] || [];
    if (current.includes(value) || current.length >= MAX_REMINDERS) return;
    const updated = { ...localData, [key]: [...current, value] };
    setLocalData(updated);
    await saveToFirestore(updated);
  };

  const removeItem = async (key, value) => {
    const updated = { ...localData, [key]: (localData[key] || []).filter(v => v !== value) };
    setLocalData(updated);
    await saveToFirestore(updated);
  };

  const hasAny = Object.values(localData).some(arr => arr.length > 0);

  // Folded or open for today only (remindersOpen). A failed save puts the card back the way it was.
  const [open, setOpen] = useState(() => remindersOpen(todayData));
  useEffect(() => { setOpen(remindersOpen(todayData)); }, [todayData]);
  const toggleFold = () => {
    const next = !open;
    setOpen(next);
    if (!next) setEditing(false);
    if (uid) setRemindersFolded(uid, !next).catch(e => { console.error("Saving the fold failed:", e); setOpen(!next); });
  };

  return (
    // The same card and fold as Today's Inspiration (Craig, 24 Oct 2026), open each morning ("expanded by default, but
    // remember the user's last state each day"), so the choice is kept on today's day document (dayService.js).
    <FoldCard id="todays-reminders" icon="📌" title="Today's Reminders" line="Things to keep in mind today" open={open} onToggle={toggleFold} help={
        <HelpTip title="Today's Reminders" lines={[
          "Choose a few mindset reminders, habits, gratitude and temptations to avoid, and they show here all day.",
          "Tap below the heading to choose what shows today.",
          "Tap the heading to fold it away. It stays folded until tomorrow unless you open it again.",
          "You add your own on the Mindset, Habits, Gratitude and Temptations pages, which you can open from Home.",
          "Your choices are cleared each day, so you can choose a fresh set.",
        ]} />
      }>
      <div onClick={() => { if (!editing) setEditing(true); }}>
      {!editing ? (
        // ── Collapsed / display state ── (a button for the keyboard too; tapping it chooses today's reminders)
        <div role="button" tabIndex={0} aria-label="Choose today's reminders"
          onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setEditing(true); } }}
          style={{ background: C.foldCard, border: `1px solid ${C.borderCard}`, borderRadius: C.r, padding: "16px 16px", boxShadow: C.shadow, cursor: "pointer" }}>
          {!hasAny ? (
            <div style={{ textAlign: "center", padding: "10px 0" }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: C.textMid, marginBottom: 17 }}>No reminders set for today</div>
              <div style={{ fontSize: 13, color: C.textDim, fontStyle: "italic" }}>Tap to choose mindset reminders, habits and more.</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {DAILY_REMINDER_CATEGORIES.map(cat => {
                const values = localData[cat.key] || [];
                if (values.length === 0) return null;
                const isHabit = cat.key === "habits";
                const isTrap = cat.key === "temptations";
                // Each line starts with its list's icon, so a temptation never reads as an affirmation; colour only adds to it.
                return values.map((value, vi) => (
                  <div key={`${cat.key}-${vi}`} style={{ textAlign: "center" }}>
                    <span role="img" aria-label={cat.label} style={{ fontStyle: "normal", marginRight: 6 }}>{cat.icon}</span>
                    <span style={{
                      fontFamily: "Georgia, serif",
                      fontStyle: "italic",
                      fontSize: 16,
                      color: isTrap ? C.amber : C.text,
                      lineHeight: 1.7,
                    }}>
                      {isHabit
                        ? <><HabitMark habit={findHabit(compass?.categories, value)} label style={{ marginRight: 4 }} />{value}</>
                        : `"${value}"`
                      }
                    </span>
                  </div>
                ));
              })}
            </div>
          )}
        </div>
      ) : (
        // ── Edit / form state ──
        <>
          <div>
          <div style={{ background: C.foldCard, borderRadius: C.r, border: `1px solid ${C.borderCard}`, padding: "16px 16px 12px", boxShadow: C.shadow }}>
            {DAILY_REMINDER_CATEGORIES.map((cat, i) => {
              const compassItems = kindTexts(compass, cat.kind);
              const values = localData[cat.key] || [];
              const available = compassItems.filter(item => !values.includes(item));
              const atMax = values.length >= MAX_REMINDERS;
              return (
                <div key={cat.key} style={{ paddingBottom: 6, marginBottom: 12, borderBottom: i < DAILY_REMINDER_CATEGORIES.length - 1 ? `1px solid ${C.border}` : "none" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.textDim, marginBottom: 8, display: "flex", alignItems: "center", gap: 5 }}>
                    <span>{cat.icon}</span>
                    <span style={{ letterSpacing: "0.05em", textTransform: "uppercase", flex: 1 }}>{cat.label}</span>
                    {i === 0 && (
                      <button onClick={() => setEditing(false)} aria-label="Close"
                        style={{ width: 44, height: 44, margin: -12, padding: 0, background: "none", border: "none", color: C.textDim, cursor: "pointer", fontSize: 20, lineHeight: 1, fontFamily: "inherit" }}>×</button>
                    )}
                  </div>
                  {values.length === 0 && available.length > 0 && (
                    <div style={{ fontSize: 13, color: C.textDim, fontStyle: "italic", marginBottom: 8 }}>
                      {cat.emptyPrompt}
                    </div>
                  )}
                  {values.map((value, vi) => (
                    <div key={vi} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: C.accentLight, border: `1px solid ${C.accentMid}`, borderRadius: C.rsm, padding: "8px 12px", marginBottom: 6 }}>
                      <span style={{ fontSize: 14, color: C.text, fontStyle: "italic", flex: 1, lineHeight: 1.5 }}>{value}</span>
                      <button onClick={() => removeItem(cat.key, value)} aria-label={`Remove "${value}"`}
                        style={{ width: 44, height: 44, margin: "-12px -12px -12px 0", padding: 0, background: "none", border: "none", color: C.textDim, cursor: "pointer", fontSize: 17, flexShrink: 0, fontFamily: "inherit" }}>×</button>
                    </div>
                  ))}
                  {available.length === 0 && values.length === 0 && (
                    <div style={{ fontSize: 14, color: C.textMid }}>
                      {`You don't have any ${COMPASS_KINDS[cat.kind].many} yet.`}
                      {(
                        <button onClick={() => onNav(COMPASS_KINDS[cat.kind].screen)}
                          style={{ display: "block", minHeight: 44, marginTop: 4, padding: 0, background: "none", border: "none", color: C.textGreen, fontSize: 14, fontWeight: 700, fontStyle: "normal", cursor: "pointer", fontFamily: "inherit" }}>
                          {`Add a ${COMPASS_KINDS[cat.kind].one}`} ›
                        </button>
                      )}
                    </div>
                  )}
                  {!atMax && available.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: values.length > 0 ? 6 : 0 }}>
                      {available.map((item, j) => (
                        <button key={j} onClick={() => addItem(cat.key, item)}
                          style={{ minHeight: 44, padding: "7px 12px", background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.rsm, fontSize: 13, color: C.text, cursor: "pointer", lineHeight: 1.4, textAlign: "left", fontFamily: "inherit" }}>
                          {cat.key === "habits" && <HabitMark habit={findHabit(compass?.categories, item)} style={{ marginRight: 5 }} />}
                          {item}
                        </button>
                      ))}
                    </div>
                  )}
                  {atMax && (
                    <div style={{ fontSize: 12, color: C.textDim, fontStyle: "italic", marginTop: 4 }}>
                      Maximum of {MAX_REMINDERS} reached
                    </div>
                  )}
                </div>
              );
            })}
            <div style={{ textAlign: "center", marginTop: 8 }}>
              <button onClick={() => setEditing(false)}
                style={{ minHeight: 44, background: C.accent, color: C.onAccent, border: "none", borderRadius: C.rsm, padding: "9px 32px", fontSize: 14, fontWeight: 700, cursor: "pointer", letterSpacing: "0.03em", fontFamily: "inherit" }}>
                Done
              </button>
            </div>
          </div>
          </div>
        </>
      )}
      </div>
    </FoldCard>
  );
}



function AccountTypeInfoModal({ onClose }) {
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 300, animation: "fdIn 0.2s ease" }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 301, width: "min(340px, calc(100vw - 40px))", background: C.surface, borderRadius: 24, padding: "32px 28px 28px", boxShadow: "0 20px 60px rgba(0,0,0,0.25)", animation: "scaleIn 0.2s ease forwards", textAlign: "center" }}>

        {/* Icon graphic */}
        <div style={{ marginBottom: 20 }}>
          <svg width="90" height="90" viewBox="0 0 100 100" style={{ display: "block", margin: "0 auto" }}>
            <circle cx="50" cy="50" r="46" fill={`rgba(42,95,74,0.08)`} stroke={`rgba(42,95,74,0.25)`} strokeWidth="1.5" />
            <circle cx="50" cy="50" r="38" fill={`rgba(42,95,74,0.1)`} stroke={C.accent} strokeWidth="2" />
            <text x="50" y="64" textAnchor="middle" fontSize="34" style={{ userSelect: "none" }}>🧠</text>
          </svg>
        </div>

        <div style={{ fontSize: 20, fontWeight: 800, color: C.text, marginBottom: 12, letterSpacing: "-0.3px" }}>Work vs. Personal</div>
        <div style={{ fontSize: 15, color: C.textMid, lineHeight: 1.65, marginBottom: 24 }}>
          We recommend having <strong>2 separate accounts</strong> — one for Work, one for Personal.<br /><br />
          Mixing both in the same lists can quickly become overwhelming and trigger procrastination. Keeping them separate clears the mind, sharpens focus, and helps you get more done.
        </div>

        <button onClick={onClose} style={{ width: "100%", background: C.accent, color: "white", border: "none", borderRadius: C.rsm, padding: "13px 0", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          Got it
        </button>
      </div>
    </>
  );
}

function MotivationMoment({ settings }) {
  const [expanded, setExpanded] = useState(false);

  // Derive which phase slot we're in (0=morning, 1=afternoon, 2=evening)
  const getSlot = () => PHASE_IDS.indexOf(phaseNow(settings));

  // A new strategy each part of the day, in order through the list (motivationPick.js).
  const allTheories = SOLUTION_THEORY_GROUPS.flatMap(g => g.theories);
  const strategy = allTheories[motivationIndex(new Date(), getSlot(), allTheories.length)];

  return (
    <button onClick={() => setExpanded(e => !e)} aria-expanded={expanded}
      style={{ display: "block", width: "100%", textAlign: "left", fontFamily: "inherit", background: C.foldCard, borderRadius: C.r, border: `1px solid ${C.border}`, padding: "12px 14px", boxShadow: C.shadow, cursor: "pointer" }}
    >
      <span style={{ display: "block", fontSize: 12, fontWeight: 700, color: C.textGreen, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 6 }}>Motivation Moment</span>
      <span style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: C.textGreen, paddingTop: 2, minWidth: 22, flexShrink: 0 }}>#{strategy.n}.</span>
        <span style={{ flex: 1 }}>
          <span style={{ display: "block", fontSize: 15, fontWeight: 500, color: C.text, lineHeight: 1.4 }}>{strategy.title}</span>
          {expanded && (
            <span style={{ display: "block", fontSize: 15, color: C.textMid, lineHeight: 1.5, marginTop: 6 }}>{strategy.body}</span>
          )}
          {!expanded && (
            <span style={{ display: "block", fontSize: 12, color: C.textGreen, marginTop: 4, fontWeight: 500 }}>Tap to learn more</span>
          )}
        </span>
      </span>
    </button>
  );
}

// Today's Inspiration on Today: Motivation Moment and the "Need a nudge?" buttons in one fold. Closed until opened; it
// stays as the user left it. Off Radar since 25 Oct 2026 (Craig: "I haven't been using it, so it might just be noise and
// it's already on the Today page").
function TodaysInspiration({ settings, onToggleSection, onNav }) {
  const page = OPEN_PAGES.TODAY;
  return (
    <FoldCard id={`${page}-inspiration`} icon="✨" title="Today's Inspiration" line="Ideas to help you get started" open={isOpen(settings, page, INSPIRATION)} onToggle={() => onToggleSection(page, INSPIRATION)}>
      <MotivationMoment settings={settings} />
      <NeedANudge onChat={(mode) => onNav("chat", mode)} />
    </FoldCard>
  );
}

// ─── HELP BUTTON + OVERLAY ───────────────────────────────────────────
// A small ? inside a card (check-ins, reminders) that opens the shared help sheet.
// `label` names the ? for a screen reader: "About Today's Reminders" unless the caller says better.
function HelpTip({ title, lines, label = `About ${title}` }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {/* A 44px tap area around the 22px circle; the negative margin keeps the card's layout as it was. */}
      <button onClick={e => { e.stopPropagation(); setOpen(true); }} aria-label={label}
        style={{ display: "grid", placeItems: "center", width: 44, height: 44, margin: -11, padding: 0, border: "none", background: "none", cursor: "pointer", flexShrink: 0, fontFamily: "inherit" }}>
        <span style={{ display: "grid", placeItems: "center", width: 22, height: 22, borderRadius: "50%", border: `1.5px solid ${C.textGreen}`, background: C.accentLight, boxSizing: "border-box" }}>
          <span style={{ fontWeight: 700, fontSize: 12, color: C.textGreen, lineHeight: 1 }}>?</span>
        </span>
      </button>
      {open && <HelpSheet title={title} lines={lines} onClose={() => setOpen(false)} />}
    </>
  );
}

function TodayPage({ todayData, onNav, onNudgeHistory, onCheckin, onProcrastinate, onOverwhelmed, onChat, onDecide, onRevise, onAction, onNudgeHelp, onMotivate, onAvoiding, onWhyProcrastinate, settings, onToggleSection, checkIn, setCheckIn, softStreak, lifetimeCount, uid, compass, tasks, goals = [], hasFullAccess, trialDaysLeft, userEmail, activeNudge, onSnoozeNudge, onGotItNudge, onPinNudge, onUnpinNudge, pinnedNudge, pinnedNudgeId, nudgeHistory }) {
  const [win, setWin] = useState(todayData.win || "");
  const [editingWin, setEditingWin] = useState(false);
  const [pickerPhase, setPickerPhase] = useState(null);
  const [newTaskIn, setNewTaskIn] = useState(null); // { todayPhase, text } while a new task is being written
  const [choosingPhase, setChoosingPhase] = useState(null); // the part of the day "Help me choose" is for
  const closePicker = useCallback(() => setPickerPhase(null), []);
  const phaseAdd = usePhaseAdd((taskId, fields) => writeTask(uid, taskId, fields));
  // Picking a task puts it in that part of the day and closes the sheet or "Help me choose".
  const addToPhase = (task, phaseId) => {
    phaseAdd.add(task, phaseId);
    setPickerPhase(null);
    setChoosingPhase(null);
  };
  const closeChoosing = useCallback(() => setChoosingPhase(null), []);
  const [editingTask, setEditingTask] = useState(null);
  const setEditingTaskId = (taskId) => {
    if (!taskId) { setEditingTask(null); return; }
    const t = (tasks || []).find(t => t.id === taskId);
    if (t) setEditingTask({ ...t });
  };
  const [openPhases, setOpenPhases] = useState(() => {
    // The part of the day it is now opens, to the minute, so Today agrees with Radar's "Add to today".
    // Outside the day (before it starts or after it ends) all three stay closed.
    const now = new Date();
    const m = now.getHours() * 60 + now.getMinutes();
    const inDay = m >= minutes(settings?.dayStart || "06:00") && m < minutes(settings?.dayEnd || "22:00");
    const current = phaseNow(settings, now);
    return Object.fromEntries(PHASE_IDS.map(id => [id, inDay && id === current]));
  });
  const ref = useRef(null);

  useEffect(() => { if (editingWin) ref.current?.focus(); }, [editingWin]);

  // Keep win in sync with todayData
  useEffect(() => { setWin(todayData.win || ""); }, [todayData.win]);

  const saveWin = async (value) => {
    if (!uid) return;
    await setDoc(
      doc(db, "users", uid, "days", todayKey()),
      { win: value },
      { merge: true }
    ).catch(console.error);
  };

  const start    = settings?.dayStart       || "06:00";
  const afStart  = settings?.afternoonStart || "12:00";
  const eveStart = settings?.eveningStart   || "17:00";
  const end      = settings?.dayEnd         || "22:00";

  const phases = [
    { id: "morning",   icon: "🌅", label: PHASE_LABELS.morning,   time: `${fmtHour(start)} – ${fmtHour(afStart)}`,  data: todayData.morning   },
    { id: "afternoon", icon: "☀️", label: PHASE_LABELS.afternoon, time: `${fmtHour(afStart)} – ${fmtHour(eveStart)}`, data: todayData.afternoon },
    { id: "evening",   icon: "🌙", label: PHASE_LABELS.evening,   time: `${fmtHour(eveStart)} – ${fmtHour(end)}`,   data: todayData.evening   },
  ];

  return <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", background: C.bg, position: "relative" }}>
    {!hasFullAccess && <PaywallOverlay onNav={onNav} />}
    <TopBar title="Today" help={HELP.today} />
    <div className="page-columns-scroll" style={{ flex: 1, overflowY: "auto", padding: `14px ${columnPadding()} 24px` }}>
      <TrialBanner daysLeft={trialDaysLeft} onNav={onNav} />
      <>
        {/* On a laptop, the day's set-up on the left and Today's Plan on the right (Craig, 2 Nov 2026). */}
        <div className="page-columns">
        <div>
          {/* Daily Win */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 5 }}>Today's win</div>
            {editingWin ? (
              <input ref={ref} value={win} onChange={e => setWin(e.target.value)}
                onBlur={() => { setEditingWin(false); saveWin(win); }}
                onKeyDown={e => { if (e.key === "Enter") { setEditingWin(false); saveWin(win); } }}
                style={{ width: "100%", border: "none", borderBottom: `2px solid ${C.accent}`, background: "transparent", fontSize: 17, fontWeight: 600, color: C.text, outline: "none", paddingBottom: 3, boxSizing: "border-box", fontFamily: "inherit" }} />
            ) : (
              <div onClick={() => setEditingWin(true)} style={{ fontSize: 19, fontWeight: 600, color: win ? C.text : C.textDim, fontStyle: win ? "normal" : "italic", cursor: "text", borderBottom: `1px dashed ${C.border}`, paddingBottom: 4, lineHeight: 1.45 }}>
                {/* {win || "(Enter the MOST important thing to do today)"} */}
                {/* {win || "(What would make today a successful day?)"} */}
                {win || "(What would make today successful?)"}
              </div>
            )}
          </div>

          {/* Check-in card */}
          <CheckInCard checkIn={checkIn} onEdit={(t, ctx) => setCheckIn({ time: t, context: ctx ?? "" })} onCheckInNow={() => onNav("chat", "checkin")} />
          <TodaysInspiration settings={settings} onToggleSection={onToggleSection} onNav={onNav} />
          {/* Back on the page, above Today's Plan (Craig, 24 Oct 2026: "move Today's Reminders back out of the fold"). */}
          <DailyReminders todayData={todayData} compass={compass} uid={uid} onNav={onNav} />
        </div>

        <div>
          {/* Today's Plan heading */}
          {/* Beside the Radar button when the plan has a column of its own, so its cards line up with the left column's. */}
          <div className="plan-head">
          <h2 style={{ textAlign: "center", margin: "17px 0 8px", userSelect: "none", fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: 20, color: C.text, letterSpacing: "-0.3px", fontWeight: 700 }}>
            Today's Plan
          </h2>
          <HelpDeciding onRadar={() => onNav("radar")} />
          </div>

          {/* Phase sections */}
          {phases.map(ph => {
            const phaseOpen = openPhases[ph.id];
            const parseTimeToMins = (t) => {
              if (!t) return Infinity;
              const m = t.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i);
              if (!m) return Infinity;
              let h = parseInt(m[1]), min = parseInt(m[2] || "0");
              const ampm = (m[3] || "").toLowerCase();
              if (ampm === "pm" && h !== 12) h += 12;
              if (ampm === "am" && h === 12) h = 0;
              return h * 60 + min;
            };
            const phaseTasks = (tasks || [])
              .filter(t => t.todayPhase === ph.id)
              .sort((a, b) => parseTimeToMins(a.todayTime) - parseTimeToMins(b.todayTime));
            const taskCount = phaseTasks.length;
            return (
              <div key={ph.id} style={{ background: C.surface2, borderRadius: C.r, border: `1px solid ${C.borderCard}`, marginBottom: 10, boxShadow: C.shadow, overflow: "hidden" }}>
 
                {/* Header row — always visible, tap to collapse */}
                <div
                  onClick={() => setOpenPhases(p => ({ ...p, [ph.id]: !p[ph.id] }))}
                  style={{ padding: "13px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", userSelect: "none", background: C.surface }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 17 }}>{ph.icon}</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 18, color: C.text }}>{ph.label}</div>
                      <div style={{ fontSize: 14, color: C.textDim }}>{ph.time}</div>
                    </div>
                  </div>
                  <span style={{ color: C.textGreen, fontSize: 11 }}>{phaseOpen ? "▲" : "▼"}</span>
                </div>
 
                {/* Collapsible body */}
                {phaseOpen && (
                  <div style={{ padding: "0 0 14px", borderTop: `1px solid ${C.borderCard}`, background: C.surface2 }}>
 
                    {/* Task rows */}
                    {phaseTasks.map(t => (
                      <TaskRow
                        key={t.id}
                        task={t}
                        uid={uid}
                        compass={compass}
                        tasks={tasks}
                        onEdit={setEditingTaskId}
                      />
                    ))}
 
                    {/* No tasks placeholder */}
                    {taskCount === 0 && (
                      <div style={{ fontSize: 15, color: C.textDim, fontStyle: "italic", paddingTop: 12, paddingLeft: 16 }}>Not planned yet</div>
                    )}
 
                    {/* Add task button */}
                    <div style={{ padding: "0 16px" }}>
                      <button data-add-task={ph.id}
                        onClick={e => { e.stopPropagation(); setPickerPhase(ph.id); }}
                        style={{ minHeight: 44, fontSize: 14, color: C.textGreen, cursor: "pointer", marginTop: taskCount ? 6 : 2, fontWeight: 500, padding: 0, background: "none", border: "none", borderTop: taskCount ? `1px solid ${C.border}` : "none", fontFamily: "inherit" }}
                      >
                        + Add task
                      </button>
                    </div>
 
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </div>
 
          {/* Task edit modal — rendered at page level to prevent re-render flickering */}
          {editingTask && (
            <TaskEditModal
              task={editingTask}
              uid={uid}
              compass={compass}
              tasks={tasks}
              goals={goals}
              settings={settings}
              onClose={() => setEditingTaskId(null)}
            />
          )}

          {/* Task picker sheet */}
          {pickerPhase && (
            <AddToPhaseSheet phaseId={pickerPhase} tasks={tasks || []} goals={goals} dueSoonWindow={dueSoonWindowOf(settings)}
              onClose={closePicker} onPick={(task) => addToPhase(task, pickerPhase)}
              onNew={(text) => { setNewTaskIn({ todayPhase: pickerPhase, text }); setPickerPhase(null); }}
              onHelpMeChoose={() => { setChoosingPhase(pickerPhase); setPickerPhase(null); }} />
          )}
          {choosingPhase && (
            <HelpMeChoose phaseId={choosingPhase} tasks={tasks || []} goals={goals} onClose={closeChoosing}
              onRank={(view, ordered) => writeGoalRanks(uid, view, ordered)}
              onPick={(task) => addToPhase(task, choosingPhase)}
              onSeeAll={() => { setPickerPhase(choosingPhase); setChoosingPhase(null); }} />
          )}
          {newTaskIn && (
            <TaskEditModal newTaskDefaults={newTaskIn} uid={uid} compass={compass} tasks={tasks} goals={goals} settings={settings} onClose={() => setNewTaskIn(null)} />
          )}
          {phaseAdd.toast}
      </>
    </div>

    {/* ── Account indicator ── */}
    <div style={{ textAlign: "center", padding: "4px 0 8px", fontSize: 12, color: C.textDim }}>
      {userEmail}{settings.accountType ? ` · ${settings.accountType === "work" ? "Work" : "Personal"}` : ""}
    </div>

    <FAB
      onCheckin={onCheckin}
      onProcrastinate={onProcrastinate}
      onOverwhelmed={onOverwhelmed}
      onChat={onChat}
      onDecide={onDecide}
      onRevise={onRevise}
      onNudgeHelp={onNudgeHelp}
      onMotivate={onMotivate}
    />
    <BottomNav active="today" onNav={onNav} />
  </div>;
}

// ─── CHAT ────────────────────────────────────────────────────────────

const QUICK_STARTS_ROW1 = [
  { icon: "🔥", label: "Motivate Me!", message: "I'm feeling unmotivated and need some help getting started." },
  { icon: "🚨", label: "Check In", message: "I'm checking in on my plan" },
  { icon: "💡", label: "How to Use Nudge", message: "How does Nudge work? I'd like a guide to using it effectively." },
];

const QUICK_STARTS_ROW2 = [
  { icon: "📅", label: "Update My Plan", message: "Plan or revise my day" },
  { icon: "❓", label: "Why do I Procrastinate?", message: "Why do people procrastinate?" },
  { icon: "💬", label: "Just Chat",     message: "I just want to chat for a bit. No agenda." },
];

const DEFAULT_GREETING = "I'm listening. What's going on?";

function ChatHub({ onNav, initialMode, chatSeedMessage, onClearChatSeed, nudgeHistory, setNudgeHistory, checkIn, setCheckIn, onCheckIn, lastSessionDate, compass,
  chatMessages, setChatMessages, chatHistory, setChatHistory, uid, todayData, settings, tasks, goals = [] }) {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  // The conversation starters show on a new chat and fold away once anything is sent (Craig, 24 Oct 2026: "expand
  // … by default when the user visits the page for a new chat. And collapse it as soon as they tap on one").
  const [showQuickStarts,  setShowQuickStarts]  = useState(() => !initialMode && !chatSeedMessage && !(chatMessages || []).some(m => m.role === "user"));
  const endRef = useRef(null);
  const initDone = useRef(false);
  const inputRef = useRef(null);
  const fullTextRef = useRef("");
  const loadingRef = useRef(false);

  const extractPlanSilently = useCallback(async (history, lastResponse) => {
    try {
      // Broad trigger — any conversation that might involve plan changes
      const triggerKeywords = [
        "morning", "afternoon", "evening", "tonight", "today",
        "plan", "revise", "reschedule", "change", "update", "add", "move", "push", "cancel",
        "checking in", "checked in", "came back", "update you",
        "finished", "completed", "done", "got it done", "knocked it out",
        "didn't", "skipping", "not going to",
        "📅", "🌅", "☀️", "🌙"
      ];

      const recentMessages = history.slice(-6).map(m => m.content || "").join(" ").toLowerCase();
      const combinedText = (lastResponse + " " + recentMessages).toLowerCase();
      const shouldExtract = triggerKeywords.some(k => combinedText.includes(k.toLowerCase()));
      if (!shouldExtract) return;

      // Normalise text for matching
      const normalise = (t) => (t || "").toLowerCase().replace(/[^a-z0-9\s]/g, "").replace(/\s+/g, " ").trim();
      const matchScore = (a, b) => {
        const na = normalise(a);
        const nb = normalise(b);
        if (na === nb) return 100;
        if (na.length >= 4 && nb.includes(na)) return 90;
        if (nb.length >= 4 && na.includes(nb)) return 90;
        const wordsA = na.split(" ").filter(w => w.length >= 4);
        const wordsB = nb.split(" ").filter(w => w.length >= 4);
        if (wordsA.length === 0 || wordsB.length === 0) return 0;
        const shared = wordsA.filter(w => wordsB.includes(w));
        if (shared.length === 0) return 0;
        // Require majority overlap — single shared word is not enough
        const ratio = shared.length / Math.max(wordsA.length, wordsB.length);
        if (ratio < 0.5) return 0;
        return Math.round(ratio * 80);
      };
      const MATCH_THRESHOLD = 70;

      // Build snapshot of active (not done) scheduled tasks for the extraction prompt
      // Completed tasks are excluded so the model doesn't accidentally match new tasks against old done work
      const scheduledTasksSnapshot = openPlanTasks(tasks)
        .map(t => `- "${t.text}" (phase: ${t.todayPhase}${t.todayTime ? `, time: ${t.todayTime}` : ""})`)
        .join("\n") || "None scheduled yet.";

      // Build snapshot of all tasks for matching context
      const allTasksSnapshot = tasks
        .map(t => `- "${t.text}"${t.deadline ? ` (deadline: ${t.deadline})` : ""}`)
        .join("\n") || "No tasks.";

      const extractionPrompt = `You are a plan extraction assistant for a personal accountability app called Nudge.

CURRENTLY SCHEDULED TASKS FOR TODAY:
${scheduledTasksSnapshot}

ALL TASKS IN THE USER'S LIST (for matching):
${allTasksSnapshot}

WIN STATEMENT: ${todayData?.win || "not set"}

RECENT CONVERSATION:
${history.slice(-12).map(m => `${m.role}: ${m.content}`).join("\n")}

LAST AI RESPONSE:
${lastResponse}

---

Your job: determine what task scheduling changes, if any, should be made based on this conversation.

First, classify this conversation:
- PLANNING: User is actively planning or replanning their day, adding/changing tasks, updating times. May also include reporting completed tasks alongside scheduling new ones.
- CHECKIN: User is checking in, reporting status, saying what they did or didn't do — with little or no new scheduling happening.
- CASUAL: Casual conversation, emotional support, no plan changes intended

For both PLANNING and CHECKIN: set "done": true ONLY for tasks that were explicitly confirmed as already completed by the user — i.e. tasks that already existed on the plan and the user reported finishing. NEVER set "done": true for a task that is being scheduled for the first time in this conversation — a brand new task cannot already be done.

Then return a JSON object:

For PLANNING or CHECKIN conversations:
{
  "planned": true,
  "conversationType": "planning|checkin",
  "win": "updated win statement, or empty string to leave unchanged",
  "tasks": []
}

For CASUAL conversations:
{ "planned": false }

Each task object in your response:
{
  "text": "task name — ALWAYS use the EXACT text from the user's task list above if ANY reasonable match exists. Only use a new name from the conversation if there is absolutely no match in the task list. Never elaborate, expand, or reword an existing task name.",
  "phase": "morning|afternoon|evening",
  "time": "e.g. 9:00am, or null if not set",
  "done": false,
  "cancelled": false,
  "emotion": "happy|neutral|dreading or null",
  "commitment": null,
  "obstacle": null,
  "microStep": null,
  "commitmentStatement": null,
  "emotionalStakes": null,
  "duration": null,
  "urgency": null,
  "enjoyNote": "string from '💡 Make it better:' line in AI response, or null"
}

RULES:
1. Match task names against the user's task list — use EXACT text from the list when a match exists. Matching is case-insensitive and ignores punctuation.
2. Only include tasks that were mentioned or changed in this conversation.
3. For CHECKIN: set "done": true for tasks confirmed complete or where user reported doing the work. Always include the task's current phase in the extraction even when marking it done — use the phase from the task list context below.
4. For PLANNING: include all tasks discussed with their phase assignment.
5. Only populate V2 attributes (emotion, commitment, etc.) if explicitly discussed in conversation.
6. If a task was cancelled or pushed to another day: include it with "cancelled": true and no phase.
7. If a task moved phases: include it once with the NEW phase.
8. If win statement was not discussed: return empty string for win.
9. Return raw JSON only — no explanation, no markdown.

PHASE TIME BOUNDARIES (use these to assign the correct phase when a time is given):
- morning: ${settings?.dayStart || "06:00"} – just before afternoon
- afternoon: ${settings?.afternoonStart || "12:00"} – just before evening
- evening: ${settings?.eveningStart || "17:00"} – ${settings?.dayEnd || "23:00"}
If no time is given, use context from the conversation to infer the phase.`;

      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 1500,
          messages: [{ role: "user", content: extractionPrompt }],
        }),
      });

      const data = await res.json();
      const raw = data.content?.[0]?.text?.trim();
      if (!raw) return;

      const cleaned = raw.replace(/^[\s]*json/gm, "").replace(/\u0060{3}/g, "").trim();
      const plan = JSON.parse(cleaned);
      console.log("Plan extracted:", plan);

      if (!plan.planned || !uid) return;

      // Update win statement and planned flag in todayData
      if (plan.win || plan.planned) {
        await setDoc(
          doc(db, "users", uid, "days", todayKey()),
          {
            planned: true,
            ...(plan.win ? { win: plan.win } : {}),
          },
          { merge: true }
        );
      }

      // Apply task changes to the tasks collection
      const extractedTasks = plan.tasks || [];
      const processedTaskIds = new Set();
      for (const ext of extractedTasks) {
        if (!ext.text) continue;

        // Find best matching existing task — exclude completed tasks and already-processed tasks
        let bestTask = null;
        let bestScore = 0;
        for (const t of tasks.filter(t => !t.done && !processedTaskIds.has(t.id))) {
          const score = matchScore(t.text, ext.text);
          if (score > bestScore) { bestScore = score; bestTask = t; }
        }

        if (ext.cancelled) {
          // Remove from today schedule if it was scheduled
          if (bestTask && bestTask.todayPhase) {
            await updateTask(uid, bestTask.id, { todayPhase: null, todayTime: null });
            console.log("Task removed from today:", bestTask.text);
          }
          continue;
        }

        // Fall back to the existing task's phase when extraction omits it (e.g. time-only updates)
        const resolvedPhase = ext.phase || (bestScore >= MATCH_THRESHOLD && bestTask?.todayPhase) || null;
        if (!resolvedPhase) continue;

        const taskFields = {
          todayPhase: resolvedPhase,
          ...(ext.time ? { todayTime: ext.time } : {}),
          ...(ext.done ? { done: true } : {}),
          ...(ext.emotion ? { emotion: ext.emotion } : {}),
          ...(ext.commitment != null ? { commitment: ext.commitment } : {}),
          ...(ext.obstacle ? { obstacle: ext.obstacle } : {}),
          ...(ext.microStep ? { microStep: ext.microStep } : {}),
          ...(ext.commitmentStatement ? { commitmentStatement: ext.commitmentStatement } : {}),
          ...(ext.emotionalStakes ? { emotionalStakes: ext.emotionalStakes } : {}),
          ...(ext.duration ? { duration: ext.duration } : {}),
          ...(ext.urgency ? { urgency: ext.urgency } : {}),
          ...(ext.enjoyNote ? { enjoyNote: ext.enjoyNote } : {}),
        };

        if (bestScore >= MATCH_THRESHOLD && bestTask) {
          // Update existing task
          await updateTask(uid, bestTask.id, taskFields);
          processedTaskIds.add(bestTask.id);
          console.log("Task updated:", bestTask.text, taskFields);
        } else if (!ext.done) {
          // Only create a new task if it's not a completion — completions must match an existing task
          await createTask(uid, { text: ext.text, ...taskFields });
          console.log("Task created:", ext.text, taskFields);
        } else {
          console.log("Task completion skipped — no matching task found for:", ext.text);
        }
      }

      const isCheckin = plan.conversationType === "checkin";
      console.log("Plan extraction complete ✓", { conversationType: plan.conversationType });

      // Clear check-in card after a check-in conversation,
      // but only if a new check-in wasn't just set in this same response
      if (isCheckin) {
        const freshSnap = await getDoc(doc(db, "users", uid, "days", todayKey()));
        const freshCheckIn = freshSnap.exists() ? freshSnap.data().checkIn : null;
        if (!freshCheckIn?.time) {
          await setDoc(
            doc(db, "users", uid, "days", todayKey()),
            { checkIn: { time: "", context: "" } },
            { merge: true }
          ).catch(console.error);
          setCheckIn({ time: "", context: "" });
          console.log("Check-in cleared ✓");
        } else {
          console.log("Check-in preserved ✓", freshCheckIn.time);
        }
      }

    } catch (e) {
      console.error("Plan extraction error:", e);
    }
  }, [uid, todayData, tasks, setCheckIn, settings]);


  const sendMessage = useCallback(async (textOverride) => {
    const text = (textOverride || input).trim();
    if (!text || loadingRef.current) return;
    loadingRef.current = true;
    setShowQuickStarts(false);
    if (!textOverride) setInput("");
    setLoading(true);

    // Count as a chat session only when the first message is actually sent
    if (chatHistory.length === 0) onCheckIn?.();

    const userMsg = { role: "user", text, time: nowTime() };
    const thinkingMsg = { role: "ai", text: "", time: nowTime(), streaming: true };
    setChatMessages(prev => [...prev, userMsg, thinkingMsg]);

    const newH = [...chatHistory, { role: "user", content: text }];
    setChatHistory(newH);

    const abortController = new AbortController();
    const abortTimeout = setTimeout(() => abortController.abort(), 30000);

    try {

      const payload = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 4096,
          system: buildSystemPrompt(compass, todayData, settings, tasks),
          messages: newH,
        }),
        signal: abortController.signal,
      };

      let response = await fetch("/api/chat", payload);

      // Auto-retry up to 3 times on 529 (Anthropic overloaded) with exponential backoff
      if (response.status === 529) {
        const delays = [3000, 6000, 12000];
        for (const delay of delays) {
          await new Promise(res => setTimeout(res, delay));
          response = await fetch("/api/chat", { ...payload, signal: abortController.signal });
          if (response.status !== 529) break;
        }
      }

      if (!response.ok) throw new Error(`API error: ${response.status}`);

      const data = await response.json();
      fullTextRef.current = data.content?.[0]?.text || "";

      const extractNudge = (text) => {

          // Extract next check-in time from [CHECKIN:{...}] block
          const ci = text.match(/\[CHECKIN:\s*(\{.*?\})\]/s);
          if (ci) {
            try {
              const parsed = JSON.parse(ci[1]);
              if (parsed.time) {
                setCheckIn({ time: parsed.time, context: parsed.context || "" });
                onCheckIn();
              } else {
                // Empty time means clear the check-in
                setCheckIn({ time: "", context: "" });
              }
            } catch (e) { console.error("CheckIn parse error:", e); }
            text = text.replace(/\[CHECKIN:.*?\]/s, "").trim();
          } else {
            // Scan plain text for check-in time patterns
            const plainCi = text.match(/(?:come back at|check in at|check-in at|checking in at|be here (?:at|when you check in at))\s+([0-9]{1,2}(?::[0-9]{2})?\s*(?:am|pm)?)(?:\s*[—–-]+\s*([^.\n]+))?/i);
            if (plainCi) {
              const newTime = plainCi[1].trim();
              const newContext = plainCi[2] ? plainCi[2].trim() : "";
              const existingTime = checkIn?.time;
              // Only overwrite an existing check-in if the new time is later,
              // so a mid-conversation planning close doesn't clobber an earlier
              // check-in that was already set for a sooner commitment.
              const parseTime = (t) => {
                if (!t) return 0;
                const m = t.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
                if (!m) return 0;
                let h = parseInt(m[1]), min = parseInt(m[2] || 0);
                const ampm = (m[3] || "").toLowerCase();
                if (ampm === "pm" && h !== 12) h += 12;
                if (ampm === "am" && h === 12) h = 0;
                return h * 60 + min;
              };
              if (!existingTime || parseTime(newTime) < parseTime(existingTime)) {
                setCheckIn({ time: newTime, context: newContext });
                onCheckIn();
              }
            }
            // Scan plain text for check-in cancellation language
            const cancelPhrases = ["check-in cancelled", "check-in cleared", "check-in removed", "cancelled your check-in", "cleared your check-in", "removed your check-in", "no check-in scheduled", "won't schedule a check-in"];
            const textLower = text.toLowerCase();
            if (cancelPhrases.some(p => textLower.includes(p))) {
              setCheckIn({ time: "", context: "" });
            }
          }

        const m = text.match(/\[NUDGE:\s*(.+?)\s*\|\s*type:\s*(\w+)\]/s);
        if (m) {
          setNudgeHistory({
            id: Date.now(),
            message: m[1],
            type: m[2],
            nudgeCategory: "chat",
            time: nowTime(),
            date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" }),
            thread: "Chat"
          });
          const ci = text.match(/Come back at ([0-9]{1,2}(?::[0-9]{2})?\s*(?:am|pm)?)/i);
          if (ci) { setCheckIn({ time: ci[1].trim(), context: "" }); onCheckIn(); }
          return cleanMessage(text.replace(/\[NUDGE:.*?\]/s, ""));
        }
        return cleanMessage(text);
      };
      
      const finalText = extractNudge(fullTextRef.current);
      setChatMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: "ai", text: finalText, time: nowTime(), streaming: false };
        return updated;
      });
      setChatHistory(prev => [...prev, { role: "assistant", content: finalText }]);

      // Silent plan extraction — runs after every AI response
      extractPlanSilently(
        [...chatHistory, { role: "user", content: text }],
        finalText
      );

    } catch (err) {
      console.error("Streaming error:", err);
      const isAbort = err.name === "AbortError";
      const is529 = err.message?.includes("529");
      setChatMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: "ai",
          text: isAbort
            ? "Nudge took too long to respond. Please try again."
            : is529
              ? "Nudge is having trouble connecting right now — the AI service is under heavy load. Wait a moment and try again."
              : "Nudge couldn't connect just now. Check your connection and try again.",
          time: nowTime(),
          streaming: false
        };
        return updated;
      });
    } finally {
      clearTimeout(abortTimeout);
      loadingRef.current = false;
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, chatHistory, compass, checkIn, tasks]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initDone.current && (chatSeedMessage || initialMode)) {
      initDone.current = true;
      const modeMessages = {
        plan:         "Plan or revise my day",
        action:       "Time to take action. Help me get started.",
        help:         "I need help", 
        compass:      "Build my Compass", 
        talk:         null,
        chat:         "I just want to chat for a bit. No agenda.",
        overwhelmed:  "I'm feeling overwhelmed right now.",
        checkin:      "I'm checking in on my plan",
        procrastinate: "I'm procrastinating on something important. Help me figure out how to take action.",
        decide:       "Help me decide what to work on today.",
        compasshelp:  "What is My Compass all about? How should I use it? How do the categories work? And how does it integrate with the Today page?",
        nudgehelp:    "How does Nudge work? I'd like a guide to using it effectively.",
        motivate:     "I'm feeling unmotivated and need some help getting started.",
        avoiding:     "I need help. I've been spending time on things I enjoy instead of the harder, more important things I should be doing.",
        whyprocrastinate: "Why do people procrastinate?",
      };
      const msg = chatSeedMessage || modeMessages[initialMode];
      if (chatSeedMessage && onClearChatSeed) onClearChatSeed();
      if (msg) {
        if (initialMode === "checkin") {
          setCheckIn({ time: "", context: "" });
        }
        setTimeout(() => sendMessage(msg), 300);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMode, chatSeedMessage]);

  // Auto-inject theory list when Claude signals it's ready via trigger phrase.
  // Intentionally decoupled from initialMode — Claude can surface either list
  // from any chat mode (e.g. Motivate Me drifting into I'm Procrastinating).
  useEffect(() => {
    if (loading) return;
    const last = chatMessages[chatMessages.length - 1];
    if (!last || last.role !== "ai" || last.streaming) return;
    const textLower = (last.text || "").toLowerCase();
    const wantsProblemList  = textLower.includes("here are all 21 reasons");
    const wantsSolutionList = textLower.includes("here are all 27 strategies");
    if (!wantsProblemList && !wantsSolutionList) return;
    const token = wantsSolutionList
      ? "Tap any tile to learn more about tips and tricks for motivation:\n[SHOW_SOLUTION_THEORIES]"
      : "Tap any tile to learn more about the psychology of procrastination:\n[SHOW_PROBLEM_THEORIES]";
    setTimeout(() => {
      setChatMessages(prev => [
        ...prev,
        { role: "ai", text: token, time: nowTime(), streaming: false }
      ]);
    }, 400);
    if (wantsProblemList) {
      setTimeout(() => {
        setChatMessages(prev => [
          ...prev,
          { role: "ai", text: "Tap any of the above to learn more. Or if you've seen enough, would you like to see our 27 strategies for overcoming procrastination?", time: nowTime(), streaming: false }
        ]);
      }, 600);
    }
    if (wantsSolutionList && initialMode === "whyprocrastinate") {
      setTimeout(() => {
        setChatMessages(prev => [
          ...prev,
          { role: "ai", text: "Do you feel you're procrastinating on anything right now?", time: nowTime(), streaming: false }
        ]);
      }, 600);
    }
  }, [chatMessages, loading, setChatMessages, initialMode]);
 
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chatMessages, loading]);

  return <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", background: C.bg }}>
    <TopBar title="Chat" help={HELP.chat} extra={
      <BarButton label="Start a new chat" onClick={() => {
        setChatMessages([{ role: "ai", text: DEFAULT_GREETING, time: nowTime() }]);
        setChatHistory([]);
        setShowQuickStarts(true);
      }}>
        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 4v6h-6" /><path d="M20.49 15a9 9 0 1 1-.08-6.27" /></svg>
      </BarButton>
    } />

    {/* Messages */}
    <div style={{ flex: 1, overflowY: "auto", padding: `16px ${columnPadding(16)} 8px`, display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1 }} />
      {chatMessages.map((m, i) => {
        const showProblemList  = m.role === "ai" && m.text && m.text.includes("[SHOW_PROBLEM_THEORIES]");
        const showSolutionList = m.role === "ai" && m.text && m.text.includes("[SHOW_SOLUTION_THEORIES]");
        return (
          <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: m.role === "user" ? "flex-end" : "flex-start", marginBottom: 12, animation: "fiu 0.25s ease" }}>
            <div style={{ maxWidth: "82%", padding: "8px 12px", borderRadius: m.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px", background: m.role === "user" ? C.accent : C.surface, color: m.role === "user" ? "white" : C.text, fontSize: 14, lineHeight: 1.55, border: m.role === "ai" ? `1px solid ${C.border}` : "none", boxShadow: C.shadow, fontWeight: m.role === "user" ? 500 : 400 }}>
              {m.text
                ? cleanMessage(m.text).split("\n").map((l, j) => <span key={j}>{l}{j < cleanMessage(m.text).split("\n").length - 1 && <br />}</span>)
                : <TypingDots />
              }
              {m.streaming && m.text && (
                <span style={{ display: "inline-block", width: 2, height: 13, background: C.textMid, marginLeft: 2, verticalAlign: "middle", animation: "cursor 0.8s infinite" }} />
              )}
            </div>
            {showProblemList  && !m.streaming && <ProblemTheoryList  onSendMessage={sendMessage} />}
            {showSolutionList && !m.streaming && <SolutionTheoryList onSendMessage={sendMessage} />}
            <div style={{ fontSize: 12, color: C.textDim, marginTop: 3, padding: "0 4px" }}>{m.time}</div>
          </div>
        );
      })}
      <div ref={endRef} />
    </div>


    {/* Quick start pills — shown when ⚡ is tapped */}
    {showQuickStarts && (
      <div style={{ padding: `8px ${columnPadding(16)} 6px`, display: "flex", flexDirection: "column", gap: 6, flexShrink: 0, background: C.surface, borderTop: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", gap: 7, overflowX: "auto", scrollbarWidth: "none" }}>
          {QUICK_STARTS_ROW2.map(qs => (
            <button key={qs.label} onClick={() => { if (!loading) sendMessage(qs.message); }} style={{ flexShrink: 0, minHeight: 44, display: "flex", alignItems: "center", gap: 5, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 100, padding: "5px 12px", fontFamily: "inherit", fontSize: 12, color: C.textMid, cursor: loading ? "default" : "pointer", whiteSpace: "nowrap", userSelect: "none" }}>
              <span style={{ fontSize: 12 }}>{qs.icon}</span>
              {qs.label}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 7, overflowX: "auto", scrollbarWidth: "none" }}>
          {QUICK_STARTS_ROW1.map(qs => (
            <button key={qs.label} onClick={() => { if (!loading) sendMessage(qs.message); }} style={{ flexShrink: 0, minHeight: 44, display: "flex", alignItems: "center", gap: 5, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 100, padding: "5px 12px", fontFamily: "inherit", fontSize: 12, color: C.textMid, cursor: loading ? "default" : "pointer", whiteSpace: "nowrap", userSelect: "none" }}>
              <span style={{ fontSize: 12 }}>{qs.icon}</span>
              {qs.label}
            </button>
          ))}
        </div>
      </div>
    )}

    {/* Input */}
    <div style={{ padding: `4px ${columnPadding(16)} 8px`, display: "flex", gap: 8, alignItems: "flex-end", background: C.surface, borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
      <textarea ref={inputRef} value={input}
        onChange={e => {
          setInput(e.target.value);
          e.target.style.height = "auto";
          e.target.style.height = Math.min(e.target.scrollHeight, 100) + "px";
        }}
        onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
        placeholder="What's on your mind..."
        rows={1}
        style={{ flex: 1, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 18, padding: "7px 14px", fontFamily: "inherit", fontSize: 14, color: C.text, resize: "none", outline: "none", maxHeight: 100, lineHeight: 1.5, overflowY: "auto" }}
      />
      {/* 44px tap areas around the 34px circles; the negative margin keeps the row as it was. */}
      <button onClick={() => setShowQuickStarts(s => !s)} aria-label="Show conversation starters" aria-pressed={showQuickStarts}
        style={{ width: 44, height: 44, margin: -5, padding: 0, border: "none", background: "none", flexShrink: 0, display: "grid", placeItems: "center", cursor: "pointer" }}>
        <span style={{ width: 34, height: 34, borderRadius: "50%", background: showQuickStarts ? C.accent : C.surface2, display: "grid", placeItems: "center", transition: "background 0.15s" }}>
          <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="white" stroke="none"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
        </span>
      </button>
      <button onClick={() => sendMessage()} aria-label="Send"
        style={{ width: 44, height: 44, margin: -5, padding: 0, border: "none", background: "none", flexShrink: 0, display: "grid", placeItems: "center", cursor: input.trim() && !loading ? "pointer" : "default", fontFamily: "inherit" }}>
        <span aria-hidden="true" style={{ width: 34, height: 34, borderRadius: "50%", background: input.trim() && !loading ? C.accent : C.surface2, display: "grid", placeItems: "center", fontSize: 15, fontWeight: 700, color: "white", transition: "background 0.15s" }}>↑</span>
      </button>
    </div>

    <BottomNav active="chat" onNav={onNav} />
  </div>;
}

// ─── MY COMPASS ──────────────────────────────────────────────────────
const CATEGORY_EMOJIS = [
  "🧭","👤","👨‍👩‍👧","❤️","🧠","😊","🌱","🌟","🙏","✨","🎁",
  "💪","🏃","🧘","🥗","😴","🚴","🏋️","🧬","💊","🫀",
  "🏠","🏡","✈️","🎯","🎨","🎵","📚","🐾","🌿","🍳","🛋️",
  "💼","💻","📊","🚀","🏆","💡","🤝","📋","🎓","🔧","⚡",
  "💰","💳","📈","🏦","💵","🪙","📉","🔐",
];

const CATEGORY_DEFS = [
  { id: "personal",      label: "Personal",      icon: "🧭", sectionsKey: "personal", color: "#5B50D9" },
  { id: "health",        label: "Health",        icon: "💪", sectionsKey: "standard", color: "#2A9A78" },
  { id: "home",          label: "Home",          icon: "🏠", sectionsKey: "standard", color: "#3B75D9" },
  { id: "career",        label: "Career",        icon: "💼", sectionsKey: "standard", color: "#E8853A" },
  { id: "financial",     label: "Financial",     icon: "💰", sectionsKey: "standard", color: "#3DAA6A" },
  { id: "relationships", label: "Relationships", icon: "❤️", sectionsKey: "standard", color: "#E8556A" },
];


const DEFAULT_MINDSET = [
  "Just start. Action breeds motivation.",
  "Spark the fire inside you.",
  "Done is better than perfect.",
  "Starting always sounds worse than it is.",
  "Progress compounds — small steps count.",
  "Your future self will thank you.",
];

const makeEmptyCategory = (id, label, icon, color = "#5B50D9") => ({
  id, label, icon, color,
  habits: [],
  traps: [],
});

// ─── MANAGE CATEGORIES ───────────────────────────────────────────────
function ManageCategoriesModal({ categories, compass, setCompass, onClose, onCategoryDeleted, uid, goals = [] }) {
  const [cats, setCats]               = useState(categories.map(c => ({ ...c })));
  const originalCatsRef               = useRef(categories.map(c => ({ ...c })));
  const [editingId, setEditingId]     = useState(null);
  const [editLabel, setEditLabel]     = useState("");
  const [editIcon, setEditIcon]       = useState("");
  const [editColor, setEditColor]     = useState("#5B50D9");
  const [addingNew, setAddingNew]     = useState(false);
  const [newLabel, setNewLabel]       = useState("");
  const [newIcon, setNewIcon]         = useState("🎯");
  const [newColor, setNewColor]       = useState("#5B50D9");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [saving, setSaving]           = useState(false);

  // ── dnd-kit sensors (300ms hold to drag on touch, 8px on pointer) ──
  const catSensors = useDragSensors();

  const onCatDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = cats.findIndex(c => c.id === active.id);
    const newIndex  = cats.findIndex(c => c.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove([...cats], oldIndex, newIndex);
    setCats(reordered);
    setCompass(p => ({ ...p, categories: reordered }));
  };

  const hasContent = (catId) => {
    const cat = (compass?.categories || []).find(c => c.id === catId);
    if (!cat) return false;
    return ["shortterm","medterm","longterm","habits","traps","gratitude","mindset"]
      .some(k => (cat[k] || []).length > 0);
  };

  const inTopGoals = (catId) =>
    (compass?.topGoals || []).some(k => k.startsWith(`${catId}:`));

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditLabel(cat.label);
    setEditIcon(cat.icon || "🎯");
    setEditColor(resolveColorHex(cat.color));
    setAddingNew(false);
  };

  // Autosave helpers — called on blur (label) or immediate tap (icon, color)
  const applyEdit = (overrides = {}) => {
    setCats(prev => {
      const label  = (overrides.label  ?? editLabel).trim();
      const icon   = overrides.icon   ?? editIcon;
      const color  = overrides.color  ?? editColor;
      if (!label) return prev;
      const updated = prev.map(c => c.id === editingId ? { ...c, label, icon, color } : c);
      setCompass(p => ({ ...p, categories: updated }));
      return updated;
    });
  };

  const onLabelBlur = () => applyEdit();

  const onIconChange = (icon) => {
    setEditIcon(icon);
    applyEdit({ icon });
  };

  const onColorChange = (color) => {
    setEditColor(color);
    applyEdit({ color });
  };

  const deleteCategory = (catId) => {
    const newCats = cats.filter(c => c.id !== catId);
    setCats(newCats);
    setConfirmDeleteId(null);
  };

  const addCategory = () => {
    if (!newLabel.trim()) return;
    const newCat = makeEmptyCategory(`custom_${Date.now()}`, newLabel.trim(), newIcon, newColor);
    const updated = [...cats, newCat];
    setCats(updated);
    setCompass(p => ({ ...p, categories: updated }));
    setNewLabel(""); setNewIcon("🎯"); setNewColor("#5B50D9");
    setAddingNew(false);
  };

  const saveAll = async () => {
    setSaving(true);
    const deletedIds = new Set(categories.map(c => c.id).filter(id => !cats.find(c => c.id === id)));
    const newCategories = cats.map(c => {
      const existing = (compass?.categories || []).find(e => e.id === c.id);
      return existing ? { ...existing, label: c.label, icon: c.icon, color: c.color || "#5B50D9" } : c;
    });
    if (uid && goals.length > 0) {
      const colorChanges = new Map();
      newCategories.forEach(nc => {
        const orig = originalCatsRef.current.find(e => e.id === nc.id);
        if (orig) {
          const oldHex = resolveColorHex(orig.color);
          const newHex = resolveColorHex(nc.color);
          if (oldHex !== newHex) colorChanges.set(nc.id, { oldHex, newHex });
        }
      });
      if (colorChanges.size > 0) {
        const batch = writeBatch(db);
        goals.forEach(g => {
          const change = g.categoryId && colorChanges.get(g.categoryId);
          if (change && !g.colorCustomized) {
            batch.set(doc(db, "users", uid, "goals", g.id),
              { color: change.newHex, updatedAt: serverTimestamp() }, { merge: true });
          }
        });
        await batch.commit().catch(console.error);
      }
    }
    const newTopGoals = (compass?.topGoals || []).filter(k => !deletedIds.has(k.split(":")[0]));
    await setCompass(p => ({ ...p, categories: newCategories, topGoals: newTopGoals }));
    deletedIds.forEach(id => onCategoryDeleted(id));
    setSaving(false);
    onClose();
  };

  const ColorSwatches = ({ value, onChange }) => (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
      {PRESET_GOAL_COLORS.map(hex => (
        <div key={hex} onClick={() => onChange(hex)} style={{
          width: 28, height: 28, borderRadius: "50%", background: goalCardBg(hex),
          border: value === hex ? `3px solid ${C.text}` : `3px solid transparent`,
          boxShadow: value === hex ? `0 0 0 2px ${hex}` : "none",
          cursor: "pointer", flexShrink: 0, transition: "all 0.15s",
        }} />
      ))}
    </div>
  );

  const EmojiPicker = ({ value, onChange }) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
      {CATEGORY_EMOJIS.map(e => (
        <div key={e} onClick={() => onChange(e)}
          style={{ width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: C.rsm, cursor: "pointer", fontSize: 23, background: value === e ? C.accentLight : C.surface2, border: `1.5px solid ${value === e ? C.accent : C.border}` }}>
          {e}
        </div>
      ))}
    </div>
  );

  // Sortable row wrapper using dnd-kit
  const SortableCatRow = ({ cat }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: cat.id });
    return (
      <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}>
        <div {...attributes} {...listeners}
          style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: `1px solid ${C.border}`, cursor: "grab", touchAction: "none", userSelect: "none" }}>
          <span style={{ color: C.textDim, fontSize: 18, flexShrink: 0 }}>⠿</span>
          <span style={{ fontSize: 18, flexShrink: 0 }}>{cat.icon}</span>
          <div style={{ width: 18, height: 18, borderRadius: "50%", background: goalCardBg(cat.color || "#5B50D9"), flexShrink: 0, border: `1.5px solid ${C.border}` }} />
          <span style={{ flex: 1, fontSize: 15, fontWeight: 500, color: C.text }}>{cat.label}</span>
          <div onClick={e => { e.stopPropagation(); startEdit(cat); }} style={{ fontSize: 13, color: C.accent, cursor: "pointer", fontWeight: 600, padding: "4px 8px" }}>Edit</div>
          <div onClick={e => { e.stopPropagation(); setConfirmDeleteId(cat.id); }} style={{ fontSize: 13, color: C.red, cursor: "pointer", fontWeight: 600, padding: "4px 8px" }}>Delete</div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 300 }} />
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 301, maxWidth: PAGE_WIDTH, margin: "0 auto", background: C.surface, borderRadius: "20px 20px 0 0", padding: "20px 20px 32px", maxHeight: "85vh", display: "flex", flexDirection: "column", animation: "slideUp 0.25s ease" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexShrink: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>Manage Categories</div>
          <div onClick={onClose} style={{ fontSize: 20, color: C.textDim, cursor: "pointer", padding: "0 4px" }}>✕</div>
        </div>

        {/* Category list */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          <DndContext sensors={catSensors} collisionDetection={closestCenter} onDragEnd={onCatDragEnd}>
            <SortableContext items={cats.map(c => c.id)} strategy={verticalListSortingStrategy}>
              {cats.map(cat => (
                <div key={cat.id}>
                  {editingId === cat.id ? (
                    /* Edit panel — autosaves on blur/tap, Done just closes */
                    <div style={{ background: C.accentLight, border: `1px solid ${C.accent}`, borderRadius: C.r, padding: 14, marginBottom: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.accent }}>Edit category</div>
                        <button onClick={() => setEditingId(null)}
                          style={{ background: C.accent, color: "white", border: "none", borderRadius: C.rsm, padding: "5px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                          Done
                        </button>
                      </div>
                      <input
                        value={editLabel}
                        onChange={e => setEditLabel(e.target.value)}
                        onBlur={onLabelBlur}
                        style={{ width: "100%", background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "9px 12px", fontFamily: "inherit", fontSize: 14, color: C.text, outline: "none", boxSizing: "border-box", marginBottom: 4 }}
                      />
                      <EmojiPicker value={editIcon} onChange={onIconChange} />
                      <div style={{ fontSize: 11, fontWeight: 700, color: C.textDim, letterSpacing: "0.06em", textTransform: "uppercase", marginTop: 12, marginBottom: 2 }}>Colour</div>
                      <ColorSwatches value={editColor} onChange={onColorChange} />
                    </div>
                  ) : confirmDeleteId === cat.id ? (
                    <div style={{ background: C.redLight, border: `1px solid ${C.red}`, borderRadius: C.r, padding: 14, marginBottom: 8 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.red, marginBottom: 6 }}>Delete "{cat.label}"?</div>
                      {(hasContent(cat.id) || inTopGoals(cat.id)) && (
                        <div style={{ fontSize: 13, color: C.red, marginBottom: 8, lineHeight: 1.5 }}>
                          {hasContent(cat.id) && "This category has goals, habits or temptations that will also be deleted. "}
                          {inTopGoals(cat.id) && "Some of its goals are in your Top 10 and will be removed."}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={() => deleteCategory(cat.id)} style={{ flex: 1, background: C.red, color: "white", border: "none", borderRadius: C.rsm, padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Delete</button>
                        <button onClick={() => setConfirmDeleteId(null)} style={{ background: C.surface2, color: C.textMid, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "10px 14px", fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <SortableCatRow cat={cat} />
                  )}
                </div>
              ))}
            </SortableContext>
          </DndContext>

          {/* Add new category */}
          {addingNew ? (
            <div style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.r, padding: 14, marginTop: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.textMid, marginBottom: 8 }}>New category</div>
              <input value={newLabel} onChange={e => setNewLabel(e.target.value)}
                placeholder="Category name" autoFocus
                style={{ width: "100%", background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "9px 12px", fontFamily: "inherit", fontSize: 14, color: C.text, outline: "none", boxSizing: "border-box", marginBottom: 4 }} />
              <EmojiPicker value={newIcon} onChange={setNewIcon} />
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textDim, letterSpacing: "0.06em", textTransform: "uppercase", marginTop: 12, marginBottom: 2 }}>Colour</div>
              <ColorSwatches value={newColor} onChange={setNewColor} />
            </div>
          ) : (
            <div onClick={() => { setAddingNew(true); setEditingId(null); }}
              style={{ fontSize: 14, color: C.accent, fontWeight: 600, cursor: "pointer", padding: "12px 0", display: "flex", alignItems: "center", gap: 6 }}>
              + Add category
            </div>
          )}
        </div>

        {/* Footer buttons */}
        <div style={{ display: "flex", gap: 10, marginTop: 16, flexShrink: 0 }}>
          <button onClick={() => { setAddingNew(false); setNewLabel(""); setNewIcon("🎯"); onClose(); }}
            style={{ flex: 1, background: C.surface2, color: C.textMid, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "13px 0", fontSize: 15, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          {addingNew ? (
            <button onClick={addCategory} disabled={!newLabel.trim()}
              style={{ flex: 2, background: newLabel.trim() ? C.accent : C.surface2, color: newLabel.trim() ? "white" : C.textDim, border: "none", borderRadius: C.rsm, padding: "13px 0", fontSize: 15, fontWeight: 700, cursor: newLabel.trim() ? "pointer" : "default", fontFamily: "inherit" }}>
              Add Category
            </button>
          ) : (
            <button onClick={saveAll} disabled={saving}
              style={{ flex: 2, background: C.accent, color: "white", border: "none", borderRadius: C.rsm, padding: "13px 0", fontSize: 15, fontWeight: 700, cursor: saving ? "default" : "pointer", fontFamily: "inherit", opacity: saving ? 0.7 : 1 }}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
          )}
        </div>

      </div>
    </>
  );
}

// ─── TASK SEARCH MODAL ───────────────────────────────────────────────────────
function SearchModal({ tasks, compass, setCompass, uid, onClose, settings, goals = [], onGoalChat }) {
  const [query,        setQuery]        = useState("");
  const [detailTaskId, setDetailTaskId] = useState(null);
  const [detailGoal,   setDetailGoal]   = useState(null);
  const detailTask = detailTaskId ? tasks.find(t => t.id === detailTaskId) : null;
  const categories = compass?.categories || [];
  const TIER_LABELS = { shortterm: "Short-term", medterm: "Medium-term", longterm: "Long-term", inbox: "Someday" };

  const q = query.trim().toLowerCase();
  const taskResults = q.length < 1 ? [] : tasks.filter(t => t.text?.toLowerCase().includes(q));
  const goalResults = q.length < 1 ? [] : goals.filter(g =>
    g.title?.toLowerCase().includes(q) || g.description?.toLowerCase().includes(q)
  );
  const hasResults = taskResults.length > 0 || goalResults.length > 0;

  const getCat = (id) => categories.find(c => c.id === id) || null;

  return (
    <div style={{ position: "fixed", inset: 0, background: C.bg, zIndex: 490, display: "flex", flexDirection: "column" }}>
      {detailTask && (
        <TaskEditModal
          task={detailTask}
          uid={uid}
          compass={compass}
          tasks={tasks}
          goals={goals}
          settings={settings}
          onClose={() => setDetailTaskId(null)}
        />
      )}
      {detailGoal && (
        <GoalDetailPage
          goal={detailGoal}
          uid={uid}
          compass={compass}
          setCompass={setCompass}
          tasks={tasks}
          goals={goals}
          settings={settings}
          onBack={() => setDetailGoal(null)}
          backLabel="Back to Search"
          onGoalChat={onGoalChat}
        />
      )}

      <div style={{ display: (detailTask || detailGoal) ? "none" : "contents" }}>
        <div style={{ padding: `16px ${columnPadding()} 12px`, borderBottom: `1px solid ${C.border}`, flexShrink: 0, display: "flex", alignItems: "center", gap: 10 }}>
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search goals and tasks..."
            style={{ flex: 1, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "10px 14px", fontFamily: "inherit", fontSize: 15, color: C.text, outline: "none", boxSizing: "border-box" }}
          />
          <button onClick={onClose}
            style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: "50%", width: 36, height: 36, fontSize: 16, cursor: "pointer", color: C.textMid, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: `10px ${columnPadding()} 24px` }}>
          {q.length === 0 && (
            <div style={{ fontSize: 14, color: C.textDim, fontStyle: "italic", marginTop: 24, textAlign: "center" }}>Search goals and tasks</div>
          )}
          {q.length > 0 && !hasResults && (
            <div style={{ fontSize: 14, color: C.textDim, fontStyle: "italic", marginTop: 24, textAlign: "center" }}>No results found</div>
          )}

          {/* ── GOALS ── */}
          {goalResults.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textDim, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 8, marginTop: 4 }}>Goals</div>
              {goalResults.map(g => {
                const cat = getCat(g.categoryId);
                const badge = g.deadline ? deadlineBadge(g.deadline, C.textDim) : null;
                return (
                  <div key={g.id} onClick={() => setDetailGoal(g)}
                    style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", marginBottom: 8, background: goalCardBg(g.color), borderRadius: C.r, cursor: "pointer", boxShadow: C.shadow, opacity: g.status !== "active" ? 0.6 : 1 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "#fff", lineHeight: 1.4 }}>{g.title}</div>
                      <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap", alignItems: "center" }}>
                        {cat && <span style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>{cat.icon} {cat.label}</span>}
                        {badge && <span style={{ fontSize: 12, color: "rgba(255,255,255,0.8)", fontWeight: 600 }}>· {formatDeadlineDate(g.deadline)} · {badge.label}</span>}
                        {g.status !== "active" && <span style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", textTransform: "capitalize" }}>· {g.status}</span>}
                      </div>
                    </div>
                    <span style={{ fontSize: 18, color: "rgba(255,255,255,0.6)", paddingTop: 1, flexShrink: 0 }}>›</span>
                  </div>
                );
              })}
            </>
          )}

          {/* ── TASKS ── */}
          {taskResults.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textDim, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 8, marginTop: goalResults.length > 0 ? 16 : 4 }}>Tasks</div>
              {taskResults.map(t => {
                const cat = getCat(t.categoryId);
                const tierLabel = TIER_LABELS[t.tier] || null;
                const badge = t.deadline ? deadlineBadge(t.deadline, C.textDim) : null;
                return (
                  <div key={t.id} onClick={() => setDetailTaskId(t.id)}
                    style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", marginBottom: 8, background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.r, cursor: "pointer", boxShadow: C.shadow, opacity: t.done ? 0.6 : 1 }}>
                    <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, marginTop: 2, background: t.done ? C.accent : "transparent", border: `2px solid ${t.done ? C.accent : C.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {t.done && <span style={{ color: "white", fontSize: 12, fontWeight: 700, lineHeight: 1 }}>✓</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: t.done ? C.textDim : C.text, lineHeight: 1.4, textDecoration: t.done ? "line-through" : "none" }}>{t.text}</div>
                      <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap", alignItems: "center" }}>
                        {cat && <span style={{ fontSize: 12, color: C.textDim, fontWeight: 600 }}>{cat.icon} {cat.label}</span>}
                        {tierLabel && <span style={{ fontSize: 12, color: C.textDim }}>· {tierLabel}</span>}
                        {badge && <span style={{ fontSize: 12, color: badge.color, fontWeight: 600 }}>· {formatDeadlineDate(t.deadline)} · {badge.label}</span>}
                      </div>
                    </div>
                    <span style={{ fontSize: 18, color: C.textDim, paddingTop: 1, flexShrink: 0 }}>›</span>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── GOAL DETAIL PAGE ────────────────────────────────────────────────
function GoalDetailPage({ goal, uid, compass, setCompass, tasks, goals, settings, onBack, backLabel = "Back", onClose, onGoalChat }) {
  const phaseAdd = usePhaseAdd((taskId, fields) => writeTask(uid, taskId, fields), phaseNow(settings));
  const [showEdit,       setShowEdit]       = useState(false);
  const [editTask,       setEditTask]       = useState(null);
  // Greys a task out while its tick saves, as every task list does (useTickTasks).
  const { isTicked, tick } = useTickTasks((t) => writeTask(uid, t.id, { done: true }));
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [detailSort,     setDetailSort]     = useState("date");
  const [doneOpen,       setDoneOpen]       = useState(false);

  const sensors = useDragSensors();
  // The goal's tasks are every list's task cards (Craig, 29 Oct 2026: "Yes, I like that idea"), without the goal, whose
  // page this is. The editor is this page's own: the page opens over Search and Radar, and the app's shared editor would
  // draw beneath it.
  const linksOf = useTaskLinksOf();
  const taskCard = (t, handle = null) => (
    <TaskListRow key={t.id} task={t} links={linksOf(t)} done={isTicked(t.id)} onDone={tick} onOpen={setEditTask}
      onAddToday={phaseAdd.addNow} handle={handle} />
  );

  const tok = goalColorTokens(goal.color);
  const cat = (compass?.categories || []).find(c => c.id === goal.categoryId);
  const goalKeyDates = keyDatesForGoal(useKeyDatesList(), goal.id);
  const [editKeyDate, setEditKeyDate] = useState(null);
  const TIMEFRAME_LABEL = { short: "Short-term", medium: "Medium-term", long: "Long-term" };

  const linkedActive = tasksForGoal(tasks, goal.id)
    .filter(t => !t.done)
    .sort((a, b) => {
      if (detailSort === "manual") return goalTaskSort(a, b);
      // "date": deadline asc, then alpha
      if (a.deadline && b.deadline) return a.deadline.localeCompare(b.deadline);
      if (a.deadline) return -1;
      if (b.deadline) return 1;
      return (a.text || "").localeCompare(b.text || "");
    });
  const linkedDone = tasksForGoal(tasks, goal.id).filter(t => t.done);

  // Seed goalTaskOrder for unordered tasks when entering manual sort
  useEffect(() => {
    if (detailSort !== "manual" || !uid || linkedActive.length === 0) return;
    const unordered = linkedActive.filter(t => t.goalTaskOrder == null);
    if (unordered.length === 0) return;
    const maxOrder = linkedActive.reduce((max, t) => Math.max(max, t.goalTaskOrder ?? 0), 0);
    const seedBatch = writeBatch(db);
    unordered.forEach((t, i) => {
      seedBatch.set(doc(db, "users", uid, "tasks", t.id),
        { goalTaskOrder: maxOrder + i + 1, updatedAt: serverTimestamp() }, { merge: true });
    });
    seedBatch.commit().catch(console.error);
  }, [detailSort, goal.id, uid, linkedActive.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDragEndGoalTasks = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = linkedActive.findIndex(t => t.id === active.id);
    const newIndex  = linkedActive.findIndex(t => t.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove([...linkedActive], oldIndex, newIndex);
    const batch = writeBatch(db);
    reordered.forEach((t, i) => {
      batch.set(doc(db, "users", uid, "tasks", t.id),
        { goalTaskOrder: i + 1, updatedAt: serverTimestamp() }, { merge: true });
    });
    await batch.commit().catch(console.error);
  };

  const CTA_BUTTONS = [
    { label: "📋  Make a Plan",          mode: "plan"          },
    { label: "😓  I'm Procrastinating",  mode: "procrastinate" },
    { label: "⚡  Motivate Me",           mode: "motivate"      },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", background: C.bg }}>

      {/* Header */}
      <PageHeader title="Goal" onBack={onBack} backLabel={backLabel} onClose={onClose} closeLabel="Close this goal">
        <button onClick={() => setShowEdit(true)}
          style={{ minHeight: 44, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "8px 16px", fontSize: 14, fontWeight: 600, color: C.text, cursor: "pointer", fontFamily: "inherit" }}>
          Edit
        </button>
      </PageHeader>

      <div style={{ flex: 1, overflowY: "auto", paddingBottom: 48 }}>

        {/* Colour hero band */}
        <div style={{ background: goalCardBg(goal.color), minHeight: 170, overflow: "hidden", marginBottom: 2 }}>
          <div style={{ padding: `24px ${columnPadding()} 20px` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.55)", letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 8 }}>
              {[cat?.label, TIMEFRAME_LABEL[goal.timeframe]].filter(Boolean).join(" · ")}
            </div>
            <div style={{ fontWeight: 700, fontSize: 24, color: "#fff", lineHeight: 1.3, marginBottom: 14 }}>{goal.title}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#fff", background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 20, padding: "4px 12px" }}>
                {goal.status.charAt(0).toUpperCase() + goal.status.slice(1)}
              </span>
              <DueDatePill deadline={goal.deadline} onChange={(deadline) => updateGoal(uid, goal.id, { deadline })} />
              {goal.inTop3Goals && (
                <span style={{ fontSize: 12, fontWeight: 700, color: "#fff", background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 20, padding: "4px 12px" }}>
                  ⭐ Top 3 Goal
                </span>
              )}
            </div>
          </div>
        </div>

        <div style={pageColumn}>
        {/* Description */}
        <div style={{ margin: "12px 20px", padding: "14px 16px", background: C.surface, border: `1px solid ${C.borderCard}`, borderRadius: C.r, borderLeft: `4px solid ${tok.main}` }}>
          {goal.description
            ? <div style={{ fontSize: 15, color: C.textMid, fontStyle: "italic", lineHeight: 1.6 }}>"{goal.description}"</div>
            : <div onClick={() => setShowEdit(true)} style={{ fontSize: 14, color: C.textDim, fontStyle: "italic", lineHeight: 1.6, cursor: "pointer" }}>Why does this goal matter to you? Tap Edit to add a description.</div>
          }
        </div>

        {/* Key Dates for this goal: chosen on the Key Date, shown here too so the link is visible both ways */}
        {goalKeyDates.length > 0 && (
          <section aria-labelledby="goal-key-dates" style={{ margin: "12px 20px" }}>
            <h2 id="goal-key-dates" style={{ margin: "0 4px 8px", fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: C.textMid }}>Key dates</h2>
            <div style={{ background: C.surface, border: `1px solid ${C.borderCard}`, borderRadius: C.r, overflow: "hidden" }}>
              {goalKeyDates.map((k, i) => (
                <KeyDateLine key={k.id} keyDate={k} tasks={tasksForKeyDate(tasks, k.id)} past={k.date < todayKey()} first={i === 0} onEdit={() => setEditKeyDate(k)} />
              ))}
            </div>
          </section>
        )}
        {editKeyDate && (
          <KeyDateEditor keyDate={editKeyDate} goals={goals} taskCount={tasksForKeyDate(tasks, editKeyDate.id).length}
            onSave={(fields) => updateKeyDate(uid, editKeyDate.id, unchangedDateLeftOut(fields, editKeyDate))}
            onDelete={() => deleteKeyDate(uid, editKeyDate.id, tasksForKeyDate(tasks, editKeyDate.id).map(t => t.id))}
            onClose={() => setEditKeyDate(null)} />
        )}

        <GoalHabits goal={goal} goals={goals} compass={compass} setCompass={setCompass} />

        {/* Chat CTAs — only where the page can open Chat */}
        {onGoalChat && <div style={{ margin: "20px 20px 8px" }}>
          <h2 style={{ ...sectionLabel(), marginBottom: 10 }}>Take action</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {CTA_BUTTONS.map(({ label, mode }) => (
              <button key={mode} onClick={() => onGoalChat(goal, mode)}
                style={{ width: "100%", padding: "14px 18px", background: C.surface, border: `1px solid ${tok.main}`, borderRadius: C.r, fontSize: 15, fontWeight: 600, color: tok.main, cursor: "pointer", fontFamily: "inherit", textAlign: "left", boxShadow: C.shadow }}>
                {label}
              </button>
            ))}
          </div>
        </div>}

        {/* Linked Tasks */}
        <div style={{ margin: "20px 20px 8px" }}>
          {/* The goal's tasks as every list's task cards (Craig, 30 Oct 2026: "Yes, I like that idea"), in the order chosen
              here, then "+ Add a task", then the done ones folded. */}
          <h2 style={{ ...sectionLabel(), marginBottom: 10 }}>Tasks</h2>
          {linkedActive.length > 1 && (
            <div style={{ marginBottom: 12 }}>
              <Segmented label="Order the tasks" stretch options={[["date", "By deadline"], ["manual", "Your order"]]} value={detailSort} onChange={setDetailSort} />
            </div>
          )}
          {detailSort === "manual" ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndGoalTasks}>
              <SortableContext items={linkedActive.map(t => t.id)} strategy={verticalListSortingStrategy}>
                {linkedActive.map(t => (
                  <Sortable key={t.id} id={t.id}>{(drag) => taskCard(t, <DragHandle label={t.text} {...drag} />)}</Sortable>
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            linkedActive.map(t => taskCard(t))
          )}
          <AddItemButton label={`Add a task for ${goal.title}`} onClick={() => setShowCreateTask(true)} />
          {linkedDone.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <button onClick={() => setDoneOpen(o => !o)} aria-expanded={doneOpen}
                style={{ ...sectionLabel(), width: "100%", minHeight: 44, display: "flex", alignItems: "center", justifyContent: "space-between", padding: 0, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
                Done ({linkedDone.length}) <span aria-hidden="true" style={{ fontSize: 11 }}>{doneOpen ? "▲" : "▼"}</span>
              </button>
              {doneOpen && <DoneTaskList tasks={linkedDone} onUndo={(t) => writeTask(uid, t.id, { done: false })} onOpen={setEditTask} />}
            </div>
          )}
        </div>
        </div>

        {/* Status — Park / Complete / Reactivate available in Edit modal */}
      </div>

      {/* Edit modal */}
      {showEdit && (
        <GoalCreateModal uid={uid} compass={compass} tasks={tasks} existingGoal={goal} onClose={() => setShowEdit(false)}
          onDeleted={() => { if (habitsForGoal(compass?.categories, goal.id).length) setCompass(p => ({ ...p, categories: unlinkGoalFromHabits(p.categories, goal.id) })); }} />
      )}

      {/* Linked task edit modal */}
      {editTask && (
        <TaskEditModal task={editTask} uid={uid} compass={compass} tasks={tasks} goals={goals} settings={settings} onClose={() => setEditTask(null)} />
      )}

      {/* New task pre-linked to this goal */}
      {showCreateTask && (
        <TaskEditModal
          newTaskDefaults={newTaskForGoal(goal, tasks)}
          uid={uid}
          compass={compass}
          tasks={tasks}
          goals={goals}
          settings={settings}
          onClose={() => setShowCreateTask(false)}
        />
      )}
      {phaseAdd.toast}
    </div>
  );
}

// ─── GOAL CREATE / EDIT MODAL ────────────────────────────────────────
function GoalCreateModal({ uid, compass, tasks, existingGoal, onClose, onDeleted }) {
  const isEdit = !!existingGoal;
  const [title,          setTitle]          = useState(existingGoal?.title       ?? "");
  const [categoryId,     setCategoryId]     = useState(existingGoal?.categoryId  ?? null);
  const [colorOverride,  setColorOverride]  = useState(
    // On edit: detect if goal color differs from its category's color (= was customized)
    existingGoal
      ? (() => {
          const catHex = compass?.categories?.find(c => c.id === existingGoal.categoryId)?.color;
          const goalHex = resolveColorHex(existingGoal.color);
          return catHex && resolveColorHex(catHex) !== goalHex ? goalHex : null;
        })()
      : null
  );
  const [deadline,    setDeadline]    = useState(existingGoal?.deadline    ?? "");
  const [description, setDescription] = useState(existingGoal?.description ?? "");
  const [saving,      setSaving]      = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const categories = compass?.categories || [];

  const save = async () => {
    if (!title.trim() || !categoryId) return;
    setSaving(true);
    const catHex = categories.find(c => c.id === categoryId)?.color;
    const resolvedColor = colorOverride || resolveColorHex(catHex) || "#5B50D9";
    const fields = {
      title: title.trim(),
      color: resolvedColor,
      colorCustomized: !!colorOverride,
      categoryId: categoryId || null,
      deadline:   deadline   || null,
      description: description.trim() || null,
    };
    if (isEdit) {
      await updateGoal(uid, existingGoal.id, fields);
    } else {
      await createGoal(uid, fields);
    }
    setSaving(false);
    onClose();
  };

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setDeleteError(null);
    try {
      await deleteGoal(uid, existingGoal.id, tasks);
      // Its habits lose the link too. They live on the compass, which is saved whole from here rather than in the batch.
      onDeleted?.();
      onClose();
    } catch (e) {
      console.error("Deleting a goal failed:", e);
      setDeleteError("We couldn't delete this goal. Check your connection and try again.");
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", flexDirection: "column", justifyContent: "flex-end", background: "rgba(0,0,0,0.5)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: "100%", maxWidth: PAGE_WIDTH, margin: "0 auto", boxSizing: "border-box", background: C.surface, borderRadius: "20px 20px 0 0", padding: "28px 20px 40px", maxHeight: "90vh", overflowY: "auto" }}>

        {/* Handle bar */}
        <div style={{ width: 36, height: 4, borderRadius: 2, background: C.border, margin: "0 auto 24px" }} />

        {/* Title */}
        <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 20 }}>
          {isEdit ? "Edit Goal" : "New Goal"}
        </div>

        {/* Goal title input */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.textDim, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>Goal</div>
          <input
            autoFocus
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="What do you want to achieve?"
            style={{ width: "100%", background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "12px 14px", fontSize: 16, color: C.text, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
          />
        </div>

        {/* Category — required — determines the goal colour */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.textDim, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>Category</div>
          <select value={categoryId || ""} onChange={e => { setCategoryId(e.target.value || null); setColorOverride(null); }}
            style={{ width: "100%", background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "11px 14px", fontSize: 15, color: categoryId ? C.text : C.textDim, fontFamily: "inherit", outline: "none" }}>
            <option value="" disabled>Select a category…</option>
            {categories.map(cat => <option key={cat.id} value={cat.id}>{categoryLabel(cat)}</option>)}
          </select>
          {/* Colour preview + override */}
          {categoryId && (() => {
            const catHex = resolveColorHex(categories.find(c => c.id === categoryId)?.color);
            const activeHex = colorOverride || catHex;
            return (
              <div style={{ marginTop: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: goalCardBg(activeHex), flexShrink: 0, border: `2px solid ${C.border}` }} />
                  <div style={{ fontSize: 13, color: C.textMid }}>
                    {colorOverride ? "Custom colour" : "Inherited from category"}
                  </div>
                  {colorOverride && (
                    <span onClick={() => setColorOverride(null)}
                      style={{ fontSize: 12, color: C.accent, fontWeight: 600, cursor: "pointer", marginLeft: "auto" }}>
                      Reset
                    </span>
                  )}
                </div>
                {/* Expand to show swatches + custom picker */}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                  {PRESET_GOAL_COLORS.map(hex => (
                    <div key={hex} onClick={() => setColorOverride(hex === catHex ? null : hex)} style={{
                      width: 26, height: 26, borderRadius: "50%",
                      background: goalCardBg(hex),
                      border: activeHex === hex ? `3px solid ${C.text}` : `3px solid transparent`,
                      boxShadow: activeHex === hex ? `0 0 0 2px ${hex}` : "none",
                      cursor: "pointer", flexShrink: 0, transition: "all 0.15s",
                    }} />
                  ))}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Description — moved above deadline, encouraged not optional */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.textDim, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>Why does this goal matter?</div>
          <textarea value={description} onChange={e => setDescription(e.target.value)}
            placeholder="What does achieving this mean to you? How will it change your life?"
            rows={3}
            style={{ width: "100%", background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "11px 14px", fontSize: 15, color: C.text, fontFamily: "inherit", outline: "none", resize: "vertical", boxSizing: "border-box" }} />
        </div>

        {/* Deadline */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textDim, letterSpacing: "0.06em", textTransform: "uppercase" }}>Target date <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></div>
            {deadline && (
              <button onClick={() => setDeadline("")}
                style={{ fontSize: 12, color: C.red, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontWeight: 600, padding: 0 }}>
                Remove
              </button>
            )}
          </div>
          <input type="date" className="date-field" value={deadline} onChange={e => setDeadline(e.target.value)}
            style={{ width: "100%", background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "11px 14px", fontSize: 15, color: deadline ? C.text : C.textDim, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
        </div>

        {/* Save button */}
        <button onClick={save} disabled={!title.trim() || !categoryId || saving}
          style={{ width: "100%", padding: "15px 0", background: (title.trim() && categoryId) ? (colorOverride || resolveColorHex(categories.find(c => c.id === categoryId)?.color)) : C.surface2, color: (title.trim() && categoryId) ? "#fff" : C.textDim, border: "none", borderRadius: 30, fontSize: 16, fontWeight: 700, cursor: (title.trim() && categoryId) ? "pointer" : "default", fontFamily: "inherit", marginBottom: isEdit ? 12 : 0, transition: "background 0.15s" }}>
          {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Goal"}
        </button>

        {/* Edit-only actions: Park / Complete / Delete */}
        {isEdit && (
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            {existingGoal.status !== "parked" && (
              <button onClick={() => { updateGoal(uid, existingGoal.id, { status: "parked" }); onClose(); }}
                style={{ flex: 1, padding: "11px 0", background: C.surface2, color: C.textMid, border: `1px solid ${C.border}`, borderRadius: 30, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                Park it
              </button>
            )}
            {existingGoal.status !== "active" && (
              <button onClick={() => { updateGoal(uid, existingGoal.id, { status: "active" }); onClose(); }}
                style={{ flex: 1, padding: "11px 0", background: C.surface2, color: C.textMid, border: `1px solid ${C.border}`, borderRadius: 30, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                Reactivate
              </button>
            )}
            {existingGoal.status !== "complete" && (
              <button onClick={() => { updateGoal(uid, existingGoal.id, { status: "complete" }); onClose(); }}
                style={{ flex: 1, padding: "11px 0", background: C.accentLight, color: C.accent, border: `1px solid ${C.accent}`, borderRadius: 30, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                ✓ Done
              </button>
            )}
          </div>
        )}
        {isEdit && (
          <button onClick={handleDelete}
            style={{ width: "100%", padding: "11px 0", background: confirmDelete ? C.red : "transparent", color: confirmDelete ? "#fff" : C.red, border: `1px solid ${C.red}`, borderRadius: 30, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}>
            {confirmDelete ? "Tap again to confirm delete" : "Delete Goal"}
          </button>
        )}
        {deleteError && <p role="alert" style={{ margin: "10px 0 0", fontSize: 15, lineHeight: 1.5, color: C.red }}>{deleteError}</p>}
      </div>
    </div>
  );
}

// A switch in Radar settings: a real button, named for its group, in a 44px tap area around the 26px track. At module
// scope, so toggling it doesn't remount it and lose keyboard focus.
function RadarSwitch({ on, label, onClick }) {
  return (
    <button role="switch" aria-checked={on} aria-label={`Show ${label}`} onClick={onClick}
      style={{ width: 52, height: 44, flexShrink: 0, display: "grid", placeItems: "center", padding: 0, background: "none", border: "none", cursor: "pointer" }}>
      <span aria-hidden="true" style={{ width: 44, height: 26, borderRadius: 13, background: on ? C.accent : C.surface2, border: `1px solid ${on ? C.accent : C.border}`, position: "relative", transition: "background 0.15s", boxSizing: "border-box" }}>
        <span style={{ position: "absolute", top: 2, left: on ? 20 : 2, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left 0.15s" }} />
      </span>
    </button>
  );
}

// ─── KEY DATES ───────────────────────────────────────────────────────
// The Key Dates page (src/features/keydates/). Its tasks open in NudgeApp's task editor (TaskActionsContext).
function KeyDatesScreen({ uid, keyDates, loadError, tasks, goals, onNav, addRequested, onAddHandled }) {
  return (
    <KeyDatesPage uid={uid} keyDates={keyDates} loadError={loadError} tasks={tasks} goals={goals} onNav={onNav}
      addRequested={addRequested} onAddHandled={onAddHandled} />
  );
}

// ─── TASKS ───────────────────────────────────────────────────────────
// The Tasks page (src/features/tasks/). Its tasks open in NudgeApp's task editor.
function TasksScreen({ uid, tasks, goals, compass, setCompass, settings, getSortMode, setSortMode, onNav }) {
  const { openTask } = useTaskActions();
  const view = tasksPageView(getSortMode(TASK_VIEW_SETTING, null), getSortMode(GROUP_BY_SETTING, null));
  return (
    <>
      <TasksPage tasks={tasks} goals={goals} categories={compass?.categories || []} view={view} onView={(v) => setSortMode(TASK_VIEW_SETTING, v)}
        onDone={(task) => writeTask(uid, task.id, { done: true })} onOpenTask={openTask}
        onMoveCategories={(categories) => setCompass(p => ({ ...p, categories }))}
        saveTask={(taskId, fields) => writeTask(uid, taskId, fields)} phase={phaseNow(settings)} onNav={onNav} />
    </>
  );
}

// ─── RADAR PAGE ──────────────────────────────────────────────────────
function RadarPage({ radarCount, onToggleSection, tasks, goals, visions, keyDates, uid, onNav, compass, setCompass, onGoalChat, settings, setSettings, activeNudge, onSnoozeNudge, onGotItNudge, onNextNudge, hasNextNudge, onPinNudge, onUnpinNudge, pinnedNudge, pinnedNudgeId, nudgeHistory }) {
  const [openGoals,      setOpenGoals]      = useState(() => new Set()); // goal bars opened, as "group-goalId"
  const [detailGoalId,   setDetailGoalId]   = useState(null);      // goal open over the page
  const [editorHost,     setEditorHost]     = useState(null);      // where Habits, Dreams and Fears open their editors
  const [drillGoalId,    setDrillGoalId]    = useState(null);      // vision whose goals are expanded
  const [drillVGoalId,   setDrillVGoalId]   = useState(null);      // goal-within-vision whose tasks are expanded
  const [showWhatMattersHelp, setShowWhatMattersHelp] = useState(false);
  const [showSettings,   setShowSettings]   = useState(false);
  const [noNextNudge,    setNoNextNudge]    = useState(false); // Next found nothing due; the card says so until closed
  // A nudge arriving later replaces that message for good, so its Dismiss leaves no card behind.
  useEffect(() => { if (activeNudge) setNoNextNudge(false); }, [activeNudge]);

  // Every group starts closed and opens the way the user left it, on any device (Craig, 23 Oct 2026).
  const groupProps = (id) => ({ id, off: groupsOff.has(id), collapsed: !isOpen(settings, OPEN_PAGES.RADAR, id), onToggle: () => onToggleSection(OPEN_PAGES.RADAR, id) });

  // Starting order and labels: RADAR_GROUPS (signals.js). The order the user drags the groups into is kept in their
  // settings, so it lasts and follows them to any device (Craig, 24 Oct 2026: "Yes, save it to settings").
  const groupOrder = radarGroupOrder(settings?.radarGroupOrder);
  const groupLabel = (id) => RADAR_GROUPS.find(g => g.id === id).label; // one name for the page and its settings
  const groupSensors = useDragSensors();
  const onGroupDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = groupOrder.indexOf(active.id);
    const newIndex = groupOrder.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove([...groupOrder], oldIndex, newIndex);
    setSettings(p => ({ ...p, radarGroupOrder: next }));
  };

  const dueSoonWindow = dueSoonWindowOf(settings);
  const groupsOff     = new Set(settings?.radarGroupsOff || []); // group ids toggled off

  const activeTasks = useMemo(() => (tasks || []).filter(t => !t.done), [tasks]);
  const activeGoals = useMemo(() => (goals || []).filter(g => g.status === "active"), [goals]);

  const overdue         = useMemo(() => overdueTasks(activeTasks), [activeTasks]);
  const dueToday        = useMemo(() => dueTodayTasks(activeTasks), [activeTasks]);
  // From tomorrow: today's are under Tasks Due Today (Craig, 25 Oct 2026: "add a new section called 'Tasks Due Today'").
  const dueSoon         = useMemo(() => dueSoonTasks(activeTasks, dueSoonWindow, 1), [activeTasks, dueSoonWindow]);
  const urgentImportant = useMemo(() => urgentImportantGoals(activeGoals), [activeGoals]);
  const mostImportant   = useMemo(() => mostImportantGoals(activeGoals), [activeGoals]);
  // "Key Dates Coming Up" shows the dates themselves, as the Key Dates page's cards (Craig, 24 Oct 2026); their tasks
  // still count toward "N tasks on my radar today".
  const keyDatesNow     = useMemo(() => keyDatesToGetReadyFor(keyDates || []), [keyDates]);
  // A goal's tasks in the Goals page's order.
  const openInGoalOrder = useMemo(() => [...activeTasks].sort(byDeadline(goals)), [activeTasks, goals]);

  const whatMattersMost = useMemo(() => (VISION_SHOWN ? buildWhatMattersMost(visions, activeGoals) : []), [visions, activeGoals]);

  // Tasks as the Tasks page draws them (Craig, 25 Oct 2026: "change the display of tasks to look like they do on the
  // Tasks page"): the circle ticks one off, its goal and deadline under it, and the Key Date, dream and fear it's for.
  // Under a dream, fear or Key Date its own name is left out. Add to today's Undo message says where it went.
  const tasksKit = useTaskRows(goals);
  const taskRow = tasksKit.row;
  const keyDateCards = useKeyDateCards({ uid, tasks, goals, taskRows: tasksKit });

  // Goals as the Goals page draws them (Craig, 25 Oct 2026: "change the display of goals (and their related tasks) to
  // look the way they do on the Goals page"). A goal can be in both goal groups, so each opens on its own.
  const goalBlock = (groupId, goal) => {
    const key = `${groupId}-${goal.id}`;
    return (
      <GoalBlock key={goal.id} goal={goal} words={taskWords(tasksForGoal(tasks, goal.id))}
        openTasks={openTasksOfGoal(openInGoalOrder, goal.id)} open={openGoals.has(key)}
        onToggle={() => setOpenGoals(s => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; })}
        onOpenGoal={() => setDetailGoalId(goal.id)} onAddTask={() => tasksKit.newTask(newTaskForGoal(goal, tasks))}
        row={(t) => taskRow(t, { withGoal: false })} />
    );
  };

  const VisionDrillRow = ({ entry }) => {
    const { vision, topGoals } = entry;
    const open = drillGoalId === `v-${vision.id}`;
    const label = topGoals.length === 1
      ? `Behind your top goal: ${topGoals[0].title}`
      : `Behind ${topGoals.length} of your top goals`;
    return (
      <div style={{ borderBottom: `1px solid ${C.border}` }}>
        <div onClick={() => setDrillGoalId(open ? null : `v-${vision.id}`)}
          style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", cursor: "pointer" }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, marginTop: 5, background: "#7F77DD" }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: C.text, lineHeight: 1.35 }}>{vision.text}</div>
            <div style={{ fontSize: 13, marginTop: 2, color: "#9A92E6" }}>{label}</div>
          </div>
          <span style={{ flexShrink: 0, fontSize: 16, color: C.textDim, transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>›</span>
        </div>
        {open && (
          <div style={{ paddingLeft: 18, paddingBottom: 4 }}>
            {topGoals.map(g => {
              const gTasks = openTasksOfGoal(openInGoalOrder, g.id);
              const gKey = `${vision.id}-${g.id}`;
              const gOpen = drillVGoalId === gKey;
              const gTok = goalColorTokens(g.color);
              return (
                <div key={g.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <div onClick={() => setDrillVGoalId(gOpen ? null : gKey)}
                    style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 14px", cursor: "pointer" }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, marginTop: 5, background: gTok.main }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 16, fontWeight: 600, color: C.text, lineHeight: 1.35 }}>{g.title}</div>
                      <div style={{ fontSize: 13, marginTop: 2, color: C.textDim }}>Goal · {gTasks.length} task{gTasks.length !== 1 ? "s" : ""}</div>
                    </div>
                    <span style={{ flexShrink: 0, fontSize: 16, color: C.textDim, transform: gOpen ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>›</span>
                  </div>
                  {gOpen && (
                    <div style={{ paddingLeft: 18, paddingBottom: 4 }}>
                      {gTasks.length === 0
                        ? <div style={{ padding: "6px 14px 12px", fontSize: 13, color: C.textDim, fontStyle: "italic" }}>No active tasks under this goal.</div>
                        : gTasks.map(t => taskRow(t, { withGoal: false }))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Only groups that are on: with every group that has something switched off, the page says so instead of going blank.
  const groupSizes = { overdue: overdue.length, duetoday: dueToday.length, duesoon: dueSoon.length, keydates: keyDatesNow.length,
    urgentimportant: urgentImportant.length, mostimportant: mostImportant.length, whatmatters: whatMattersMost.length };
  const totalSurfaced = Object.entries(groupSizes).reduce((n, [id, size]) => n + (groupsOff.has(id) ? 0 : size), 0);
  // "N tasks on my radar today": NudgeApp counts it once for this header and the sidebar (radarTodayCount).
  const taskCount = radarCount;
  const detailGoal = detailGoalId && (goals || []).find(g => g.id === detailGoalId);

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", background: C.bg, position: "relative" }}>

      {keyDateCards.editor}
      <div ref={setEditorHost} />
      <GoalPageOverlay goal={detailGoal} onClose={() => setDetailGoalId(null)} backLabel="Back to Radar"
        uid={uid} compass={compass} setCompass={setCompass} tasks={tasks} goals={goals} settings={settings} onGoalChat={onGoalChat} />
      {showSettings && (() => {
        // In the order the page shows them (Craig, 24 Oct 2026: "show them in that order in the Radar Settings form too").
        const SIGNAL_GROUPS = groupOrder.map(id => RADAR_GROUPS.find(g => g.id === id)).filter(g => VISION_SHOWN || g.id !== "whatmatters");
        const isOff = (id) => groupsOff.has(id);
        const toggleGroup = (id) => {
          const next = new Set(groupsOff);
          next.has(id) ? next.delete(id) : next.add(id);
          setSettings(p => ({ ...p, radarGroupsOff: [...next] }));
        };
        const setWindow = (n) => setSettings(p => ({ ...p, radarDueSoonWindow: n }));
        return (
          <div onClick={() => setShowSettings(false)}
            style={{ position: "absolute", inset: 0, zIndex: 110, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-end" }}>
            <div onClick={e => e.stopPropagation()}
              style={{ width: "100%", maxWidth: PAGE_WIDTH, margin: "0 auto", maxHeight: "85%", background: C.surface, borderRadius: "20px 20px 0 0", display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <div style={{ padding: "12px 20px 0", flexShrink: 0 }}>
                <div onClick={() => setShowSettings(false)} style={{ width: 36, height: 4, borderRadius: 2, background: C.border, margin: "0 auto 16px", cursor: "pointer" }} />
                <div style={{ fontSize: 19, fontWeight: 700, color: C.text }}>Radar settings</div>
                <div style={{ fontSize: 14, color: C.textMid, marginTop: 2, marginBottom: 8 }}>Choose which groups Radar shows, and how far ahead it looks for tasks due soon.</div>
              </div>

              <div style={{ flex: 1, overflowY: "auto", padding: "8px 20px 0" }}>

                {SIGNAL_GROUPS.map(g => (
                  <div key={g.id}>
                    <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 0", borderTop: `1px solid ${C.border}` }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 15, color: C.text, fontWeight: 500 }}>{g.label}</div>
                        <div style={{ fontSize: 14, color: C.textMid, marginTop: 2, lineHeight: 1.4 }}>{g.id === "duesoon" ? `Tasks due in the next ${dueSoonWindow} days, starting tomorrow` : g.desc}</div>
                      </div>
                      <RadarSwitch on={!isOff(g.id)} label={g.label} onClick={() => toggleGroup(g.id)} />
                    </div>
                    {g.id === "duesoon" && !isOff("duesoon") && (
                      <div style={{ paddingBottom: 14 }}>
                        <div id="radar-window-label" style={{ fontSize: 14, color: C.textMid, marginBottom: 8 }}>How many days ahead to look</div>
                        <div role="group" aria-labelledby="radar-window-label" style={{ display: "flex", gap: 8 }}>
                          {[3, 7, 14].map(n => (
                            <button key={n} onClick={() => setWindow(n)} aria-pressed={dueSoonWindow === n}
                              style={{ flex: 1, minHeight: 44, textAlign: "center", borderRadius: C.rsm, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                                background: dueSoonWindow === n ? C.accent : C.surface2,
                                color: dueSoonWindow === n ? C.onAccent : C.textMid,
                                border: `1px solid ${dueSoonWindow === n ? C.accent : C.border}` }}>
                              {n} days
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ flexShrink: 0, padding: "12px 20px 32px", borderTop: `1px solid ${C.border}` }}>
                <button onClick={() => setShowSettings(false)}
                  style={{ width: "100%", background: C.accent, color: "#fff", border: "none", borderRadius: C.r, padding: "15px 0", fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <TopBar title="Radar" help={HELP.radar} />
      <div style={{ flexShrink: 0, padding: `0 ${columnPadding()} 10px`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        {/* Craig's words, 23 Oct 2026 ("My Radar" heading removed). */}
        <p style={{ margin: 0, minWidth: 0, fontSize: 16, color: C.textMid }}>
          {taskCount === 0 ? "No tasks on my radar today" : `${taskCount} task${taskCount !== 1 ? "s" : ""} on my radar today`}
        </p>
        <button onClick={() => setShowSettings(true)} aria-label="Choose what Radar shows" title="Choose what Radar shows"
          style={{ width: 44, height: 44, flexShrink: 0, display: "grid", placeItems: "center", background: C.surface2, color: C.textMid, border: `1px solid ${C.border}`, borderRadius: C.rsm, cursor: "pointer", padding: 0 }}>
          <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
        </button>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: "auto", padding: `0 ${columnPadding()} 90px` }}>

        {/* ── Nudge card ── */}
        {settings?.nudgeCard !== false && (activeNudge || pinnedNudge || noNextNudge) && (
          <div style={{ paddingBottom: 16 }}>
            <NudgeCard
              nudge={activeNudge}
              onSnooze={onSnoozeNudge}
              onGotIt={onGotItNudge}
              onNext={(id) => setNoNextNudge(!onNextNudge(id))}
              hasNext={hasNextNudge}
              noneLeft={noNextNudge}
              onCloseNoneLeft={() => setNoNextNudge(false)}
              onPin={onPinNudge}
              onUnpin={onUnpinNudge}
              pinnedNudge={pinnedNudge}
              pinnedNudgeId={pinnedNudgeId}
              nudgeHistory={nudgeHistory}
              onPastNudges={() => onNav("nudges")}
              tasks={tasks}
              onOpenTask={(taskId) => { const t = (tasks||[]).find(t=>t.id===taskId); if(t) tasksKit.openTask(t); }}
            />
          </div>
        )}

        {/* Habits, Dreams and Fears still show below when nothing is urgent. */}
        {totalSurfaced === 0 && (
          <div style={{ padding: "36px 0", textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 600, color: C.text, marginBottom: 8 }}>Nothing urgent today</div>
            <div style={{ fontSize: 17, color: C.textMid, lineHeight: 1.6, maxWidth: 280, margin: "0 auto" }}>
              No tasks are overdue, due today or due in the next few days.
            </div>
          </div>
        )}
        {(() => {
            // Tasks Due Today's rows leave out "Due today", which its heading already says.
            const taskGroup = (id, icon, color, list, rowOptions) => (dh) => (
              <RadarGroup key={id} {...groupProps(id)} icon={icon} label={groupLabel(id)} color={color} count={list.length} {...dh}>
                <CappedList items={list} renderItem={(t) => taskRow(t, rowOptions)} />
              </RadarGroup>
            );
            const goalGroup = (id, icon, color, list) => (dh) => (
              <RadarGroup key={id} {...groupProps(id)} icon={icon} label={groupLabel(id)} color={color} count={list.length} {...dh}>
                <CappedList items={list} renderItem={({ goal }) => goalBlock(id, goal)} />
              </RadarGroup>
            );
            // A group that is off draws nothing. Dreams and fears come from NudgeApp (TaskLinksContext).
            const listGroup = (id, icon, hueName, Group, props) => (dh) => (groupsOff.has(id) ? null : (
              <Group key={id} group={{ ...groupProps(id), icon, label: groupLabel(id), color: hue(hueName).c, ...dh }} editorHost={editorHost} {...props} />
            ));
            // A dream's or fear's tasks, drawn as every other Radar task is.
            const linkTasks = tasksKit.linkTasks(tasks);
            const groupNodes = {
              // Every heading in a hue token, readable on both themes (the old hex headings failed contrast in light).
              overdue: taskGroup("overdue", "⚠️", C.red, overdue),
              duetoday: taskGroup("duetoday", "☀️", C.gold, dueToday, { showDue: false }),
              duesoon: taskGroup("duesoon", "📅", C.blue, dueSoon),
              keydates: (dh) => (
                <RadarGroup key="keydates" {...groupProps("keydates")} icon="🗓️" label={groupLabel("keydates")} color={C.amber} count={keyDatesNow.length} layout="cards" {...dh}>
                  <CappedList items={keyDatesNow} renderItem={keyDateCards.card} />
                </RadarGroup>
              ),
              urgentimportant: goalGroup("urgentimportant", "🔥", C.orange, urgentImportant),
              mostimportant: goalGroup("mostimportant", "⭐", C.forest, mostImportant),
              // In the Home tiles' hues, but Dreams in pink: red is Overdue's.
              habits: listGroup("habits", "🔁", "teal", RadarHabits, { compass, setCompass, goals }),
              dreams: listGroup("dreams", "✨", "pink", RadarDreams, { linkTasks }),
              fears: listGroup("fears", "😟", "grey", RadarFears, { goals, compass, linkTasks }),
              whatmatters: (dh) => (
                <RadarGroup key="whatmatters" {...groupProps("whatmatters")} icon="✦" label={groupLabel("whatmatters")} color={C.purple} count={whatMattersMost.length} layout="card" {...dh}
                  helpIcon={
                    <span onClick={() => setShowWhatMattersHelp(v => !v)}
                      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, borderRadius: "50%", border: `1px solid ${C.textDim}`, fontSize: 10, fontWeight: 700, color: C.textDim, cursor: "pointer", marginLeft: 2 }}>?</span>
                  }>
                  {showWhatMattersHelp && (
                    <div style={{ padding: "12px 14px", borderBottom: `1px solid ${C.border}`, fontSize: 13, color: C.textMid, lineHeight: 1.6, background: C.surface2 }}>
                      These are the vision statements behind your highest-priority goals. They surface here so the deeper "why" stays visible while you decide what to work on today. Drill in to act on the goals driving them.
                    </div>
                  )}
                  <CappedList items={whatMattersMost} renderItem={(entry) => <VisionDrillRow key={entry.vision.id} entry={entry} />} />
                </RadarGroup>
              ),
            };
            return (
              <DndContext sensors={groupSensors} collisionDetection={closestCenter} onDragEnd={onGroupDragEnd}>
                <SortableContext items={groupOrder} strategy={verticalListSortingStrategy}>
                  {groupOrder.map(gid => (
                    <Sortable key={gid} id={gid}>
                      {({ isDragging, attributes, listeners }) =>
                        groupNodes[gid]({ dragAttributes: attributes, dragListeners: listeners, isDragging })
                      }
                    </Sortable>
                  ))}
                </SortableContext>
              </DndContext>
            );
          })()}
      </div>

      {tasksKit.toast}
      <BottomNav active="radar" onNav={onNav} />
    </div>
  );
}

// ─── GOALS ───────────────────────────────────────────────────────────
// A goal's page over Goals or Radar, from its bar; its ◀ says where it goes back to.
function GoalPageOverlay({ goal, onClose, backLabel, uid, compass, setCompass, tasks, goals, settings, onGoalChat }) {
  if (!goal) return null;
  return (
    <div style={{ position: "absolute", inset: 0, zIndex: 100, background: C.bg, display: "flex", flexDirection: "column" }}>
      <GoalDetailPage goal={goal} uid={uid} compass={compass} setCompass={setCompass} tasks={tasks} goals={goals} settings={settings} onBack={onClose} backLabel={backLabel} onGoalChat={onGoalChat} />
    </div>
  );
}

// The Goals page (src/features/goals/) with goal detail and the task editor, which still live here. On a phone a goal
// opens over the list. Where two columns fit, it opens beside it, so the list stays in view (Craig, 4 Nov 2026: "Yes,
// implement the suggested change for the Goals page"), and the goal open is ringed in the list.
function GoalsScreen({ uid, tasks, goals, compass, setCompass, settings, getSortMode, setSortMode, onGoalChat, onNav }) {
  const [detailGoalId, setDetailGoalId] = useState(null);
  const { openTask, newTask } = useTaskActions();
  // With no active goal there's nothing to open beside the list.
  const split = useTwoColumns() && goals.some(g => g.status === "active");
  const detailGoal = goals.find(g => g.id === detailGoalId);
  // Closing the pane puts keyboard focus back on the goal's name in the list, so it isn't lost when ✕ goes.
  const closePane = () => {
    const name = `Open ${detailGoal?.title}`;
    setDetailGoalId(null);
    requestAnimationFrame(() => [...document.querySelectorAll("button[aria-label^='Open ']")].find(b => b.getAttribute("aria-label") === name)?.focus());
  };
  const list = (
    <GoalsPage goals={goals} tasks={tasks} categories={compass?.categories || []} view={goalsPageView(getSortMode(GROUP_BY_SETTING, GOAL_VIEWS.IMPORTANCE))}
      onView={(v) => setSortMode(GROUP_BY_SETTING, v)} onRank={(view, ordered) => writeGoalRanks(uid, view, ordered)}
      onDone={(task) => writeTask(uid, task.id, { done: true })} onOpenGoal={(goal) => setDetailGoalId(goal.id)}
      onOpenTask={openTask} onAddTask={(goal) => newTask(newTaskForGoal(goal, tasks))}
      saveTask={(taskId, fields) => writeTask(uid, taskId, fields)} phase={phaseNow(settings)} onNav={onNav}
      selectedGoalId={split ? detailGoalId : null} />
  );
  // One tree at both widths, so crossing 1184px keeps the list's folds and filter and the goal's open editor. The whole
  // screen is the positioned box, so an editor opened from the goal (a key date's) covers the list too, as on a phone.
  return (
    <div style={{ display: "flex", flex: 1, overflow: "hidden", position: "relative" }}>
      <div style={{ flex: "5 1 0", minWidth: 0, display: "flex", flexDirection: "column", borderRight: split ? `1px solid ${C.border}` : "none" }}>{list}</div>
      {split ? (
      <div style={{ flex: "6 1 0", minWidth: 0, display: "flex", flexDirection: "column" }}>
        {detailGoal
          ? <GoalDetailPage key={detailGoal.id} goal={detailGoal} uid={uid} compass={compass} setCompass={setCompass} tasks={tasks} goals={goals} settings={settings}
              onClose={closePane} onGoalChat={onGoalChat} />
          : (
            <>
              <div aria-hidden="true" style={{ minHeight: TOP_BAR_HEIGHT, flexShrink: 0, borderBottom: `1px solid ${C.border}` }} />
              <p style={{ ...intro(), margin: 0, padding: "24px 32px" }}>Tap a goal's name to see it here, with its tasks, habits and key dates.</p>
            </>
          )}
      </div>
      ) : (
        <GoalPageOverlay goal={detailGoal} onClose={() => setDetailGoalId(null)} backLabel="Back to Goals"
          uid={uid} compass={compass} setCompass={setCompass} tasks={tasks} goals={goals} settings={settings} onGoalChat={onGoalChat} />
      )}
    </div>
  );
}

// ─── BUBBLE UP ───────────────────────────────────────────────────────

// Urgency / importance slider, opened from TaskEditModal.
// item:  task (uses item.text ?? item.title for display)
// fixed: use position:fixed (for use inside portals/modals)
function BubbleSliderOverlay({ item, compass, initialUrgency, initialImportance, initialSlider, onSave, fixed = false }) {
  const [urgency,    setUrgency]    = useState(initialUrgency);
  const [importance, setImportance] = useState(initialImportance);
  const [slider,     setSlider]     = useState(initialSlider);

  const displayName = item.text ?? item.title ?? "";

  const catLabel = (() => {
    if (!item.categoryId) return null;
    const cats = compass?.categories || CATEGORY_DEFS;
    return cats.find(c => c.id === item.categoryId)?.label || null;
  })();

  const pos = fixed ? "fixed" : "absolute";

  return (
    <>
      {/* Backdrop — tap saves and closes */}
      <div onClick={() => onSave(urgency, importance, slider)}
        style={{ position: pos, inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 600 }} />

      {/* Panel */}
      <div style={{
        position: pos, inset: 0, zIndex: 601,
        background: C.surface, display: "flex", flexDirection: "column",
        padding: `32px ${columnPadding(24)} 40px`, overflowY: "auto",
      }}>
        {/* Enlarged thumb for all three sliders — inline styles can't reach pseudo-elements */}
        <style>{`
          .bubble-slider::-webkit-slider-thumb {
            -webkit-appearance: none;
            width: 32px; height: 32px;
            border-radius: 50%;
            cursor: grab;
          }
          .bubble-slider::-moz-range-thumb {
            width: 32px; height: 32px;
            border-radius: 50%;
            border: none;
            cursor: grab;
          }
        `}</style>

        {/* Item identity */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          {catLabel && (
            <div style={{ fontSize: 11, fontWeight: 700, color: C.textDim, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 8 }}>
              {catLabel}
            </div>
          )}
          <div style={{ fontSize: 18, fontWeight: 600, color: C.text, lineHeight: 1.4 }}>{displayName}</div>
        </div>

        {/* Urgency + Importance sliders */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>

          {/* Urgency — left vertical */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, width: 56 }}>
            <div style={{ width: 40, height: 160, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
              <input type="range" min={1} max={10} step={1} value={urgency}
                onChange={e => setUrgency(Number(e.target.value))}
                className="bubble-slider"
                style={{ width: 160, height: 40, transform: "rotate(-90deg)", cursor: "pointer", accentColor: urgencyColor(urgency), margin: 0 }}
              />
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#E24B4A", letterSpacing: "0.06em", textTransform: "uppercase" }}>URGENCY</div>
          </div>

          {/* Centre readout */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <div style={{ fontSize: 52, fontWeight: 700, color: urgencyColor(urgency), lineHeight: 1 }}>{urgency}</div>
            <div style={{ width: 36, height: 1, background: C.border }} />
            <div style={{ fontSize: 52, fontWeight: 700, color: C.accent, lineHeight: 1 }}>{importance}</div>
            <div style={{ fontSize: 10, color: C.textDim, letterSpacing: "0.06em", textTransform: "uppercase", marginTop: 4 }}>urgency / importance</div>
          </div>

          {/* Importance — right vertical */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, width: 56 }}>
            <div style={{ width: 40, height: 160, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
              <input type="range" min={1} max={10} step={1} value={importance}
                onChange={e => setImportance(Number(e.target.value))}
                className="bubble-slider"
                style={{ width: 160, height: 40, transform: "rotate(-90deg)", cursor: "pointer", accentColor: C.accent, margin: 0 }}
              />
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, letterSpacing: "0.06em", textTransform: "uppercase" }}>IMPORTANCE</div>
          </div>
        </div>

        {/* Timeframe slider */}
        <div style={{ marginTop: 40 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{ fontSize: 12, color: C.textDim }}>← Act later</span>
            <span style={{ fontSize: 12, color: C.textDim }}>Act first →</span>
          </div>
          <input type="range" min={1} max={11} step={1} value={slider}
            onChange={e => setSlider(Number(e.target.value))}
            className="bubble-slider"
            style={{ width: "100%", cursor: "pointer", accentColor: C.purple }}
          />
          <div style={{ textAlign: "center", fontSize: 11, color: C.textDim, marginTop: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>TIMEFRAME</div>
        </div>

        {/* Done button */}
        <button
          onClick={() => onSave(urgency, importance, slider)}
          style={{
            marginTop: 40, width: "100%", padding: "15px 0",
            background: C.accent, color: "#fff", border: "none",
            borderRadius: 30, fontSize: 16, fontWeight: 700,
            cursor: "pointer", fontFamily: "inherit",
          }}
        >
          Done
        </button>

      </div>
    </>
  );
}

// ─── NUDGE HISTORY ───────────────────────────────────────────────────
// The Nudges page's filter for the nudges you dismissed (Dismiss or Next on the Nudge card).
const DISMISSED = "dismissed";

// A task a nudge names opens in the task editor here, as it does on Radar's card (25 Oct 2026; until then it went to
// Today, so a nudge listing four tasks meant four trips back).
function NudgeHistoryPage({ nudges, tasks, nudgeState, onGotIt, compass, settings, uid, goals = [] }) {
  const [filter, setFilter]               = useState("All");
  const [editTask, setEditTask]           = useState(null);
  const onOpenTask = (taskId) => setEditTask((tasks || []).find(t => t.id === taskId) || null);
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => forceUpdate(n => n + 1), 10 * 1000);
    return () => clearInterval(interval);
  }, []);

  const userDismissed = new Set(nudgeState?.userDismissed || []);
  const isUserDismissed = (n) => userDismissed.has(n.id);
  const isFollowedThrough = (n) => {
    if (!n.taskId) return false;
    return (tasks || []).find(t => t.id === n.taskId)?.done === true;
  };

  // ✓ Dismissed sits next to All, where a phone shows it; a type shows only when it has a nudge to open on.
  const inType = (n, key) => n.nudgeCategory === key || (key === "chat" && ["Coaching","Reminder","Mindset","CheckIn","Chat"].includes(n.type));
  const types = ["All", DISMISSED, ...NUDGE_CATEGORY_DEFS.map(c => c.key).filter(key => nudges.some(n => !isUserDismissed(n) && inType(n, key)))];
  const filtered = nudges.filter(n => {
    const dismissed = isUserDismissed(n);
    if (filter === DISMISSED) return dismissed;
    if (filter === "All") return !dismissed;
    return !dismissed && inType(n, filter);
  });

  const tc = {
    timing:    { bg: C.tealBg,    color: C.teal    },
    deadlines: { bg: C.redBg,     color: C.red     },
    top10:     { bg: C.amberBg,   color: C.amber   },
    longterm:  { bg: C.tealBg,    color: C.teal    },
    today:     { bg: C.purpleBg,  color: C.purple  },
    habits:    { bg: C.amberBg,   color: C.amber   },
    mindset:   { bg: C.blueBg,    color: C.blue    },
    chat:      { bg: C.surface2,  color: C.textMid },
  };

  return <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", background: C.bg }}>
    {editTask && <TaskEditModal task={editTask} uid={uid} compass={compass} tasks={tasks} goals={goals} settings={settings} onClose={() => setEditTask(null)} />}
    <TopBar title="Nudges" help={HELP.nudges} />
    {nudges.length > 0 && <div style={{ padding: `4px ${columnPadding()} 6px`, display: "flex", gap: 6, overflowX: "auto", flexShrink: 0 }}>
      {types.map(t => {
        const catDef = NUDGE_CATEGORY_DEFS.find(c => c.key === t);
        const label = catDef ? `${catDef.icon} ${catDef.label}` : t === DISMISSED ? "✓ Dismissed" : "✦ All";
        return <Chip key={t} label={label} active={filter === t} onClick={() => setFilter(t)} />;
      })}
    </div>}
    <div style={{ flex: 1, overflowY: "auto", padding: `0 ${columnPadding()} 20px` }}>
      {filtered.length === 0 ? (
        <p style={{ ...intro(), margin: "16px 4px 0" }}>
          {nudges.length === 0 ? "You don't have any nudges yet. They show here as Nudge sends them through the day."
            : filter === DISMISSED ? "When you tap Dismiss or Next on a nudge, it moves here."
            : filter === "All" ? "You've dismissed every nudge. You can find them under ✓ Dismissed."
            : `You don't have any ${NUDGE_CATEGORY_DEFS.find(c => c.key === filter)?.label} nudges now.`}
        </p>
      ) : filtered.map((n, i) => {
        const t = tc[n.nudgeCategory] || tc.chat;
        const followedThrough = isFollowedThrough(n);
        const dismissed = isUserDismissed(n);
        const muted = followedThrough || dismissed;
        // A nudge you've finished with keeps readable text: its strike-through and "Done" or "Dismissed" say so, not fading.
        return <div key={n.id || i} style={{ background: muted ? C.surface2 : C.surface, borderRadius: C.r, border: `1px solid ${C.border}`, padding: 16, marginBottom: 10, boxShadow: C.shadow }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {/* The type's wash behind text in the main colour: some type colours are too faint on their wash in dark. */}
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text, background: t.bg, padding: "3px 10px", borderRadius: 100 }}>
                {n.nudgeCategory ? (NUDGE_CATEGORY_DEFS.find(c => c.key === n.nudgeCategory)?.icon + " " + NUDGE_CATEGORY_DEFS.find(c => c.key === n.nudgeCategory)?.label) : n.type}
              </div>
              {followedThrough && <div style={{ fontSize: 13, color: C.textGreen, fontWeight: 600 }}>✓ Done</div>}
              {dismissed && !followedThrough && <div style={{ fontSize: 13, color: C.textMid, fontWeight: 600 }}>✓ Dismissed</div>}
            </div>
            <div style={{ fontSize: 13, color: C.textMid }}>{n.date} · {n.time}</div>
          </div>
          {/* Struck through only when its task is done: "Dismissed" means seen, not done. */}
          <div style={{ fontSize: 15, color: muted ? C.textMid : C.text, lineHeight: 1.55, fontWeight: 500, textDecoration: followedThrough ? "line-through" : "none" }}>
            {renderNudgeMessage(n.message, n.taskId, tasks, onOpenTask)}
          </div>
          {n.thread && <div style={{ fontSize: 13, color: C.textMid, marginTop: 6 }}>From {n.thread}</div>}
          {!muted && onGotIt && (
            <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
              <button onClick={() => onGotIt(n.id)}
                style={{ minHeight: 44, background: "none", color: C.textMid, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "0 14px", fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                Dismiss
              </button>
            </div>
          )}
        </div>;
      })}
    </div>
  </div>;
}

// ─── SETTINGS ────────────────────────────────────────────────────────
function PasswordManager() {
  const [mode, setMode] = useState(null); // null | "change" | "reset"
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [status, setStatus] = useState(""); // "" | "loading" | "success" | "error"
  const [errorMsg, setErrorMsg] = useState("");

  const inputStyle = {
    width: "100%", background: C.surface2, border: `1px solid ${C.border}`,
    borderRadius: C.rsm, padding: "10px 12px", fontFamily: "inherit",
    fontSize: 14, color: C.text, outline: "none", boxSizing: "border-box",
    marginBottom: 10,
  };

  const handleChangePassword = async () => {
    setErrorMsg("");
    if (!currentPw || !newPw || !confirmPw) { setErrorMsg("Please fill in all fields."); return; }
    if (newPw.length < 6) { setErrorMsg("New password must be at least 6 characters."); return; }
    if (newPw !== confirmPw) { setErrorMsg("New passwords don't match."); return; }
    setStatus("loading");
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, currentPw);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPw);
      setStatus("success");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
      setTimeout(() => { setMode(null); setStatus(""); }, 2000);
    } catch (e) {
      const msgs = {
        "auth/wrong-password": "Current password is incorrect.",
        "auth/invalid-credential": "Current password is incorrect.",
        "auth/too-many-requests": "Too many attempts. Please try again later.",
        "auth/weak-password": "New password must be at least 6 characters.",
      };
      setErrorMsg(msgs[e.code] || "Failed to update password. Please try again.");
      setStatus("error");
    }
  };

  const handleResetPassword = async () => {
    setStatus("loading");
    try {
      await sendPasswordResetEmail(auth, auth.currentUser.email);
      setStatus("success");
    } catch (e) {
      setErrorMsg("Failed to send reset email. Please try again.");
      setStatus("error");
    }
  };

  if (mode === null) return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <button onClick={() => setMode("change")} style={{ width: "100%", background: C.surface2, color: C.text, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "11px 0", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
        Change password
      </button>
      <button onClick={() => setMode("reset")} style={{ width: "100%", background: C.surface2, color: C.text, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "11px 0", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
        Forgot password — send reset email
      </button>
    </div>
  );

  if (mode === "reset") return (
    <div>
      {status === "success" ? (
        <div style={{ fontSize: 14, color: C.accent, fontWeight: 600 }}>Reset email sent to {auth.currentUser?.email} ✓</div>
      ) : (
        <>
          <div style={{ fontSize: 14, color: C.textMid, marginBottom: 14, lineHeight: 1.5 }}>
            A password reset link will be sent to <strong>{auth.currentUser?.email}</strong>.
          </div>
          {errorMsg && <div style={{ fontSize: 13, color: C.red, marginBottom: 10 }}>{errorMsg}</div>}
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => { setMode(null); setErrorMsg(""); setStatus(""); }} style={{ flex: 1, background: C.surface2, color: C.textMid, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "10px 0", fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            <button onClick={handleResetPassword} disabled={status === "loading"} style={{ flex: 1, background: C.accent, color: "white", border: "none", borderRadius: C.rsm, padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: status === "loading" ? 0.7 : 1 }}>
              {status === "loading" ? "Sending…" : "Send reset email"}
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 14 }}>Change password</div>
      <input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} placeholder="Current password" style={inputStyle} />
      <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="New password (min 6 characters)" style={inputStyle} />
      <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Confirm new password" style={inputStyle} />
      {errorMsg && <div style={{ fontSize: 13, color: C.red, marginBottom: 10 }}>{errorMsg}</div>}
      {status === "success" && <div style={{ fontSize: 13, color: C.accent, marginBottom: 10, fontWeight: 600 }}>Password updated ✓</div>}
      <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
        <button onClick={() => { setMode(null); setCurrentPw(""); setNewPw(""); setConfirmPw(""); setErrorMsg(""); setStatus(""); }} style={{ flex: 1, background: C.surface2, color: C.textMid, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "10px 0", fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
        <button onClick={handleChangePassword} disabled={status === "loading"} style={{ flex: 1, background: C.accent, color: "white", border: "none", borderRadius: C.rsm, padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: status === "loading" ? 0.7 : 1 }}>
          {status === "loading" ? "Updating…" : "Update password"}
        </button>
      </div>
    </div>
  );
}

function AccountTypeToggle({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const label = value === "work" ? "💼 Work" : "👤 Personal";
  return (
    <div style={{ position: "relative" }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ fontSize: 14, color: C.text, fontWeight: 500, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
        {label}
        <span style={{ fontSize: 11, color: C.textDim }}>▾</span>
      </div>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 50 }} />
          <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", background: C.surface, border: `1px solid ${C.borderCard}`, borderRadius: C.rsm, boxShadow: C.shadowMd, zIndex: 51, overflow: "hidden", minWidth: 130 }}>
            {[["personal", "👤 Personal"], ["work", "💼 Work"]].map(([val, lbl]) => (
              <div key={val} onClick={() => { onChange(val); setOpen(false); }}
                style={{ padding: "10px 14px", fontSize: 14, cursor: "pointer", fontWeight: value === val ? 700 : 400, color: value === val ? C.accent : C.text, background: value === val ? C.accentLight : "transparent" }}>
                {lbl}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SettingsPage({ settings, setSettings, onNav, onSignOut, userEmail, uid, compass, setCompass, goals = [], softStreak = 0, lifetimeCount = 0 }) {
  const set = (k, v) => setSettings(p => ({ ...p, [k]: v }));
  const [showDeleteData,       setShowDeleteData]       = useState(false);
  const [showDeleteAccount,    setShowDeleteAccount]    = useState(false);
  const [showAccountTypeInfo,  setShowAccountTypeInfo]  = useState(false);
  const [deleting,             setDeleting]             = useState(false);
  const [showPasswordManager,  setShowPasswordManager]  = useState(false);
  const [showManageCategories, setShowManageCategories] = useState(false);

  const deleteAllData = async () => {
    if (!uid) return;
    setDeleting(true);
    try {
      await deleteUserData(uid);

    } catch (e) { console.error("Delete data error:", e); }
    setDeleting(false);
    setShowDeleteData(false);
    onSignOut();
  };

  const deleteAccount = async () => {
    if (!uid) return;
    setDeleting(true);
    try {
      await deleteAllData();
      const user = auth.currentUser;
      if (user) await deleteUser(user);
    } catch (e) { console.error("Delete account error:", e); }
    setDeleting(false);
    setShowDeleteAccount(false);
  };

  const ConfirmModal = ({ title, message, onConfirm, onCancel }) => (
    <>
      <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 300, animation: "fdIn 0.2s ease" }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", zIndex: 301, width: "min(320px, calc(100vw - 40px))", background: C.surface, borderRadius: 20, padding: "28px 24px 24px", boxShadow: "0 20px 60px rgba(0,0,0,0.25)", animation: "scaleIn 0.2s ease forwards", textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontSize: 19, fontWeight: 700, color: C.text, marginBottom: 10 }}>{title}</div>
        <div style={{ fontSize: 14, color: C.textMid, lineHeight: 1.6, marginBottom: 8 }}>{message}</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.red, marginBottom: 24 }}>This cannot be undone.</div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onCancel} style={{ flex: 1, background: C.surface2, color: C.textMid, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "12px 0", fontSize: 15, cursor: "pointer", fontFamily: "inherit" }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={deleting} style={{ flex: 1, background: C.red, color: "white", border: "none", borderRadius: C.rsm, padding: "12px 0", fontSize: 15, fontWeight: 700, cursor: deleting ? "default" : "pointer", fontFamily: "inherit", opacity: deleting ? 0.7 : 1 }}>
            {deleting ? "Deleting…" : "Yes, delete"}
          </button>
        </div>
      </div>
    </>
  );

  return <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", background: C.bg }}>
    {/* The same bar as every other page's, with its own buttons on the right, so its line meets the sidebar's. */}
    <PageHeader title="Settings" onBack={() => onNav("home")}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <StreakBadge softStreak={softStreak} lifetimeCount={lifetimeCount} />
        <button onClick={() => onNav("support")} style={{ background: C.accent, color: "white", border: "none", borderRadius: C.rsm, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          Get Support
        </button>
      </div>
    </PageHeader>
    <div style={{ flex: 1, overflowY: "auto", padding: `0 ${columnPadding()} 20px` }}>

      <div style={{ fontSize: 13, fontWeight: 700, color: C.textDim, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Profile</div>
      <div style={{ background: C.surface, borderRadius: C.r, border: `1px solid ${C.borderCard}`, padding: 16, marginBottom: 20, boxShadow: C.shadow }}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 8 }}>Name</div>
          <input value={settings.name || ""} onChange={e => set("name", e.target.value)} placeholder="Your name" style={{ width: "100%", background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "9px 12px", fontFamily: "inherit", fontSize: 14, color: C.text, outline: "none", boxSizing: "border-box" }} />
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 8 }}>About me</div>
          <textarea value={settings.about || ""} onChange={e => set("about", e.target.value)} placeholder="e.g. Entrepreneur, parent of 2, working on health..." rows={2} style={{ width: "100%", background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "9px 12px", fontFamily: "inherit", fontSize: 14, color: C.text, outline: "none", resize: "none", boxSizing: "border-box" }} />
        </div>
        <div style={{ marginTop: 12, display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>Account type</div>
            <div onClick={() => setShowAccountTypeInfo(true)}
              style={{ width: 18, height: 18, borderRadius: "50%", background: C.surface2, border: `1.5px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 11, fontWeight: 700, color: C.textDim, lineHeight: 1, flexShrink: 0 }}>
              ?
            </div>
          </div>
          <AccountTypeToggle value={settings.accountType || "personal"} onChange={v => set("accountType", v)} />
        </div>
      </div>

      {/* Subscription */}
      <div style={{ fontSize: 13, fontWeight: 700, color: C.textDim, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Subscription</div>
      <div style={{ background: C.surface, borderRadius: C.r, border: `1px solid ${C.borderCard}`, padding: 16, marginBottom: 20, boxShadow: C.shadow }}>
        {settings.proStatus === "active" && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <span style={{ fontSize: 16 }}>✦</span>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.accent }}>Nudge Pro</div>
              <div style={{ background: C.accentLight, color: C.accent, fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 100, textTransform: "uppercase" }}>Active</div>
            </div>
            <div style={{ fontSize: 14, color: C.textDim, marginBottom: 14 }}>
              {settings.proTier === "annual" ? "Annual plan · $100/year" : "Monthly plan · $10/month"}
            </div>
            <button
              onClick={async () => {
                if (!settings.stripeCustomerId) {
                  alert("No subscription found. Please contact support.");
                  return;
                }
                const res = await fetch("/api/create-portal-session", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ customerId: settings.stripeCustomerId }),
                });
                const data = await res.json();
                if (data.url) window.location.href = data.url;
              }}
              style={{ width: "100%", background: C.surface2, color: C.text, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "11px 0", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
            >
              Manage subscription →
            </button>
          </>
        )}
        {settings.proStatus === "friends_family" && (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 16 }}>✦</span>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.accent }}>Nudge Pro</div>
                <div style={{ background: C.accentLight, color: C.accent, fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 100, textTransform: "uppercase" }}>Friends & Family</div>
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.accent }}>$0 / month</div>
            </div>
            <div style={{ fontSize: 13, color: C.textDim, paddingLeft: 24 }}>Complimentary access</div>
          </>
        )}
        {settings.proStatus === "trial" && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>Free trial</div>
              <div style={{ background: C.amberLight, color: C.amber, fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 100, textTransform: "uppercase" }}>
                {(() => {
                  if (!settings.trialStartDate) return "15 days left";
                  const daysPassed = Math.floor((new Date() - new Date(settings.trialStartDate)) / 86400000);
                  const left = Math.max(0, 15 - daysPassed);
                  return `${left} day${left !== 1 ? "s" : ""} left`;
                })()}
              </div>
            </div>
            <div style={{ fontSize: 14, color: C.textDim, marginBottom: 14 }}>Subscribe to keep full access after your trial ends.</div>
            <button onClick={() => onNav("pricing")} style={{ width: "100%", background: C.accent, color: "white", border: "none", borderRadius: C.rsm, padding: "11px 0", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              Nudge Subscription →
            </button>
          </>
        )}
        {(settings.proStatus === "expired" || !settings.proStatus) && (
          <>
            <div style={{ fontSize: 15, color: C.textDim, marginBottom: 14 }}>
              {settings.proStatus === "expired" ? "Your free trial has ended." : "Start your 15-day free trial."}
            </div>
            <button onClick={() => onNav("pricing")} style={{ width: "100%", background: C.accent, color: "white", border: "none", borderRadius: C.rsm, padding: "11px 0", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              {settings.proStatus === "expired" ? "Subscribe to Nudge →" : "See Plans →"}
            </button>
          </>
        )}
      </div>

      <div style={{ fontSize: 13, fontWeight: 700, color: C.textDim, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Coaching Style</div>
      <div style={{ background: C.surface, borderRadius: C.r, border: `1px solid ${C.borderCard}`, padding: 16, marginBottom: 20, boxShadow: C.shadow }}>
        {[{ k: "tone", label: "Tone", opts: ["Gentle", "Balanced", "Direct"] }, { k: "verbosity", label: "Verbosity", opts: ["Brief", "Normal"] }, { k: "mindStyle", label: "Mindset style", opts: ["Subtle", "Mixed", "Direct"] }, { k: "goalDepth", label: "Goal depth", opts: ["Focused", "Balanced", "Comprehensive"] }].map(row => (
          <div key={row.k} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 8 }}>{row.label}</div>
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>{row.opts.map(o => <Chip key={o} label={o} active={settings[row.k] === o} onClick={() => set(row.k, o)} />)}</div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 13, fontWeight: 700, color: C.textDim, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Day Schedule</div>
      <div style={{ background: C.surface, borderRadius: C.r, border: `1px solid ${C.borderCard}`, padding: 16, marginBottom: 20, boxShadow: C.shadow }}>
        <div style={{ fontSize: 15, color: C.textDim, marginBottom: 14, lineHeight: 1.5 }}>Defines the boundaries between your Morning, Afternoon, and Evening phases.</div>
        {[
          { k: "dayStart",       label: "Morning starts",   icon: "🌅" },
          { k: "afternoonStart", label: "Afternoon starts", icon: "☀️" },
          { k: "eveningStart",   label: "Evening starts",   icon: "🌙" },
          { k: "dayEnd",         label: "Evening ends",     icon: "🔚" },
        ].map((row, i) => (
          <div key={row.k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: i < 3 ? `1px solid ${C.border}` : "none" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 16 }}>{row.icon}</span>
              <div style={{ fontSize: 16, fontWeight: 500, color: C.text }}>{row.label}</div>
            </div>
            <input type="time" value={settings[row.k] || ""} onChange={e => set(row.k, e.target.value)} style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "6px 10px", fontFamily: "inherit", fontSize: 14, color: C.text, outline: "none", cursor: "pointer" }} />
          </div>
        ))}
      </div>

      {/* Categories card */}
      <div style={{ fontSize: 13, fontWeight: 700, color: C.textDim, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Goals and tasks</div>
      <div style={{ background: C.surface, borderRadius: C.r, border: `1px solid ${C.borderCard}`, padding: "12px 16px", marginBottom: 20, boxShadow: C.shadow }}>
        <div onClick={() => setShowManageCategories(true)}
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>Manage Categories</div>
            <div style={{ fontSize: 13, color: C.textDim, marginTop: 2 }}>
              {(compass?.categories || []).length} categories
            </div>
          </div>
          <span style={{ fontSize: 18, color: C.textDim }}>›</span>
        </div>
      </div>

      {/* Nudge reminders toggle */}
      <div style={{ fontSize: 13, fontWeight: 700, color: C.textDim, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Reminders</div>
      <div style={{ background: C.surface, borderRadius: C.r, border: `1px solid ${C.border}`, padding: 16, marginBottom: 20, boxShadow: C.shadow }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 12, borderBottom: `1px solid ${C.border}` }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 500, color: C.text }}>Nudge reminders</div>
            <div style={{ fontSize: 13, color: C.textDim, marginTop: 2 }}>Show helpful reminders on your Today page</div>
          </div>
          <Toggle on={settings.nudgeCard !== false} onToggle={() => set("nudgeCard", settings.nudgeCard === false)} />
        </div>
        {settings.nudgeCard !== false && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textDim, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 8 }}>Nudge categories</div>
            {NUDGE_CATEGORY_DEFS.map((cat, i) => (
              <div key={cat.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < NUDGE_CATEGORY_DEFS.length - 1 ? `1px solid ${C.border}` : "none" }}>
                <div style={{ fontSize: 15, color: C.text }}>{cat.icon} {cat.label}</div>
                <Toggle on={settings[`nudge_${cat.key}`] !== false} onToggle={() => set(`nudge_${cat.key}`, settings[`nudge_${cat.key}`] === false)} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Appearance card */}
      <div style={{ fontSize: 13, fontWeight: 700, color: C.textDim, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Appearance</div>
      <div style={{ background: C.surface, borderRadius: C.r, border: `1px solid ${C.borderCard}`, padding: "12px 16px", marginBottom: 20, boxShadow: C.shadow, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 14, fontWeight: 500, color: C.text }}>Theme</span>
        <div style={{ display: "flex", background: C.surface2, borderRadius: C.rsm, padding: 3, gap: 2 }}>
          {[["light","☀️ Light"],["dark","🌙 Dark"]].map(([opt, label]) => (
            <button key={opt} onClick={() => set("theme", opt)}
              style={{ padding: "5px 14px", borderRadius: "8px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", border: "none", background: settings.theme === opt ? C.surface : "transparent", color: settings.theme === opt ? C.accent : C.textDim, boxShadow: settings.theme === opt ? C.shadow : "none", transition: "all 0.15s", whiteSpace: "nowrap" }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Account card */}
      <div style={{ fontSize: 13, fontWeight: 700, color: C.textDim, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Account</div>
      <div style={{ background: C.surface, borderRadius: C.r, border: `1px solid ${C.borderCard}`, padding: 16, marginBottom: 20, boxShadow: C.shadow }}>
        {userEmail && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13, color: C.textDim, marginBottom: 2 }}>Signed in as</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text, userSelect: "text" }}>
              {userEmail.split("@").map((part, i) => (
                <span key={i}>{i > 0 ? "\u200B@" : ""}{part}</span>
              ))}
            </div>
          </div>
        )}
        <button onClick={onSignOut} style={{ width: "100%", background: C.accent, color: "white", border: "none", borderRadius: C.rsm, padding: "12px 0", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          Sign out
        </button>
        {auth.currentUser?.providerData?.[0]?.providerId === "password" && (
          <div style={{ marginTop: 12, textAlign: "center" }}>
            <span onClick={() => setShowPasswordManager(p => !p)} style={{ fontSize: 13, color: C.accent, cursor: "pointer", fontWeight: 600 }}>
              Change password
            </span>
          </div>
        )}
        {showPasswordManager && auth.currentUser?.providerData?.[0]?.providerId === "password" && (
          <div style={{ marginTop: 14, borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
            <PasswordManager />
          </div>
        )}
      </div>
      
      <div onClick={() => onNav("support")} style={{ textAlign: "center", marginBottom: 24 }}>
        <span style={{ fontSize: 14, color: C.accent, cursor: "pointer", fontWeight: 600 }}>Get Support →</span>
      </div>

      {/* Danger zone — de-emphasised */}
      <div onClick={() => setShowDeleteData(true)} style={{ textAlign: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 12, color: C.textDim, cursor: "pointer", textDecoration: "underline" }}>Delete all my data</span>
      </div>
      <div onClick={() => setShowDeleteAccount(true)} style={{ textAlign: "center", marginBottom: 32 }}>
        <span style={{ fontSize: 12, color: C.textDim, cursor: "pointer", textDecoration: "underline" }}>Delete my account</span>
      </div>

    </div>
    {showManageCategories && (
        <ManageCategoriesModal
          categories={compass?.categories || []}
          compass={compass}
          setCompass={setCompass}
          uid={uid}
          goals={goals}
          onCategoryDeleted={() => {}}
          onClose={() => setShowManageCategories(false)}
        />
      )}
    <BottomNav active="settings" onNav={onNav} />

    {showAccountTypeInfo && <AccountTypeInfoModal onClose={() => setShowAccountTypeInfo(false)} />}
    {showDeleteData && (
      <ConfirmModal
        title="Delete all your data?"
        message="This will permanently delete your dreams, fears, goals and tasks, your daily plans, your settings and your nudge history."
        onConfirm={deleteAllData}
        onCancel={() => setShowDeleteData(false)}
      />
    )}
    {showDeleteAccount && (
      <ConfirmModal
        title="Delete your account?"
        message="This will permanently delete your account and all data associated with it. You will be signed out immediately. You can set up a new account with the same email address in the future should you change your mind."
        onConfirm={deleteAccount}
        onCancel={() => setShowDeleteAccount(false)}
      />
    )}
  </div>;
}

// ─── ROOT ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────
// FIREBASE ROOT — replaces the // ─── ROOT section in nudge_v3.jsx

// ─── DEFAULTS ─────────────────────────────────────────────────────────
const DEFAULT_COMPASS = {
  categories: CATEGORY_DEFS.map(c => makeEmptyCategory(c.id, c.label, c.icon, c.color)),
  mindset: [...DEFAULT_MINDSET],
  gratitude: [],
};

// ─── TASK SCHEMA & HELPERS ────────────────────────────────────────────────────
// A new task's shape is makeTask (src/features/tasks/makeTask.js).
// Create a new task in Firestore. Returns the new task id.
const createTask = async (uid, fields = {}) => {
  if (!uid) return null;
  const taskData = {
    ...makeTask(fields),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const ref = await addDoc(collection(db, "users", uid, "tasks"), taskData);
  return ref.id;
};
 
// Update specific fields on an existing task.
// writeTask lets a failed write reach the caller, for a screen that says whether it worked;
// updateTask logs it instead.
const writeTask = (uid, taskId, fields = {}) =>
  setDoc(doc(db, "users", uid, "tasks", taskId), { ...fields, updatedAt: serverTimestamp() }, { merge: true });
const updateTask = async (uid, taskId, fields = {}) => {
  if (!uid || !taskId) return;
  await writeTask(uid, taskId, fields).catch(console.error);
};
 
// Delete a task permanently.
const deleteTask = async (uid, taskId) => {
  if (!uid || !taskId) return;
  await deleteDoc(doc(db, "users", uid, "tasks", taskId)).catch(console.error);
};

// Preset hex swatches — 24 colors across the full spectrum
const PRESET_GOAL_COLORS = [
  "#5B50D9", // purple
  "#7C40D9", // violet
  "#A040D9", // bright violet
  "#D040A0", // magenta
  "#C23B8A", // hot pink
  "#E8556A", // rose
  "#D94545", // red
  "#E8703A", // burnt orange
  "#E8853A", // amber
  "#C2882A", // bronze
  "#D4A820", // gold
  "#3DAA6A", // green
  "#2A9A5A", // forest green
  "#1A8A45", // dark green
  "#4A7A5A", // sage
  "#2A9A78", // mint teal
  "#1A9A8A", // teal
  "#1A9BC5", // cyan
  "#1A80C5", // sky blue
  "#3B75D9", // blue
  "#2A5DC5", // cobalt
  "#1A45B5", // navy
  "#6B5A8A", // mauve
  "#8A5A8A", // plum
];


const makeGoal = ({
  title        = "",
  description  = null,
  categoryId   = null,
  timeframe    = null,       // retained in schema for future use — not shown in UI
  deadline     = null,       // "YYYY-MM-DD"
  status       = "active",   // "active" | "complete" | "parked"
  inTop3Goals  = false,      // TODO: retire UI in favour of goalSortOrder top 3
  color        = "purple",   // hex string or legacy name
  colorCustomized = false,   // true = user explicitly overrode category color
  images       = [],
  // ── Bubble Up fields (mirrors task schema) ───────────────────────
  goalUrgency    = null,     // 1–10, null = not yet rated
  goalImportance = null,     // 1–10, null = not yet rated
  goalSortOrder  = null,     // integer position, 1 = top; null = unordered
  goalFocusToday  = false,   // true = selected as one of First 3 Today (goals)
  goalUrgencyOrder    = null, // integer — manual drag sort order (Urgency) on Goals page
  goalImportanceOrder = null, // integer — manual drag sort order (Importance) on Goals page
} = {}) => ({
  title, description, categoryId, timeframe,
  deadline, status, inTop3Goals, color, colorCustomized, images,
  goalUrgency, goalImportance, goalSortOrder, goalFocusToday, goalUrgencyOrder, goalImportanceOrder,
});

// Create a new goal in Firestore. Returns the new goal id.
const createGoal = async (uid, fields = {}) => {
  if (!uid) return null;
  const goalData = {
    ...makeGoal(fields),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const ref = await addDoc(collection(db, "users", uid, "goals"), goalData);
  return ref.id;
};

// Update specific fields on an existing goal.
const updateGoal = async (uid, goalId, fields = {}) => {
  if (!uid || !goalId) return;
  await setDoc(
    doc(db, "users", uid, "goals", goalId),
    { ...fields, updatedAt: serverTimestamp() },
    { merge: true }
  ).catch(console.error);
};

// ─── BUBBLE UP HELPERS ───────────────────────────────────────────────
// Urgency colours are hardcoded hex — intentional. Must stay constant in
// both light and dark themes so colour meaning is preserved.
const urgencyColor = (value) => {
  if (value == null) return "#6B97C7";
  if (value >= 8) return "#E24B4A";
  if (value >= 6) return "#EF9F27";
  if (value >= 4) return "#D4C547";
  return "#6B97C7";
};

const DEFAULT_SETTINGS = {
  name: "", about: "", tone: "Direct", verbosity: "Brief",
  mindStyle: "Mixed", goalDepth: "Balanced", accountType: "personal",
  dailyR: true, eodR: true, dayStart: "06:00", afternoonStart: "12:00", eveningStart: "17:00", dayEnd: "23:00", hasSeenNudgeHelp: false,
  theme: null,                 // null = not yet set, detected on first load
  nudgeCard: true,
  nudge_timing: true,
  nudge_deadlines: true,
  nudge_top10: true,
  nudge_longterm: true,
  nudge_today: true,
  nudge_habits: true,
  nudge_mindset: true,
  // Subscription fields — managed by Stripe webhook + manual Firestore for Friends & Family
  proStatus: null,           // "trial" | "active" | "expired" | "friends_family" | null
  proTier: null,             // "monthly" | "annual" | "friends_family" | null
  trialStartDate: null,      // "YYYY-MM-DD" — set on first planning session
  stripeCustomerId: null,
  stripeSubscriptionId: null,
  bubbleMigrated: false,           // set true after one-time Bubble Up backfill runs
  goalsMigrated:  false,           // set true after one-time Goals Bubble Up backfill runs
  goalUrgencyOrderMigrated: false, // set true after goalManualOrder → goalUrgencyOrder migration
  sortModes: {},             // persisted view choice per page key, e.g. tasks_page_groupby
};

const DEFAULT_STATS = { lifetimeCount: 0, softStreak: 0, lastCheckinDate: null, lastSessionDate: null, top10Completions: 0 };

// ─── PAYWALL OVERLAY ─────────────────────────────────────────────────
function PaywallOverlay({ onNav }) {
  return (
    <div style={{
      position: "absolute", inset: 0, zIndex: 50,
      background: "rgba(240,240,240,0.82)",
      backdropFilter: "blur(6px)",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      padding: 32, textAlign: "center",
    }}>
      <div style={{ fontSize: 32, marginBottom: 14 }}>✦</div>
      <div style={{ fontFamily: "Georgia,serif", fontSize: 22, fontWeight: 700, color: C.text, marginBottom: 10 }}>
        Your free trial has ended
      </div>
      <div style={{ fontSize: 15, color: C.textMid, lineHeight: 1.7, marginBottom: 28 }}>
        Subscribe to Nudge to keep your plan,<br />your Compass, and your momentum.
      </div>
      <button
        onClick={() => onNav("pricing")}
        style={{ background: C.accent, color: "white", border: "none", borderRadius: C.r, padding: "14px 32px", fontSize: 15, fontWeight: 700, cursor: "pointer", boxShadow: `0 4px 16px rgba(42,95,74,0.28)`, fontFamily: "inherit" }}
      >
        Nudge Subscription →
      </button>
    </div>
  );
}

// ─── TRIAL BANNER ────────────────────────────────────────────────────
function TrialBanner({ daysLeft, onNav }) {
  if (daysLeft <= 0 || daysLeft > 5) return null;
  const urgent = daysLeft <= 3;
  return (
    <div
      onClick={() => onNav("pricing")}
      style={{
        margin: "0 0 12px",
        background: urgent ? C.redLight : C.amberLight,
        border: `1px solid ${urgent ? C.red : C.amber}`,
        borderRadius: C.rsm,
        padding: "10px 14px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        cursor: "pointer",
      }}
    >
      <div style={{ fontSize: 13, color: urgent ? C.red : C.amber, fontWeight: 600 }}>
        {daysLeft === 1 ? "Last day of your free trial" : `${daysLeft} days left in your free trial`}
      </div>
      <div style={{ fontSize: 12, color: urgent ? C.red : C.amber, fontWeight: 700 }}>Subscribe →</div>
    </div>
  );
}

// ─── SUPPORT PAGE ────────────────────────────────────────────────────
function SupportPage({ onNav, settings, userEmail }) {
  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState(null);
  const [attachment, setAttachment] = useState(null); // { name, type, base64 }
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    if (file.size > 5 * 1024 * 1024) {
      alert("Image must be under 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(",")[1];
      setAttachment({ name: file.name, type: file.type, base64 });
    };
    reader.readAsDataURL(file);
  };

  const categories = [
    { id: "tech",     label: "🔧 Tech Support"  },
    { id: "feature",  label: "💡 Feature Idea"  },
    { id: "feedback", label: "💬 User Feedback" },
  ];

  const handleSubmit = async () => {
    if (!category || !message.trim()) return;
    setStatus("sending");
    try {
      const res = await fetch("/api/send-support", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          category,
          message,
          userEmail: userEmail || null,
          userName: settings?.name || null,
          attachment: attachment || null,
        }),
      });
      const data = await res.json();
      setStatus(data.ok ? "sent" : "error");
    } catch {
      setStatus("error");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", background: C.bg }}>
      <PageHeader title="Get Support" onBack={() => onNav("settings")} backLabel="Back to Settings" />
      <div style={{ flex: 1, overflowY: "auto", padding: `16px ${columnPadding()} 40px` }}>
        {status === "sent" ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 60, textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: C.accentLight, border: `1px solid ${C.accent}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, marginBottom: 16 }}>✓</div>
            <div style={{ fontFamily: "Georgia,serif", fontSize: 22, fontWeight: 700, color: C.text, marginBottom: 10 }}>Message sent</div>
            <div style={{ fontSize: 15, color: C.textMid, lineHeight: 1.7, marginBottom: 8 }}>Thanks — we'll get back to you shortly.</div>
            {userEmail && (
              <div style={{ fontSize: 14, color: C.textDim, marginBottom: 32 }}>
                Check <strong style={{ color: C.textMid }}>{userEmail}</strong> for our reply.
              </div>
            )}
            <button onClick={() => onNav("settings")} style={{ background: C.accent, color: "white", border: "none", borderRadius: C.r, padding: "13px 28px", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              Back to Settings
            </button>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 15, color: C.textMid, lineHeight: 1.6, marginTop: 8, marginBottom: 24 }}>
              Have an issue, idea, or feedback? Send it directly to the Nudge team.
            </div>
            {userEmail && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.textDim, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 8 }}>Your email</div>
                <div style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "11px 14px", fontSize: 15, color: C.textDim, fontStyle: "italic" }}>
                  {userEmail}
                </div>
                <div style={{ fontSize: 12, color: C.textDim, marginTop: 5 }}>Replies will be sent to this address.</div>
              </div>
            )}
            <div style={{ fontSize: 13, fontWeight: 700, color: C.textDim, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 10 }}>Category</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
              {categories.map(c => (
                <div key={c.id} onClick={() => setCategory(c.id)}
                  style={{ padding: "13px 16px", borderRadius: C.rsm, cursor: "pointer", background: category === c.id ? C.accentLight : C.surface, border: `1.5px solid ${category === c.id ? C.accent : C.borderCard}`, fontSize: 15, fontWeight: category === c.id ? 600 : 400, color: category === c.id ? C.accent : C.text, transition: "all 0.15s", boxShadow: category === c.id ? "none" : C.shadow }}>
                  {c.label}
                </div>
              ))}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.textDim, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 10 }}>Message</div>
            <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Describe the issue, idea, or feedback..." rows={6}
              style={{ width: "100%", background: C.surface, border: `1px solid ${C.borderCard}`, borderRadius: C.rsm, padding: "12px 14px", fontFamily: "inherit", fontSize: 15, color: C.text, outline: "none", resize: "none", boxSizing: "border-box", lineHeight: 1.6, boxShadow: C.shadow }} />
            <div style={{ marginTop: 14, marginBottom: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.textDim, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 10 }}>Attach a screenshot (optional)</div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} style={{ display: "none" }} />
              {!attachment ? (
                <div onClick={() => fileInputRef.current?.click()}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", background: C.surface, border: `1px dashed ${C.border}`, borderRadius: C.rsm, cursor: "pointer" }}>
                  <span style={{ fontSize: 20 }}>📎</span>
                  <span style={{ fontSize: 14, color: C.textDim }}>Tap to choose a photo</span>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 14px", background: C.accentLight, border: `1px solid ${C.accentMid}`, borderRadius: C.rsm }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 20 }}>🖼️</span>
                    <span style={{ fontSize: 14, color: C.text, fontWeight: 500 }}>{attachment.name}</span>
                  </div>
                  <div onClick={() => { setAttachment(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                    style={{ color: C.textDim, cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</div>
                </div>
              )}
            </div>
            {status === "error" && (
              <div style={{ background: C.redLight, border: `1px solid ${C.red}`, borderRadius: C.rsm, padding: "11px 14px", fontSize: 14, color: C.red, marginTop: 12 }}>
                Something went wrong — please try again.
              </div>
            )}
            <button onClick={handleSubmit} disabled={!category || !message.trim() || status === "sending"}
              style={{ width: "100%", background: C.accent, color: "white", border: "none", borderRadius: C.r, padding: "14px 0", fontSize: 15, fontWeight: 700, cursor: (!category || !message.trim() || status === "sending") ? "default" : "pointer", fontFamily: "inherit", marginTop: 16, opacity: (!category || !message.trim() || status === "sending") ? 0.5 : 1, transition: "opacity 0.15s" }}>
              {status === "sending" ? "Sending…" : "Send Message"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── PRICING PAGE ────────────────────────────────────────────────────
function PricingPage({ onNav, settings, uid }) {
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);

  const MONTHLY_PRICE_ID = process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID;
  const ANNUAL_PRICE_ID  = process.env.REACT_APP_STRIPE_ANNUAL_PRICE_ID;

  const handleSubscribe = async (tier) => {
    setLoading(tier);
    setError(null);
    try {
      const priceId = tier === "monthly" ? MONTHLY_PRICE_ID : ANNUAL_PRICE_ID;
      const res = await fetch("/api/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid,
          email: null,
          priceId,
          existingCustomerId: settings.stripeCustomerId || null,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (e) {
      setError("Something went wrong. Please try again.");
    }
    setLoading(null);
  };

  const trialDaysLeft = (() => {
    if (!settings.trialStartDate) return 15;
    const start = new Date(settings.trialStartDate);
    const daysPassed = Math.floor((new Date() - start) / (1000 * 60 * 60 * 24));
    return Math.max(0, 15 - daysPassed);
  })();

  const isExpired = settings.proStatus === "expired";

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", background: C.bg }}>
      <PageHeader title="Nudge Pro" onBack={() => onNav("home")} />
      <div style={{ flex: 1, overflowY: "auto", padding: `16px ${columnPadding()} 40px` }}>
        <div style={{ textAlign: "center", padding: "24px 0 28px" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>✦</div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: 26, fontWeight: 700, color: C.text, marginBottom: 10 }}>Nudge Subscription</div>
          {isExpired ? (
            <div style={{ fontSize: 15, color: C.red, fontWeight: 600, marginBottom: 4 }}>Your free trial has ended.</div>
          ) : (
            <div style={{ fontSize: 15, color: C.textMid, lineHeight: 1.6 }}>
              {trialDaysLeft > 0 ? `${trialDaysLeft} day${trialDaysLeft !== 1 ? "s" : ""} left in your free trial.` : "Your free trial is ending today."}
            </div>
          )}
          <div style={{ fontSize: 14, color: C.textDim, marginTop: 6, lineHeight: 1.6 }}>Keep full access to planning, check-ins,<br />coaching, and your Compass.</div>
        </div>
        <div style={{ background: C.accentLight, borderRadius: C.r, border: `1.5px solid ${C.accent}`, padding: 20, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 17, color: C.accent, marginBottom: 3 }}>Monthly</div>
              <div style={{ fontSize: 13, color: C.textDim }}>Cancel anytime</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: C.text }}>$10</div>
              <div style={{ fontSize: 13, color: C.textDim }}>per month</div>
            </div>
          </div>
          <button onClick={() => handleSubscribe("monthly")} disabled={!!loading}
            style={{ width: "100%", background: C.accent, color: "white", border: "none", borderRadius: C.rsm, padding: "13px 0", fontSize: 15, fontWeight: 700, cursor: loading ? "default" : "pointer", fontFamily: "inherit", opacity: loading === "annual" ? 0.5 : 1, transition: "all 0.15s" }}>
            {loading === "monthly" ? "Redirecting…" : "Subscribe Monthly"}
          </button>
        </div>
        <div style={{ background: C.accent, borderRadius: C.r, border: `1px solid ${C.accent}`, padding: 20, marginBottom: 12, boxShadow: `0 4px 20px rgba(42,95,74,0.22)` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                <div style={{ fontWeight: 700, fontSize: 17, color: "white" }}>Annual</div>
                <div style={{ background: "rgba(255,255,255,0.22)", color: "white", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 100 }}>BEST VALUE</div>
              </div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)" }}>Save $20 per year</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: "white" }}>$100</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)" }}>per year</div>
            </div>
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", marginBottom: 14 }}>≈ $8.33/month, billed annually</div>
          <button onClick={() => handleSubscribe("annual")} disabled={!!loading}
            style={{ width: "100%", background: "white", color: C.accent, border: "none", borderRadius: C.rsm, padding: "13px 0", fontSize: 15, fontWeight: 700, cursor: loading ? "default" : "pointer", fontFamily: "inherit", opacity: loading === "monthly" ? 0.5 : 1, transition: "all 0.15s" }}>
            {loading === "annual" ? "Redirecting…" : "Subscribe Annually"}
          </button>
        </div>
        {error && (
          <div style={{ background: C.redLight, border: `1px solid ${C.red}`, borderRadius: C.rsm, padding: "12px 16px", fontSize: 14, color: C.red, marginBottom: 12 }}>{error}</div>
        )}
        <div style={{ background: C.surface, borderRadius: C.r, border: `1px solid ${C.borderCard}`, padding: 20, marginTop: 4, boxShadow: C.shadow }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.textDim, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 14 }}>What's included</div>
          {[
            ["📅", "Daily planning with commitment scoring"],
            ["🚨", "Check-ins and mid-day coaching"],
            ["🧭", "Full Compass — goals, habits, avoidances"],
            ["🤔", "Help Me Decide mode"],
            ["😤", "Procrastination coaching"],
            ["🔥", "Streak tracking and session history"],
          ].map(([icon, text]) => (
            <div key={text} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 16 }}>{icon}</span>
              <span style={{ fontSize: 15, color: C.text }}>{text}</span>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", fontSize: 12, color: C.textDim, marginTop: 20, lineHeight: 1.7 }}>
          Secure payment via Stripe.<br />Cancel anytime from Settings.
        </div>
      </div>
    </div>
  );
}

// ─── SIGN-IN SCREEN ───────────────────────────────────────────────────
function SignInScreen({ onGoogleSignIn, onEmailSignIn, onEmailSignUp, onForgotPassword, loading, authError }) {
  const [mode, setMode] = useState("signin"); // "signin" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [localError, setLocalError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const error = localError || authError;

  const handleSubmit = () => {
    setLocalError("");
    if (!email || !password) { setLocalError("Please enter your email and password."); return; }
    if (mode === "signup") {
      if (password.length < 6) { setLocalError("Password must be at least 6 characters."); return; }
      if (password !== confirmPassword) { setLocalError("Passwords don't match."); return; }
      onEmailSignUp(email, password);
    } else {
      onEmailSignIn(email, password);
    }
  };

  const handleForgot = async () => {
    setLocalError("");
    const sent = await onForgotPassword(email);
    if (sent) setResetSent(true);
  };

  const inputStyle = {
    width: "100%", background: C.surface2, border: `1px solid ${C.border}`,
    borderRadius: C.rsm, padding: "11px 12px", fontFamily: "inherit",
    fontSize: 15, color: C.text, outline: "none", boxSizing: "border-box",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflowY: "auto", background: C.bg }}>

      {/* ── Header bar ── */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <Logo height={42} />
        <div style={{ fontSize: 13, color: C.textMid, fontWeight: 500 }}>15-day free trial</div>
      </div>

      {/* ── Hero ── */}
      <div style={{
        background: `radial-gradient(ellipse at 50% 0%, rgba(42,95,74,0.09) 0%, rgba(42,95,74,0.04) 60%, transparent 85%), ${C.bg}`,
        padding: "40px 28px 36px",
        display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center",
      }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: 38, fontWeight: 800, color: C.text, marginBottom: 8, letterSpacing: "-0.5px" }}>
          Nudge<span style={{ color: C.accent }}>.</span>
        </div>
        <div style={{ fontSize: 17, color: C.textMid, lineHeight: 1.6, marginBottom: 28, maxWidth: 280 }}>
          Most productivity apps help you plan.<br />
          <strong style={{ color: C.text }}>Nudge helps you <em style={{ color: C.accent }}>take action.</em></strong>
        </div>

        {/* ── Auth card ── */}
        <div style={{ width: "100%", maxWidth: 360, background: C.surface, borderRadius: C.r, border: `1px solid ${C.borderCard}`, padding: "24px 20px", boxShadow: C.shadowMd, textAlign: "left" }}>

          {/* Mode toggle */}
          <div style={{ display: "flex", gap: 0, marginBottom: 20, background: C.surface2, borderRadius: C.rsm, padding: 3 }}>
            {[["signin", "Sign In"], ["signup", "Create Account"]].map(([m, label]) => (
              <button key={m} onClick={() => { setMode(m); setLocalError(""); setResetSent(false); }}
                style={{ flex: 1, padding: "8px 0", fontSize: 13, fontWeight: mode === m ? 700 : 500, background: mode === m ? C.surface : "transparent", color: mode === m ? C.text : C.textMid, border: "none", borderRadius: C.rsm, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s", boxShadow: mode === m ? C.shadow : "none" }}>
                {label}
              </button>
            ))}
          </div>

          {/* Email input */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: C.textDim, fontWeight: 500, marginBottom: 5 }}>Email</div>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com" style={inputStyle}
              onKeyDown={e => e.key === "Enter" && handleSubmit()}
            />
          </div>

          {/* Password input */}
          <div style={{ marginBottom: mode === "signup" ? 12 : 8 }}>
            <div style={{ fontSize: 13, color: C.textDim, fontWeight: 500, marginBottom: 5 }}>Password</div>
            <div style={{ position: "relative" }}>
              <input
                type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                placeholder={mode === "signup" ? "At least 6 characters" : "Your password"}
                style={{ ...inputStyle, paddingRight: 40 }}
                onKeyDown={e => e.key === "Enter" && handleSubmit()}
              />
              <div onClick={() => setShowPassword(p => !p)}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: C.textDim, userSelect: "none", lineHeight: 1, display: "flex", alignItems: "center" }}>
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </div>
            </div>
          </div>

          {/* Confirm password — signup only */}
          {mode === "signup" && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 13, color: C.textDim, fontWeight: 500, marginBottom: 5 }}>Confirm Password</div>
              <div style={{ position: "relative" }}>
                <input
                  type={showConfirmPassword ? "text" : "password"} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repeat password"
                  style={{ ...inputStyle, paddingRight: 40 }}
                  onKeyDown={e => e.key === "Enter" && handleSubmit()}
                />
                <div onClick={() => setShowConfirmPassword(p => !p)}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: C.textDim, userSelect: "none", lineHeight: 1, display: "flex", alignItems: "center" }}>
                {showConfirmPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </div>
              </div>
            </div>
          )}

          {/* Forgot password link — sign in only */}
          {mode === "signin" && (
            <div style={{ textAlign: "right", marginBottom: 16 }}>
              {resetSent ? (
                <span style={{ fontSize: 13, color: C.accent }}>Reset email sent ✓</span>
              ) : (
                <span onClick={handleForgot} style={{ fontSize: 13, color: C.accent, cursor: "pointer", fontWeight: 500 }}>
                  Forgot password?
                </span>
              )}
            </div>
          )}

          {/* Error message */}
          {error && (
            <div style={{ fontSize: 13, color: C.red, marginBottom: 12, lineHeight: 1.5 }}>{error}</div>
          )}

          {/* Submit button — style swaps to primary when email+password filled */}
          {loading ? (
            <div style={{ fontSize: 14, color: C.textDim, textAlign: "center", padding: "12px 0" }}>Signing in...</div>
          ) : (() => {
            const emailFocused = email.trim().length > 0 && password.length > 0;
            return (
              <button onClick={handleSubmit} style={{ width: "100%", background: emailFocused ? C.accent : C.accentMid, color: emailFocused ? "white" : C.accent, border: emailFocused ? "none" : `1.5px solid rgba(42,95,74,0.5)`, borderRadius: C.rsm, padding: emailFocused ? "13px 0" : "12px 0", fontSize: emailFocused ? 15 : 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginBottom: 12, boxShadow: emailFocused ? `0 4px 16px rgba(42,95,74,0.28)` : "none" }}>
                {mode === "signup" ? "Create Account with Email" : "Sign In with Email"}
              </button>
            );
          })()}

          {/* Divider */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <div style={{ flex: 1, height: 1, background: C.border }} />
            <span style={{ fontSize: 12, color: C.textDim, fontWeight: 500 }}>or sign in with your Gmail account (easier!)</span>
            <div style={{ flex: 1, height: 1, background: C.border }} />
          </div>

          {/* Google button — style swaps to secondary when email+password filled */}
          {!loading && (() => {
            const emailFocused = email.trim().length > 0 && password.length > 0;
            return (
              <button onClick={onGoogleSignIn} style={{ width: "100%", background: emailFocused ? C.accentMid : C.accent, color: emailFocused ? C.accent : "white", border: emailFocused ? `1.5px solid rgba(42,95,74,0.5)` : "none", borderRadius: C.rsm, padding: "13px 0", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, boxShadow: emailFocused ? "none" : `0 4px 16px rgba(42,95,74,0.28)`, marginBottom: 12 }}>
                <span style={{ fontSize: 16 }}>G</span> Continue with Gmail
              </button>
            );
          })()}

          </div>{/* end auth card */}

        <div style={{ fontSize: 17, color: C.textDim, marginTop: 14, lineHeight: 1.8 }}>
          15-day free trial · No credit card required
        </div>
      </div>

      {/* ── Sound Familiar ── */}
      <div style={{ background: C.surface, borderTop: `1px solid ${C.border}`, padding: "36px 28px 28px" }}>
        <div style={{ maxWidth: 420, margin: "0 auto" }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: C.text, letterSpacing: "0.01em", marginBottom: 18, textAlign: "center" }}>Sound familiar?</div>
          {[
            // "You know what you should do. You just don't do it.",
            // "Tasks pile up. Days slip by. Stress accumulates.",
            // "It's not a time management failure. It's a psychology problem.",
            "You know what to do. You just don't do it.",
            "Tasks pile up. Days slip by. Stress rises.",
            "It's not a time problem. It's psychology.",
          ].map((line, i) => (
            <div key={i} style={{ fontSize: 17, color: C.textMid, lineHeight: 1.7, textAlign: "center", marginBottom: 12, maxWidth: 320, margin: "0 auto 12px" }}>
              {line}
            </div>
          ))}
        </div>
      </div>

      {/* ── Callout band ── */}
      <div style={{ background: C.tealBg, borderTop: `1px solid ${C.tealLight}`, borderBottom: `1px solid ${C.tealLight}`, padding: "28px 32px", textAlign: "center" }}>
        <div style={{ fontSize: 24, fontWeight: 800, color: C.textGreen, lineHeight: 1.2, marginBottom: 8 }}>Maybe you need a Nudge.</div>
        <div style={{ fontSize: 17, fontWeight: 400, color: C.textMid, lineHeight: 1.6, maxWidth: 260, margin: "0 auto" }}>Everyone does occasionally.</div>
      </div>

      {/* ── How Nudge helps ── */}
      <div style={{ background: C.bg, padding: "36px 28px" }}>
        <div style={{ maxWidth: 420, margin: "0 auto" }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: C.text, letterSpacing: "0.01em", marginBottom: 18, textAlign: "center" }}>How Nudge helps</div>
          {[
            { icon: "🎯", title: "Make real commitments", body: "Every task gets a commitment score before it goes on your plan. Low scores trigger a conversation, not unrealistic expectations." },
            { icon: "🔁", title: "Check in and stay on track", body: "The check-in is the heartbeat of Nudge. It's not a status report — it's a pivot point for the rest of your day." },
            { icon: "😤", title: "Get unstuck when it matters", body: "When you're avoiding something or don't know where to start, Nudge helps you make good decisions in the moments that matter." },
          ].map((item, i) => (
            <div key={i} style={{ background: C.surface, borderRadius: C.r, border: `1px solid ${C.borderCard}`, padding: "16px 18px", marginBottom: 10, boxShadow: C.shadow, display: "flex", alignItems: "flex-start", gap: 14 }}>
              <span style={{ fontSize: 22, flexShrink: 0, marginTop: 2 }}>{item.icon}</span>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 4 }}>{item.title}</div>
                <div style={{ fontSize: 16, color: C.textMid, lineHeight: 1.6 }}>{item.body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Positioning statement band ── */}
      <div style={{ background: C.tealBg, borderTop: `1px solid ${C.tealLight}`, borderBottom: `1px solid ${C.tealLight}`, padding: "28px 32px", textAlign: "center" }}>
        <div style={{ fontSize: 24, fontWeight: 800, color: C.textGreen, lineHeight: 1.2, marginBottom: 10 }}>Nudge.</div>
        <div style={{ fontSize: 19, fontWeight: 400, color: C.textMid, lineHeight: 1.6, margin: "0 auto 6px" }}>It's not a Todo List app.</div>
        <div style={{ fontSize: 19, fontWeight: 400, color: C.textMid, lineHeight: 1.6, margin: "0 auto" }}>It's not Project Management software.</div>
        <div style={{ fontSize: 24, fontWeight: 800, color: C.textGreen, lineHeight: 1.2, marginTop: 12 }}>It's a Cure for Procrastination.</div>
      </div>

      {/* ── Pricing ── */}
      <div style={{ background: C.surface, padding: "36px 28px 0", borderTop: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 420, margin: "0 auto" }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: C.text, letterSpacing: "0.01em", marginBottom: 18, textAlign: "center" }}>After your free trial</div>
          <div style={{ background: C.accentLight, borderRadius: C.r, border: `1.5px solid ${C.accent}`, padding: "20px", marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.accent, marginBottom: 3 }}>Monthly</div>
                <div style={{ fontSize: 14, color: C.textDim }}>Cancel anytime</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: C.text }}>$10</div>
                <div style={{ fontSize: 14, color: C.textDim }}>per month</div>
              </div>
            </div>
          </div>
          <div style={{ background: C.surface, borderRadius: C.r, border: `1px solid ${C.borderCard}`, padding: "16px 20px", marginBottom: 36, boxShadow: C.shadow }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>Annual</div>
                  <div style={{ background: C.accentLight, color: C.accent, fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 100 }}>SAVE $20</div>
                </div>
                <div style={{ fontSize: 14, color: C.textDim }}>≈ $8.33/month, billed annually</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>$100</div>
                <div style={{ fontSize: 14, color: C.textDim }}>per year</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom CTA ── */}
      <div style={{ background: C.bg, padding: "32px 28px", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ width: "100%", maxWidth: 360 }}>
          <button onClick={onGoogleSignIn} style={{ width: "100%", background: C.accent, color: "white", border: "none", borderRadius: C.rsm, padding: "13px 0", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, boxShadow: `0 4px 16px rgba(42,95,74,0.28)` }}>
            <span style={{ fontSize: 16 }}>G</span> Continue with Gmail
          </button>
          <div style={{ fontSize: 13, color: C.textDim, textAlign: "center", marginTop: 10, lineHeight: 1.8 }}>
            15-day free trial · No credit card required
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <div style={{ background: C.surface, borderTop: `1px solid ${C.border}`, padding: "20px 28px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.textMid, fontFamily: "Georgia,serif" }}>Nudge<span style={{ color: C.accent }}>.</span></div>
        <div style={{ fontSize: 14, color: C.textDim, textAlign: "center", lineHeight: 1.7 }}>A product of Broad Reach Labs · © {new Date().getFullYear()}</div>
        <div style={{ fontSize: 13, color: C.textDim, textAlign: "center", lineHeight: 1.7 }}>Your data is private and stored securely. Only you can see it.</div>
      </div>

    </div>
  );
}

// ─── LOADING SCREEN ───────────────────────────────────────────────────
// ─── ROOT ─────────────────────────────────────────────────────────────
// The nudge engine is src/features/nudges/useNudgeEngine.js.

// ─── END NUDGE ENGINE ─────────────────────────────────────────────────────────

// useTheme removed — theme logic handled directly in NudgeApp

// Placeholder styles handled via data-theme CSS in index.html

export default function NudgeApp() {
  // ── Auth state
  const [user, setUser]           = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [authError, setAuthError] = useState("");

  // ── App state
  const [screen, setScreen]       = useState("home");
  const wide = useWideScreen();
  // The top bar's add and search, opened here once for every page: "add" (the "Add new" sheet), "task", "goal", "search".
  const [appSheet, setAppSheet]   = useState(null);
  // The kind + sent the user to its page to add (an id from ADD_KINDS); that page opens its editor, then clears it.
  const [addKind, setAddKind]     = useState(null);
  const [chatMode, setChatMode]         = useState(null);
  const [chatSeedMessage, setChatSeedMessage] = useState(null);

  const handleGoalChat = (goal, mode) => {
    const messages = {
      plan:          `I want to make a plan for my goal: "${goal.title}"`,
      procrastinate: `I'm procrastinating on my goal: "${goal.title}". Help me figure out how to take action.`,
      motivate:      `I need help getting motivated to work on my goal: "${goal.title}"`,
    };
    setChatSeedMessage(messages[mode] ?? messages.plan);
    nav("chat", mode);
  };
  const [showNudges, setShowNudges] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  // ── Persisted state
  const [compass, setCompassState]       = useState(DEFAULT_COMPASS);
  const [settings, setSettingsState]     = useState(DEFAULT_SETTINGS);
  const [nudgeState, setNudgeStateLocal] = useState({ lastShown: {}, dismissed: [] });
  const [activeNudge, setActiveNudge]    = useState(null);
  const nudgeStateLoadedRef              = useRef(false);
  const [todayData, setTodayData]        = useState({ planned: false, nudgeCount: 0 });
  const [checkIn, setCheckInState]       = useState({ time: "", context: "" });
  const [nudgeHistory, setNudgeHistory]  = useState([]);
  const [stats, setStatsState]           = useState(DEFAULT_STATS);

  // ── Tasks (unified task collection)
  const [tasks, setTasks]               = useState([]);

  // ── Goals (first-class aspirations, separate from tasks)
  const [goals, setGoals]               = useState([]);

  // ── Visions (above goals in the hierarchy)
  const [visions, setVisions]           = useState([]);
  const [keyDates, setKeyDates]         = useState(null); // null until the first snapshot
  const [keyDatesError, setKeyDatesError] = useState(null);
 
  // ── Milestone toast
  const [milestone, setMilestone]       = useState(null);
  const [showMilestone, setShowMilestone] = useState(false);

  // ── Subscription success
  const [showSubSuccess, setShowSubSuccess] = useState(() => {
    return window.location.search.includes("subscribed=true");
  });

  // Chat state — lifted to root so conversation persists across navigation
  const [chatMessages, setChatMessages] = useState([{ role: "ai", text: DEFAULT_GREETING, time: "" }]);
  const [chatHistory, setChatHistory] = useState([]);

  // ── Subscription helpers
  const proStatus = settings.proStatus;
  const trialStartDate = settings.trialStartDate;

  const getTrialDaysLeft = () => {
    if (!trialStartDate) return 15;
    const start = new Date(trialStartDate + "T00:00:00");
    const now = new Date();
    const daysPassed = Math.floor((now - start) / (1000 * 60 * 60 * 24));
    return Math.max(0, 15 - daysPassed);
  };

  const hasFullAccess = () => {
    return ["active", "friends_family", "trial"].includes(proStatus);
  };

  const trialDaysLeft = getTrialDaysLeft();

  // ── Derived from stats
  const softStreak    = stats.softStreak || 0;
  const lifetimeCount = stats.lifetimeCount || 0;
  const lastSessionDate = stats.lastSessionDate || null;

  // ─── AUTH LISTENER ─────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      if (!firebaseUser) {
        // User signed out — clear all in-memory state including chat
        setChatMessages([{ role: "ai", text: DEFAULT_GREETING, time: "" }]);
        setChatHistory([]);
      }
      // Loading starts in the same render as the user. Before 23 Oct 2026 there was one render with a signed-in
      // user, the default settings and dataLoading false, and the theme effect saved those defaults over the
      // user's real settings on every open (name, tone, day times, nudges, and the subscription status).
      if (firebaseUser) setDataLoading(true);
      setUser(firebaseUser);
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  // ─── LOAD USER DATA ────────────────────────────────────────────────
  useEffect(() => {
    if (!user) { setDataLoading(false); return; }
    const uid = user.uid;

    // ── Real-time listener for today's plan ──────────────────────────
    const todayRef = doc(db, "users", uid, "days", todayKey());
    const unsubToday = onSnapshot(todayRef, (snap) => {
      if (snap.exists()) {
        setTodayData(snap.data());
        setCheckInState(snap.data().checkIn || { time: "", context: "" });
      } else {
        setTodayData({ planned: false, nudgeCount: 0 });
        setCheckInState({ time: "", context: "" });
      }
    });
 
    // ── Midnight reset relocated to load() — runs after settings are fetched ──
 
    // ── Real-time listener for all tasks ─────────────────────────────
    const tasksRef = collection(db, "users", uid, "tasks");
    const unsubTasks = onSnapshot(
      query(tasksRef, orderBy("createdAt", "asc")),
      (snap) => {
        setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      },
      (err) => console.error("Tasks listener error:", err)
    );

    // ── Real-time listener for all goals ─────────────────────────────
    const goalsRef = collection(db, "users", uid, "goals");
    const unsubGoals = onSnapshot(
      query(goalsRef, orderBy("createdAt", "asc")),
      (snap) => {
        setGoals(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      },
      (err) => console.error("Goals listener error:", err)
    );

    // ── Real-time listener for all visions ───────────────────────────
    const visionsRef = collection(db, "users", uid, "visions");
    const unsubVisions = onSnapshot(
      query(visionsRef, orderBy("createdAt", "asc")),
      (snap) => {
        setVisions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      },
      (err) => console.error("Visions listener error:", err)
    );

    // ── Real-time listener for Key Dates (the Key Dates page, Radar) ──
    setKeyDatesError(null);
    const unsubKeyDates = subscribeKeyDates(uid, (list) => { setKeyDates(list); setKeyDatesError(null); },
      (err) => { console.error("Key Dates listener error:", err); setKeyDatesError(err); });

    // ── One-time loads for other data ────────────────────────────────
    const load = async () => {
      setDataLoading(true);
      setLoadFailed(false);
      // Craig, 20 Oct 2026: back after time away, the app sat on the loading screen until he refreshed. On a
      // phone whose network is waking up, sign-in's token refresh and these reads can take a minute or more;
      // LoadingScreen offers Reload after 10 s. Reads start together, and no write is awaited (a write's
      // promise waits for the server; Firestore applies it locally at once).
      let loaded = false;
      try {
        const dataDoc = (name) => getDoc(doc(db, "users", uid, "data", name));
        const [compassSnap, settingsSnap, sortModesSnap, nudgeStateSnap, resetStateSnap, statsSnap] =
          await Promise.all(["compass", "settings", "sortModes", "nudgeState", "resetState", "stats"].map(dataDoc));
        if (compassSnap.exists()) setCompassState({ ...DEFAULT_COMPASS, ...compassSnap.data() });

        let loadedSettings = DEFAULT_SETTINGS;
        if (settingsSnap.exists()) loadedSettings = { ...DEFAULT_SETTINGS, ...settingsSnap.data() };

        // sortModes lives in its own document — immune to any settings overwrites
        if (sortModesSnap.exists()) loadedSettings = { ...loadedSettings, sortModes: sortModesSnap.data() };

        if (nudgeStateSnap.exists()) {
          const ns = nudgeStateSnap.data();
          setNudgeStateLocal(ns);
          if (!nudgeStateLoadedRef.current) {
            nudgeStateLoadedRef.current = true;
            if (ns?.lastActiveNudge) {
              setActiveNudge(ns.lastActiveNudge);
            }
          }
        }

        // ── Daily reset — lastResetDate stored in its own document, immune to settings overwrites ──
        const todayStr = todayKey();
        const lastResetDate = resetStateSnap.exists() ? resetStateSnap.data().lastResetDate : null;
        if (lastResetDate !== todayStr) {
          try {
            const [tasksSnap, goalsSnap] = await Promise.all([getDocs(collection(db, "users", uid, "tasks")), getDocs(collection(db, "users", uid, "goals"))]);
            const resetBatch = writeBatch(db);
            let resetCount = 0;
            let goalResetCount = 0;
            tasksSnap.forEach(taskDoc => {
              const d = taskDoc.data();
              if (d.todayPhase || d.todayTime || d.inTop3 || d.bubbleFocusToday || d.shortlisted) {
                resetBatch.set(taskDoc.ref, {
                  todayPhase: null,
                  todayTime: null,
                  inTop3: false,
                  bubbleFocusToday: false,
                  bubbleFocusDate: null,
                  shortlisted: false,
                  shortlistDate: null,
                }, { merge: true });
                resetCount++;
              }
            });
            goalsSnap.forEach(goalDoc => {
              if (goalDoc.data().goalFocusToday) {
                resetBatch.set(goalDoc.ref, { goalFocusToday: false }, { merge: true });
                goalResetCount++;
              }
            });
            if (resetCount > 0 || goalResetCount > 0) resetBatch.commit().catch(e => console.error("Midnight reset error:", e));
            // Write to dedicated resetState doc — nothing else ever touches this document
            setDoc(
              doc(db, "users", uid, "data", "resetState"),
              { lastResetDate: todayStr }
            ).catch(console.error);
            // Clear pinned nudge — use merge to avoid clobbering other nudgeState fields
            setDoc(
              doc(db, "users", uid, "data", "nudgeState"),
              { pinnedNudge: null, pinnedDate: null },
              { merge: true }
            ).catch(console.error);
            setNudgeStateLocal(prev => ({ ...prev, pinnedNudge: null, pinnedDate: null }));
          } catch (e) {
            console.error("Midnight reset error:", e);
          }
        }

        // ── Beta whitelist + trial start — runs only on true first sign-in (proStatus never set in Firestore)
        const isFirstSignIn = !settingsSnap.exists() || settingsSnap.data().proStatus == null;
        if (isFirstSignIn && user.email) {
          const whitelistSnap = await getDoc(doc(db, "config", "betaWhitelist"));
          const emails = whitelistSnap.exists() ? (whitelistSnap.data().emails || []) : [];
          if (emails.map(e => e.toLowerCase()).includes(user.email.toLowerCase())) {
            // Whitelisted — grant Friends & Family Pro
            loadedSettings = {
              ...loadedSettings,
              proStatus: "friends_family",
              proTier: "friends_family",
            };
          } else {
            // New user, not whitelisted — start trial immediately
            loadedSettings = {
              ...loadedSettings,
              proStatus: "trial",
              trialStartDate: todayKey(),
            };
          }
          setDoc(
            doc(db, "users", uid, "data", "settings"),
            loadedSettings,
            { merge: true }
          ).catch(console.error);
        }

        // ── One-time migration: set trialStartDate for existing trial users who pre-date the field ──
        if (loadedSettings.proStatus === "trial" && !loadedSettings.trialStartDate) {
          loadedSettings = { ...loadedSettings, trialStartDate: todayKey() };
          setDoc(
            doc(db, "users", uid, "data", "settings"),
            { trialStartDate: todayKey() },
            { merge: true }
          ).catch(console.error);
        }

        if (statsSnap.exists()) setStatsState({ ...DEFAULT_STATS, ...statsSnap.data() });

        setSettingsState(loadedSettings);
        loaded = true;
        setDataLoading(false);

        // ── Session notification — once per calendar day per non-test user ──
        const pingSnap = await getDoc(doc(db, "users", uid, "data", "sessionPing")).catch(() => null);
        const lastPing = pingSnap?.exists() ? pingSnap.data().lastPingDate : null;
        if (lastPing !== todayStr) {
          setDoc(doc(db, "users", uid, "data", "sessionPing"), { lastPingDate: todayStr }).catch(console.error);
          fetch("/api/notify-session", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ uid, email: user?.email || null }),
          }).catch(console.error);
        }

        const nudgesSnap = await getDocs(
          query(collection(db, "users", uid, "nudges"), orderBy("ts", "desc"), limit(50))
        );
        setNudgeHistory(nudgesSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      } catch (e) {
        console.error("Error loading data:", e);
        // Never open the app without the user's settings: its effects would save the defaults over them.
        if (!loaded) setLoadFailed(true);
      }
    };

    load();

    // ── Cleanup ──────────────────────────────────────────────────────
    return () => { unsubToday(); unsubTasks(); unsubGoals(); unsubVisions(); unsubKeyDates(); };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── PERSIST HELPERS ───────────────────────────────────────────────
  const uid = user?.uid;
  useRepeatRollover(uid, keyDates);
  // Dreams and fears, one listener each, for the task editor's fields and Radar (TaskLinksContext).
  const dreamsList = usePlanItems(uid, "dreams");
  const fearsList = useStatements(uid, "fears", goals);
  // The task editor opened through TaskActionsContext: { taskId } (shown as it is now; it closes if the task goes) or
  // { defaults } for a new one.
  const [taskEditor, setTaskEditor] = useState(null);
  const editorTask = taskEditor?.taskId ? tasks.find(t => t.id === taskEditor.taskId) : null;
  // A task deleted while open closes its editor for good, so it can't reopen if the id comes back.
  useEffect(() => { if (taskEditor?.taskId && !editorTask) setTaskEditor(null); }, [taskEditor, editorTask]);
  const phase = phaseNow(settings);
  const taskActions = useMemo(() => ({
    openTask: (task) => setTaskEditor({ taskId: task.id }),
    newTask: (defaults) => setTaskEditor({ defaults }),
    saveTask: (taskId, fields) => writeTask(uid, taskId, fields),
    phase,
  }), [uid, phase]);

  // Saves only the lists that changed (changedFields.js): a Mindset drag on one device no longer writes its copy of the
  // categories, habits and temptations over another device's (Craig, 30 Oct 2026: "Yes, I like that idea").
  const setCompass = useCallback((updater) => {
    setCompassState(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      const patch = changedFields(prev, next);
      if (uid && Object.keys(patch).length) setTimeout(() => setDoc(doc(db, "users", uid, "data", "compass"), patch, { merge: true }).catch(console.error), 0);
      return next;
    });
  }, [uid]);

  const setSettings = useCallback(async (updater) => {
    setSettingsState(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      // Only the fields that changed (settingsPatch.js), so another device's settings are never overwritten.
      const patch = settingsPatch(prev, next);
      if (uid && Object.keys(patch).length) setDoc(doc(db, "users", uid, "data", "settings"), patch, { merge: true }).catch(console.error);
      return next;
    });
  }, [uid]);

  // Opening or closing a folded section (Radar's groups, Today's Inspiration) saves only that page's list.
  const toggleSection = useCallback((page, id) => {
    setSettingsState(prev => {
      const next = toggleOpen(prev, page, id);
      if (uid) setDoc(doc(db, "users", uid, "data", "settings"), openSectionsPatch(next, page), { merge: true }).catch(console.error);
      return next;
    });
  }, [uid]);

  const setCheckIn = useCallback(async (val) => {
    setCheckInState(val);
    if (uid) {
      const dayRef = doc(db, "users", uid, "days", todayKey());
      setDoc(dayRef, { checkIn: val }, { merge: true }).catch(console.error);
    }
  }, [uid]);

  // Auto-schedule a check-in when there are phase tasks but no check-in set
  useEffect(() => {
    if (dataLoading) return;
    if (checkIn?.time) return; // already set

    const phaseTasks = openPlanTasks(tasks);
    if (phaseTasks.length === 0) return;

    // Parse phase boundaries into minutes-since-midnight
    const toMins = (t24) => {
      const [h, m] = (t24 || "00:00").split(":").map(Number);
      return h * 60 + (m || 0);
    };
    const morningStart   = toMins(settings?.dayStart       || "06:00");
    const afternoonStart = toMins(settings?.afternoonStart || "12:00");
    const eveningStart   = toMins(settings?.eveningStart   || "17:00");
    const dayEnd         = toMins(settings?.dayEnd         || "22:00");

    const phases = [
      { id: "morning",   start: morningStart,   end: afternoonStart },
      { id: "afternoon", start: afternoonStart, end: eveningStart   },
      { id: "evening",   start: eveningStart,   end: dayEnd         },
    ];

    const now = new Date();
    const nowMins = now.getHours() * 60 + now.getMinutes();

    // Find the best phase: current phase with tasks, or next upcoming phase with tasks
    let targetPhase = null;
    for (const phase of phases) {
      const hasTasksInPhase = phaseTasks.some(t => t.todayPhase === phase.id);
      if (!hasTasksInPhase) continue;
      // Current phase: we're in it and check-in target is still in the future
      if (nowMins >= phase.start && nowMins < phase.end) { targetPhase = phase; break; }
      // Upcoming phase
      if (nowMins < phase.start) { targetPhase = phase; break; }
    }
    if (!targetPhase) return;

    // Schedule 60 mins before phase end, minimum 15 mins from now
    let checkInMins = targetPhase.end - 60;
    if (checkInMins <= nowMins + 15) checkInMins = Math.min(targetPhase.end - 15, nowMins + 30);
    if (checkInMins >= targetPhase.end) return; // no room

    // Format as "3:30pm"
    const ciHour = Math.floor(checkInMins / 60);
    const ciMin  = checkInMins % 60;
    const ampm   = ciHour >= 12 ? "pm" : "am";
    const h12    = ciHour % 12 || 12;
    const timeStr = ciMin === 0 ? `${h12}${ampm}` : `${h12}:${String(ciMin).padStart(2, "0")}${ampm}`;

    setCheckIn({ time: timeStr, context: "Based on your planned tasks" });
  }, [tasks, checkIn, settings, dataLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveNudgeState = useCallback(async (newState) => {
    setNudgeStateLocal(newState);
    if (uid) setDoc(doc(db, "users", uid, "data", "nudgeState"), newState).catch(console.error);
  }, [uid]);

  const saveNudge = useCallback(async (nudge) => {
    setNudgeHistory(prev => [nudge, ...prev]);
    if (uid) {
      addDoc(collection(db, "users", uid, "nudges"), { ...nudge, ts: Date.now() }).catch(console.error);
    }
  }, [uid]);

  // ─── THEME ─────────────────────────────────────────────────────────
  const [, forceRender] = useState(0);
  const themeSetting = settings.theme;
  useEffect(() => {
    if (!uid) return;
    if (dataLoading) return;
    if (!themeSetting) {
      const saved = (() => { try { return localStorage.getItem("nudge-theme"); } catch(e) { return null; } })();
      const systemDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      const detected = saved || (systemDark ? "dark" : "light");
      setSettings(p => ({ ...p, theme: detected }));
      return;
    }
    const isDark = themeSetting === "dark";
    const palette = isDark ? DARK : LIGHT;
    Object.assign(C, palette);
    document.documentElement.setAttribute("data-theme", isDark ? "dark" : "light");
    document.documentElement.style.background = palette.bg;
    document.documentElement.style.colorScheme = isDark ? "dark" : "light";
    try { localStorage.setItem("nudge-theme", themeSetting); } catch(e) {}
    forceRender(n => n + 1);
  }, [themeSetting, uid, dataLoading, setSettings]);

  // ─── SORT MODE HELPERS ──────────────────────────────────────────────
  // Persists a view choice per page key to Firestore (e.g. "tasks_page_groupby").
  const getSortMode = (cardKey, defaultMode = "date") =>
    settings.sortModes?.[cardKey] ?? defaultMode;

  const setSortMode = (cardKey, mode) => {
    const newSortModes = { ...(settings.sortModes || {}), [cardKey]: mode };
    setSettingsState(prev => ({
      ...prev,
      sortModes: newSortModes,
    }));
    // Write to dedicated sortModes document — nothing else ever touches this
    if (uid) {
      setDoc(
        doc(db, "users", uid, "data", "sortModes"),
        newSortModes
      ).catch(console.error);
    }
  };

  // ─── BUBBLE UP ONE-TIME MIGRATION ──────────────────────────────────
  // Backfills bubbleUrgency=1, bubbleImportance=1, and sequential
  // bubbleSortOrder for all existing tasks that are missing these values.
  // Runs once per account, gated by settings.bubbleMigrated flag.
  const bubbleMigratedRef = useRef(false);
  useEffect(() => {
    if (!uid || dataLoading || bubbleMigratedRef.current) return;
    if (settings.bubbleMigrated) { bubbleMigratedRef.current = true; return; }
    if (tasks.length === 0) return;
    bubbleMigratedRef.current = true;
    (async () => {
      try {
        const needsWork = tasks.filter(t =>
          !t.done && (t.bubbleUrgency == null || t.bubbleImportance == null || t.bubbleSortOrder == null)
        );
        if (needsWork.length === 0) {
          setSettings(p => ({ ...p, bubbleMigrated: true }));
          return;
        }
        const maxOrder = tasks.reduce((max, t) => Math.max(max, t.bubbleSortOrder ?? 0), 0);
        // Sort unordered tasks by createdAt so ordering is stable
        const sorted = [...needsWork].sort((a, b) => (a.createdAt?.seconds ?? 0) - (b.createdAt?.seconds ?? 0));
        let pos = maxOrder + 1;
        const batch = writeBatch(db);
        sorted.forEach(t => {
          batch.set(doc(db, "users", uid, "tasks", t.id), {
            bubbleUrgency:    t.bubbleUrgency    ?? 1,
            bubbleImportance: t.bubbleImportance ?? 1,
            bubbleSortOrder:  t.bubbleSortOrder  ?? pos++,
            updatedAt: serverTimestamp(),
          }, { merge: true });
        });
        await batch.commit();
        setSettings(p => ({ ...p, bubbleMigrated: true }));
        console.log(`Bubble Up migration: patched ${sorted.length} tasks.`);
      } catch(e) {
        console.error("Bubble Up migration error:", e);
        bubbleMigratedRef.current = false; // allow retry on next load
      }
    })();
  }, [uid, dataLoading, tasks, settings.bubbleMigrated, setSettings]);

  // ─── GOALS BUBBLE UP ONE-TIME MIGRATION ────────────────────────────
  // Seeds goalUrgency=1, goalImportance=1, and goalSortOrder based on
  // deadline-date sort for all existing goals missing these values.
  const goalsMigratedRef = useRef(false);
  useEffect(() => {
    if (!uid || dataLoading || goalsMigratedRef.current) return;
    if (settings.goalsMigrated) { goalsMigratedRef.current = true; return; }
    if (goals.length === 0) return;
    goalsMigratedRef.current = true;
    (async () => {
      try {
        const needsWork = goals.filter(g =>
          g.goalUrgency == null || g.goalImportance == null || g.goalSortOrder == null
        );
        if (needsWork.length === 0) {
          setSettings(p => ({ ...p, goalsMigrated: true }));
          return;
        }
        // Sort by deadline asc, nulls last, then alpha — matches default Goals page sort
        const sorted = [...needsWork].sort((a, b) => {
          if (a.deadline && b.deadline) return a.deadline.localeCompare(b.deadline);
          if (a.deadline) return -1;
          if (b.deadline) return 1;
          return (a.title || "").localeCompare(b.title || "");
        });
        const maxOrder = goals.reduce((max, g) => Math.max(max, g.goalSortOrder ?? 0), 0);
        let pos = maxOrder + 1;
        const batch = writeBatch(db);
        sorted.forEach(g => {
          batch.set(doc(db, "users", uid, "goals", g.id), {
            goalUrgency:    g.goalUrgency    ?? 1,
            goalImportance: g.goalImportance ?? 1,
            goalSortOrder:  g.goalSortOrder  ?? pos++,
            updatedAt: serverTimestamp(),
          }, { merge: true });
        });
        await batch.commit();
        setSettings(p => ({ ...p, goalsMigrated: true }));
        console.log(`Goals Bubble Up migration: patched ${sorted.length} goals.`);
      } catch(e) {
        console.error("Goals Bubble Up migration error:", e);
        goalsMigratedRef.current = false;
      }
    })();
  }, [uid, dataLoading, goals, settings.goalsMigrated, setSettings]);

  // ─── GOAL URGENCY ORDER ONE-TIME MIGRATION ─────────────────────────
  // Copies goalManualOrder → goalUrgencyOrder for any existing goal where
  // the new field is null but the old field was set. Safe for new users
  // with no goals (needsWork will be empty, marks itself done cleanly).
  const goalUrgencyOrderMigratedRef = useRef(false);
  useEffect(() => {
    if (!uid || dataLoading || goalUrgencyOrderMigratedRef.current) return;
    if (settings.goalUrgencyOrderMigrated) { goalUrgencyOrderMigratedRef.current = true; return; }
    if (goals.length === 0) return;
    goalUrgencyOrderMigratedRef.current = true;
    (async () => {
      try {
        const needsWork = goals.filter(g => g.goalUrgencyOrder == null && g.goalManualOrder != null);
        if (needsWork.length === 0) {
          await setDoc(doc(db, "users", uid, "data", "settings"),
            { goalUrgencyOrderMigrated: true }, { merge: true });
          setSettings(p => ({ ...p, goalUrgencyOrderMigrated: true }));
          return;
        }
        const batch = writeBatch(db);
        needsWork.forEach(g => {
          batch.set(doc(db, "users", uid, "goals", g.id),
            { goalUrgencyOrder: g.goalManualOrder, updatedAt: serverTimestamp() }, { merge: true });
        });
        await batch.commit();
        await setDoc(doc(db, "users", uid, "data", "settings"),
          { goalUrgencyOrderMigrated: true }, { merge: true });
        setSettings(p => ({ ...p, goalUrgencyOrderMigrated: true }));
        console.log(`Goal Urgency Order migration: copied goalManualOrder → goalUrgencyOrder for ${needsWork.length} goals.`);
      } catch(e) {
        console.error("Goal Urgency Order migration error:", e);
        goalUrgencyOrderMigratedRef.current = false;
      }
    })();
  }, [uid, dataLoading, goals, settings.goalUrgencyOrderMigrated, setSettings]);

  // ─── NUDGE ENGINE HOOK ─────────────────────────────────────────────
  const { snooze: _snooze, gotIt: _gotIt, next: _next, hasNext: hasNextNudge, pinNudge: _pinNudge, unpinNudge: _unpinNudge } = useNudgeEngine({
    tasks,
    todayData,
    settings,
    compass,
    nudgeState,
    saveNudgeState,
    saveNudge,
    uid,
    top10Completions: stats.top10Completions || 0,
    onNewNudge: (nudge) => setActiveNudge(nudge),
  });

  const snoozeNudge  = useCallback((minutes) => { _snooze(minutes);  setActiveNudge(null);  }, [_snooze]);
  const gotItNudge   = useCallback((nudgeId) => { _gotIt(nudgeId);   setActiveNudge(null);  }, [_gotIt]);
  // The next nudge, if one is due: it replaces this one through onNewNudge. False when there's none.
  const nextNudge    = useCallback((nudgeId) => { if (_next(nudgeId)) return true; setActiveNudge(null); return false; }, [_next]);
  const pinNudge     = useCallback((nudge)   => { _pinNudge(nudge);  setActiveNudge(nudge); }, [_pinNudge]);
  const unpinNudge   = useCallback(()        => { _unpinNudge();     setActiveNudge(nudgeState?.lastActiveNudge || null); }, [_unpinNudge, nudgeState]);

  // ─── CHECK-IN HANDLER ──────────────────────────────────────────────
  const handleCheckIn = useCallback(() => {
    const now = new Date();
    const todayDate = todayKey();
    const lastDate = stats.lastCheckinDate;

    // Soft streak logic — compare date strings directly to avoid timezone issues
    let newStreak = stats.softStreak || 0;
    if (lastDate === todayDate) {
      // Already checked in today — don't double count
    } else if (lastDate) {
      // Parse both as local dates using date string components to avoid UTC offset issues
      const [ly, lm, ld] = lastDate.split("-").map(Number);
      const [ty, tm, td] = todayDate.split("-").map(Number);
      const lastLocal = new Date(ly, lm - 1, ld);
      const todayLocal = new Date(ty, tm - 1, td);
      const diffDays = Math.round((todayLocal - lastLocal) / (1000 * 60 * 60 * 24));
      if (diffDays <= 2) {
        newStreak += 1;
      } else {
        newStreak = 1;
      }
    } else {
      newStreak = 1;
    }

    const newCount = (stats.lifetimeCount || 0) + 1;
    const newStats = {
      lifetimeCount: newCount,
      softStreak: newStreak,
      lastCheckinDate: todayDate,
      lastSessionDate: now.toISOString(),
      top10Completions: stats.top10Completions || 0,
    };

    setStatsState(newStats);
    if (uid) setDoc(doc(db, "users", uid, "data", "stats"), newStats).catch(console.error);

    // Milestone check
    const m = getMilestone(newCount);
    if (m) {
      setMilestone(getMilestoneMessage(m));
      setShowMilestone(true);
      setTimeout(() => setShowMilestone(false), 4000);
    }
  }, [uid, stats]);

  // ─── TOP 10 COMPLETION TRACKER ─────────────────────────────────────
  const prevTop10DoneIdsRef = useRef(null);
  useEffect(() => {
    if (!uid || !tasks.length) return;
    const currentDoneIds = new Set(
      tasks.filter(t => t.inTop10 && t.done).map(t => t.id)
    );
    if (prevTop10DoneIdsRef.current === null) {
      // First load — initialise ref without counting
      prevTop10DoneIdsRef.current = currentDoneIds;
      return;
    }
    const newlyCompleted = [...currentDoneIds].filter(id => !prevTop10DoneIdsRef.current.has(id));
    prevTop10DoneIdsRef.current = currentDoneIds;
    if (newlyCompleted.length === 0) return;
    const newTotal = (stats.top10Completions || 0) + newlyCompleted.length;
    const newStats = { ...stats, top10Completions: newTotal };
    setStatsState(newStats);
    setDoc(doc(db, "users", uid, "data", "stats"), { top10Completions: newTotal }, { merge: true }).catch(console.error);
  }, [tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── AUTH ACTIONS ──────────────────────────────────────────────────
  const handleGoogleSignIn = async () => {
    setSigningIn(true);
    setAuthError("");
    try { await signInWithPopup(auth, googleProvider); }
    catch (e) { console.error("Sign in error:", e); setAuthError("Google sign-in failed. Please try again."); }
    setSigningIn(false);
  };

  const handleEmailSignIn = async (email, password) => {
    setSigningIn(true);
    setAuthError("");
    try { await signInWithEmailAndPassword(auth, email, password); }
    catch (e) {
      const msgs = {
        "auth/user-not-found": "No account found with that email.",
        "auth/wrong-password": "Incorrect password.",
        "auth/invalid-email": "Please enter a valid email address.",
        "auth/invalid-credential": "Incorrect email or password.",
        "auth/too-many-requests": "Too many attempts. Please try again later.",
      };
      setAuthError(msgs[e.code] || "Sign-in failed. Please try again.");
    }
    setSigningIn(false);
  };

  const handleEmailSignUp = async (email, password) => {
    setSigningIn(true);
    setAuthError("");
    try { await createUserWithEmailAndPassword(auth, email, password); }
    catch (e) {
      const msgs = {
        "auth/email-already-in-use": "An account with this email already exists.",
        "auth/invalid-email": "Please enter a valid email address.",
        "auth/weak-password": "Password must be at least 6 characters.",
      };
      setAuthError(msgs[e.code] || "Account creation failed. Please try again.");
    }
    setSigningIn(false);
  };

  const handleForgotPassword = async (email) => {
    setAuthError("");
    if (!email) { setAuthError("Please enter your email address first."); return false; }
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (e) {
      const msgs = {
        "auth/user-not-found": "No account found with that email.",
        "auth/invalid-email": "Please enter a valid email address.",
      };
      setAuthError(msgs[e.code] || "Failed to send reset email. Please try again.");
      return false;
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    setUser(null);
    setCompassState(DEFAULT_COMPASS);
    setSettingsState(DEFAULT_SETTINGS);
    setTodayData({ planned: false, nudgeCount: 0 });
    setNudgeHistory([]);
    setNudgeStateLocal({ lastShown: {}, dismissed: [] });
    setActiveNudge(null);
    nudgeStateLoadedRef.current = false;
    setStatsState(DEFAULT_STATS);
    setTasks([]);
    setGoals([]);
    // The next account must never see this one's visions or key dates, even for a moment.
    setVisions([]);
    setKeyDates(null);
    setKeyDatesError(null);
    setChatMessages([{ role: "ai", text: DEFAULT_GREETING, time: "" }]);
    setChatHistory([]);
    setScreen("home");
  };

  // What's waiting, beside Today, Radar and Tasks in the sidebar: the same numbers those pages give.
  const sidebarCounts = useMemo(() => ({
    today: tasksLeftToday(tasks),
    radar: radarTodayCount(tasks, keyDates, settings),
    tasks: overdueTasks((tasks || []).filter(t => !t.done)).length,
  }), [tasks, keyDates, settings]);
  const sidebarFolded = !!settings?.sidebarFolded;
  // --sidebar-w is the sidebar's width, on the page's root so a message fixed to the window, even one drawn straight into
  // the body (DoneCheck's), sits over the page beside it (UndoToast). Before sign-in there's no sidebar.
  const sidebarShown = wide && !!user && !dataLoading;
  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty("--sidebar-w", sidebarShown ? `${sidebarFolded ? SIDEBAR_FOLDED_WIDTH : SIDEBAR_WIDTH}px` : "0px");
    return () => root.removeProperty("--sidebar-w");
  }, [sidebarShown, sidebarFolded]);

  // ─── NAV ───────────────────────────────────────────────────────────
  const nav = (id, mode = null) => {
    setShowNudges(false);
    setScreen(id);
    setChatMode(mode || null);
  };
  // What the showing page needs to open its editor when + sent the user there (the kind's `screen` in ADD_KINDS).
  const addRequest = { addRequested: !!addKind && ADD_KINDS.find(k => k.id === addKind)?.screen === screen, onAddHandled: () => setAddKind(null) };
  const topBarActions = {
    onAdd: () => setAppSheet("add"),
    onSearch: () => setAppSheet("search"),
    onNudges: () => nav("nudges"),
    onSettings: () => nav("settings"),
    onHome: () => nav("home"),
  };
  
  // ─── WRAP ──────────────────────────────────────────────────────────
  // Once signed in, `sidebar` is the screen showing: on a wide screen the sidebar sits to the left of the page, in place
  // of the bottom bar.
  const wrap = (children, sidebar = null) => (
    <div style={{
      fontFamily: "'DM Sans','Segoe UI',sans-serif",
      background: C.bg,
      height: "100%",
      width: "100%",
      display: "flex",
      overflow: "hidden",
      position: "relative",
    }}>
      <style>{`
        @keyframes nd{0%,80%,100%{transform:scale(0.8);opacity:0.4}40%{transform:scale(1.2);opacity:1}}
        @keyframes fiu{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes cursor { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: translate(-50%,-50%) scale(0.92); }
          to   { opacity: 1; transform: translate(-50%,-50%) scale(1); }
        }
        @keyframes swipeInLeft {
          from { opacity: 0; transform: translateX(24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes swipeInRight {
          from { opacity: 0; transform: translateX(-24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        *{box-sizing:border-box}
        ::-webkit-scrollbar{display:none}
        input,textarea,button{font-family:inherit}
        button{border:none;cursor:pointer}
        html{height:100%;overflow:hidden}
        body{height:100%;overflow:hidden;margin:0}
        #root{height:100%;overflow:hidden;display:flex;flex-direction:column}
        ${wideCss(C.sidebarHover)}
      `}</style>
      {showSubSuccess && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
          <div style={{ background: C.surface, borderRadius: 20, padding: "32px 24px", maxWidth: 320, width: "100%", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.25)", animation: "scaleIn 0.2s ease forwards" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>✦</div>
            <div style={{ fontFamily: "Georgia,serif", fontSize: 22, fontWeight: 700, color: C.text, marginBottom: 10 }}>Welcome to Nudge Pro</div>
            <div style={{ fontSize: 15, color: C.textMid, lineHeight: 1.7, marginBottom: 24 }}>
              You're all set. Full access. Thanks for joining the Nudge Community — let's make it count.
            </div>
            <button
              onClick={() => {
                setShowSubSuccess(false);
                window.history.replaceState({}, "", "/");
              }}
              style={{ width: "100%", background: C.accent, color: "white", border: "none", borderRadius: C.r, padding: "14px 0", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
            >
              Let's go →
            </button>
          </div>
        </div>
      )}
      <TopBarContext.Provider value={topBarActions}>
        {/* The sheets sit inside too: search and + open the task editor, which lists Key Dates from here. */}
        <KeyDatesContext.Provider value={keyDates}>
        <TaskLinksContext.Provider value={{ dreams: dreamsList, fears: fearsList }}>
        <TaskActionsContext.Provider value={taskActions}>
          {sidebar && wide && <Sidebar active={sidebar} onNav={nav} counts={sidebarCounts} folded={sidebarFolded} onFold={(f) => setSettings(p => ({ ...p, sidebarFolded: f }))} />}
          <div style={{ flex: 1, minWidth: 0, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}>
            <MilestoneToast message={milestone} visible={showMilestone} />
            {children}
          </div>
          {editorTask && <TaskEditModal task={editorTask} uid={uid} compass={compass} tasks={tasks} goals={goals} settings={settings} onClose={() => setTaskEditor(null)} />}
          {taskEditor?.defaults && <TaskEditModal newTaskDefaults={taskEditor.defaults} uid={uid} compass={compass} tasks={tasks} goals={goals} settings={settings} onClose={() => setTaskEditor(null)} />}
          {appSheet === "add" && (
            <AddSheet onClose={() => setAppSheet(null)} onPick={(kind) => {
              if (!kind.screen) { setAppSheet(kind.id); return; }
              setAppSheet(null);
              setAddKind(kind.id);
              nav(kind.screen);
            }} />
          )}
          {appSheet === "task" && (
            <TaskEditModal newTaskDefaults={{}} uid={uid} compass={compass} tasks={tasks} goals={goals} settings={settings} onClose={() => setAppSheet(null)} />
          )}
          {appSheet === "goal" && <GoalCreateModal uid={uid} compass={compass} tasks={tasks} onClose={() => setAppSheet(null)} />}
          {appSheet === "search" && (
            <SearchModal tasks={tasks} compass={compass} setCompass={setCompass} uid={uid} settings={settings} goals={goals} onGoalChat={handleGoalChat} onClose={() => setAppSheet(null)} />
          )}
        </TaskActionsContext.Provider>
        </TaskLinksContext.Provider>
        </KeyDatesContext.Provider>
      </TopBarContext.Provider>
    </div>
  );

  // ─── RENDER STATES ─────────────────────────────────────────────────
  // Apply system/saved theme for pre-auth screens (loading, sign-in)
  const savedTheme = (() => { try { return localStorage.getItem("nudge-theme"); } catch(e) { return null; } })();
  const preAuthDark = savedTheme ? savedTheme === "dark" : window.matchMedia("(prefers-color-scheme: dark)").matches;
  if (authLoading || dataLoading) Object.assign(C, preAuthDark ? DARK : LIGHT);

  if (authLoading) return wrap(<LoadingScreen />);
  if (!user) {
    Object.assign(C, preAuthDark ? DARK : LIGHT);
    return wrap(<SignInScreen onGoogleSignIn={handleGoogleSignIn} onEmailSignIn={handleEmailSignIn} onEmailSignUp={handleEmailSignUp} onForgotPassword={handleForgotPassword} loading={signingIn} authError={authError} />);
  }
  if (dataLoading) return wrap(<LoadingScreen failed={loadFailed} />);

  // ─── MAIN APP ──────────────────────────────────────────────────────
  if (showNudges || screen === "nudges") return wrap(<>
    <NudgeHistoryPage nudges={nudgeHistory} tasks={tasks} goals={goals} nudgeState={nudgeState} onBack={() => { setShowNudges(false); setScreen("home"); }} onGotIt={gotItNudge} compass={compass} settings={settings} uid={uid} />
    <BottomNav active="nudges" onNav={nav} />
  </>, "nudges");

  return wrap(<>
    {screen === "pricing" && (
      <PricingPage onNav={nav} settings={settings} uid={uid} />
    )}
    {screen === "support" && (
      <SupportPage onNav={nav} settings={settings} userEmail={user.email} />
    )}
    {screen === "today" && (
      <TodayPage
        todayData={todayData}
        onToggleSection={toggleSection}
        onNav={nav}
        onNudgeHistory={() => setShowNudges(true)}
        activeNudge={activeNudge}
        onSnoozeNudge={snoozeNudge}
        onGotItNudge={gotItNudge}
        onPinNudge={pinNudge}
        onUnpinNudge={unpinNudge}
        pinnedNudge={nudgeState?.pinnedNudge || null}
        pinnedNudgeId={nudgeState?.pinnedNudge && nudgeState?.pinnedDate === todayKey() ? nudgeState.pinnedNudge.id : null}
        nudgeHistory={nudgeHistory}
        onCheckin={() => { setScreen("chat"); setChatMode("checkin"); }}
        onProcrastinate={() => { setScreen("chat"); setChatMode("procrastinate"); }}
        onOverwhelmed={() => { setScreen("chat"); setChatMode("overwhelmed"); }}
        onChat={() => { setScreen("chat"); setChatMode("chat"); }}
        onDecide={() => { setScreen("chat"); setChatMode("decide"); }}
        onRevise={() => { setScreen("chat"); setChatMode("plan"); }}
        onAction={() => { setScreen("chat"); setChatMode("action"); }}
        onNudgeHelp={() => { setSettings(p => ({ ...p, hasSeenNudgeHelp: true })); setScreen("chat"); setChatMode("nudgehelp"); }}
        onMotivate={() => { setScreen("chat"); setChatMode("motivate"); }}
        onAvoiding={() => { setScreen("chat"); setChatMode("avoiding"); }}
        onWhyProcrastinate={() => { setScreen("chat"); setChatMode("whyprocrastinate"); }}
        settings={settings}
        checkIn={checkIn}
        setCheckIn={setCheckIn}
        softStreak={softStreak}
        lifetimeCount={lifetimeCount}
        uid={uid}
        compass={compass}
        tasks={tasks}
        goals={goals}
        hasFullAccess={hasFullAccess()}
        trialDaysLeft={trialDaysLeft}
        userEmail={user?.email || ""}
      />
    )}
    {screen === "chat" && (
      <ChatHub
        onNav={nav}
        initialMode={chatMode}
        chatSeedMessage={chatSeedMessage}
        onClearChatSeed={() => setChatSeedMessage(null)}
        nudgeHistory={nudgeHistory}
        setNudgeHistory={saveNudge}
        checkIn={checkIn}
        setCheckIn={setCheckIn}
        onCheckIn={handleCheckIn}
        lastSessionDate={lastSessionDate}
        compass={compass}
        chatMessages={chatMessages}
        setChatMessages={setChatMessages}
        chatHistory={chatHistory}
        setChatHistory={setChatHistory}
        uid={uid}
        todayData={todayData}
        settings={settings}
        tasks={tasks}
        goals={goals}
      />
    )}
    {screen === "home" && (
      <HomePage onNav={nav} onOpen={(tile) => nav(tile.to)} />
    )}
    {screen === "learn" && (
      <LearnPage uid={uid} onNav={nav}
        onTalk={(message) => {
          if (message) { setChatSeedMessage(message); nav("chat"); }
          else nav("chat", "procrastinate");
        }} />
    )}
    {screen === "radar" && (
      <RadarPage radarCount={sidebarCounts.radar} onToggleSection={toggleSection} tasks={tasks} goals={goals} visions={visions} keyDates={keyDates} uid={uid} onNav={nav} compass={compass} setCompass={setCompass} onGoalChat={handleGoalChat} settings={settings} setSettings={setSettings} activeNudge={activeNudge} onSnoozeNudge={snoozeNudge} onGotItNudge={gotItNudge} onNextNudge={nextNudge} hasNextNudge={hasNextNudge} onPinNudge={pinNudge} onUnpinNudge={unpinNudge} pinnedNudge={nudgeState?.pinnedNudge || null} pinnedNudgeId={nudgeState?.pinnedNudge && nudgeState?.pinnedDate === todayKey() ? nudgeState.pinnedNudge.id : null} nudgeHistory={nudgeHistory} />
    )}
    {screen === "dreams" && <PlanItemsPage kind="dreams" uid={uid} tasks={tasks} goals={goals} onNav={nav} {...addRequest} />}
    {(screen === "mindset" || screen === "gratitude") && (
      <CompassListPage key={screen} kind={screen} compass={compass} setCompass={setCompass} onNav={nav} {...addRequest} />
    )}
    {(screen === "habits" || screen === "temptations") && (
      <CompassGroupsPage key={screen} kind={screen} uid={uid} compass={compass} setCompass={setCompass} goals={goals} onNav={nav} {...addRequest} />
    )}
    {screen === "keydates" && (
      <KeyDatesScreen uid={uid} keyDates={keyDates} loadError={keyDatesError} tasks={tasks} goals={goals} onNav={nav} {...addRequest} />
    )}
    {(screen === "fears" || screen === "vision") && (
      <StatementsPage key={screen} kind={screen === "vision" ? "visions" : "fears"} uid={uid} tasks={tasks} goals={goals} compass={compass} onNav={nav} {...addRequest}
        renderGoal={(goal, onBack) => <GoalDetailPage goal={goal} uid={uid} compass={compass} setCompass={setCompass} tasks={tasks} goals={goals} settings={settings} onBack={onBack} backLabel={screen === "vision" ? "Back to Vision" : "Back to Fears"} onGoalChat={handleGoalChat} />} />
    )}
    {screen === "tasks" && (
      <TasksScreen uid={uid} tasks={tasks} goals={goals} compass={compass} setCompass={setCompass} settings={settings} getSortMode={getSortMode} setSortMode={setSortMode} onNav={nav} />
    )}
    {screen === "goals" && (
      <GoalsScreen uid={uid} tasks={tasks} goals={goals} compass={compass} setCompass={setCompass} settings={settings} getSortMode={getSortMode} setSortMode={setSortMode} onGoalChat={handleGoalChat} onNav={nav} />
    )}
    {screen === "settings" && (
      <SettingsPage
        settings={settings}
        setSettings={setSettings}
        onNav={nav}
        onSignOut={handleSignOut}
        userEmail={user.email}
        uid={uid}
        compass={compass}
        setCompass={setCompass}
        goals={goals}
        softStreak={softStreak}
        lifetimeCount={lifetimeCount}
      />
    )}
  </>, screen);
} // end root